/**
 * Community Messaging API
 *
 * Supabase client functions for conversations, messages, reactions, and read state.
 * Uses direct Supabase queries (browser-side), following the same pattern as client.ts.
 *
 * All types derived from Drizzle schemas via drizzle-zod (Directive D3).
 * @see BUILD_PLAN_INDEX.md — D1, D3
 * @see ERRATA §1.2 (no bool prefix), §1.3 (4 conversation types), §1.4 (5 message types)
 * @see ERRATA §2.1 (watermark read), §2.2 (UUIDv7 comparison), §2.3 (GREATEST), §4.2 (client_temp_id)
 * @see ERRATA §4.4 (onSettled only invalidates conversations), §6.4 (canonical stale/GC)
 * @see phase-2-messaging-core.md
 */

import { z } from 'zod';
import { supabase, getSessionUser } from '@/lib/supabase';
import { stripMentionTokens, tokenizeContent } from '@/lib/text/mentions';
import { getRevenueRangeForPod, type PodNumber } from '@/lib/pods';
import { REALTIME_EVENTS } from './realtime-events';
import { api } from './client';
import { downscaleImage } from '@/lib/images/downscale-image';
import { isSpeediesEnabled } from '@/lib/feature-flags';
import {
  conversationTypeSchema,
  messageTypeSchema,
  attachmentSchema,
  leadStatusSchema,
  leadSourceSchema,
  channelTypeSchema,
  reportReasonSchema,
  postTypeSchema,
  postImageSchema,
  storyTypeSchema,
  type UserBlock,
  type MessageReport,
  type NewMessageReport,
  type PostImage,
  type PostAttachment,
  type PostLocation,
  postAttachmentSchema,
  postLocationSchema,
  type PostType,
  type StoryType,
  mentionSchema,
  type Mention,
} from '@hermes/db/schema';

// ===========================================
// Zod Schemas — API-level composite types (D3)
// Derived from / composed with Drizzle schemas
// ===========================================

/** Participant profile as returned by user_profiles JOIN */
export const participantProfileSchema = z.object({
  user_id: z.string().uuid(),
  full_name: z.string(),
  avatar_url: z.string().nullable(),
  // Group chats (mig 647): the sidebar RPC returns each live member's role.
  role: z.string().nullable().optional(),
});
export type ParticipantProfile = z.infer<typeof participantProfileSchema>;

/** Detailed participant (includes role and read state) — ERRATA §2.1 */
export const participantDetailSchema = participantProfileSchema.extend({
  role: z.string().nullable(),
  last_read_at: z.string().nullable(),
  last_read_message_id: z.string().uuid().nullable(),
});
export type ParticipantDetail = z.infer<typeof participantDetailSchema>;

/** Conversation list item — sidebar representation */
export const conversationListItemSchema = z.object({
  id: z.string().uuid(),
  type: conversationTypeSchema,
  title: z.string().nullable(),
  avatar_url: z.string().nullable(),
  last_message_preview: z.string().nullable(),
  last_message_at: z.string().nullable(),
  last_message_sender_name: z.string().nullable(),
  // Group chats (mig 647): lets a group row say "You: …" and "Tim added you".
  last_message_sender_id: z.string().uuid().nullable().optional(),
  last_message_type: z.string().nullable().optional(),
  last_message_metadata: z.record(z.string(), z.unknown()).nullable().optional(),
  unread_count: z.number().int().nonnegative(),
  pinned: z.boolean(), // ERRATA §1.2: no is_ prefix
  muted: z.boolean(),
  muted_until: z.string().nullable().optional(),
  participants: z.array(participantProfileSchema),
  // Group chats (mig 647): metadata.color is the group colour; member_count
  // counts live members including the viewer; my_role is the viewer's role;
  // left_at is set when the viewer left a group (the thread is read-only).
  metadata: z.record(z.string(), z.unknown()).nullable().optional(),
  member_count: z.number().int().nullable().optional(),
  my_role: z.string().nullable().optional(),
  left_at: z.string().nullable().optional(),
  community_tier_id: z.string().uuid().nullable().optional(),
  // Custom-community chat (mig 382): resolves the community's icon/color in
  // the sidebar. Optional so pre-382 payloads still parse.
  community_id: z.string().uuid().nullable().optional(),
});
export type ConversationListItem = z.infer<typeof conversationListItemSchema>;

/** Conversation detail — full view */
export const conversationDetailSchema = conversationListItemSchema.extend({
  created_at: z.string(),
  created_by: z.string().uuid().nullable().optional(),
  community_tier_id: z.string().uuid().nullable(),
  participants: z.array(participantDetailSchema),
});
export type ConversationDetail = z.infer<typeof conversationDetailSchema>;

/** Conversation filter for list queries */
export const conversationFilterSchema = z.object({
  type: conversationTypeSchema.optional(),
  search: z.string().optional(),
  archived: z.boolean().optional(),
}).partial();
export type ConversationFilter = z.infer<typeof conversationFilterSchema>;

/** Sender profile embedded in message */
export const senderProfileSchema = z.object({
  full_name: z.string(),
  avatar_url: z.string().nullable(),
});
export type SenderProfile = z.infer<typeof senderProfileSchema>;

/** Reply-to preview embedded in message */
export const replyToPreviewSchema = z.object({
  id: z.string().uuid(),
  content: z.string(),
  sender_name: z.string(),
  /** The quoted message's metadata, so the quote can be rendered SAFELY —
   *  an asset share must be quoted as "Asset", never by whatever sits in its
   *  content (legacy `/asset` rows carry the title there). The REST read and
   *  the realtime broadcast's `reply_to` (hermes mig 645) both carry it.
   *  Optional so an older payload still parses; the quote then falls back to
   *  content. See reply-preview.ts. */
  metadata: z.record(z.string(), z.unknown()).nullable().optional(),
});
export type ReplyToPreview = z.infer<typeof replyToPreviewSchema>;

/** Aggregated reaction for display */
export const reactionAggregateSchema = z.object({
  emoji: z.string(),
  count: z.number().int().positive(),
  reacted_by_me: z.boolean(),
  // Reactor user_ids for this emoji. Carried in the message cache (seeded here +
  // maintained by realtime reaction.added/removed) so the "who reacted" tooltip
  // renders instantly from the prewarmed member map — no per-hover fetch.
  user_ids: z.array(z.string()).default([]),
});
export type ReactionAggregate = z.infer<typeof reactionAggregateSchema>;

/** Hydrated message with sender, reactions, reply context */
export const messageWithContextSchema = z.object({
  id: z.string(),
  conversation_id: z.string().uuid(),
  sender_id: z.string().uuid(),
  sender: senderProfileSchema,
  content: z.string(),
  message_type: messageTypeSchema,
  reply_to: replyToPreviewSchema.nullable(),
  attachments: z.array(attachmentSchema),
  mentions: z.array(mentionSchema).default([]),
  metadata: z.record(z.string(), z.unknown()).nullable(),
  reactions: z.array(reactionAggregateSchema),
  edited_at: z.string().nullable(),
  deleted_at: z.string().nullable(),
  created_at: z.string(),
});
export type MessageWithContext = z.infer<typeof messageWithContextSchema>;

/** Paginated message response */
export const messagePageSchema = z.object({
  messages: z.array(messageWithContextSchema),
  nextCursor: z.string().nullable(),
  hasMore: z.boolean(),
});
export type MessagePage = z.infer<typeof messagePageSchema>;

/**
 * Max chat message length. Matches the community_posts limit so there's one
 * number across the product.
 *
 * The binding constraint is NOT the DB — `messages.content` is plain `text`
 * with no constraint — it's the Supabase Realtime broadcast payload ceiling
 * (256 KB default). 16000 chars is ~64 KB worst-case UTF-8, so ~4x headroom.
 * Every other consumer (push outbox, conversation previews, Alfie digests)
 * already truncates well below this. Raise it further only after checking
 * `fn_broadcast_message_events` (hermes migration 468) — realtime overflow
 * fails SILENTLY: the sender sees success and the receiver simply never gets
 * the message.
 *
 * Must be kept in sync with insertMessageSchema in
 * hermes-alfie/packages/db/src/schema/community/messages.ts.
 */
export const MESSAGE_MAX_LENGTH = 16000;

/** Reply previews are rendered truncated, so only this much is ever fetched,
 *  stored on the message or sent over realtime. Keeps a reply-to-a-long-message
 *  from doubling the broadcast payload. */
export const REPLY_PREVIEW_MAX_LENGTH = 200;

/** Send message input — validated before API call */
export const sendMessagePayloadSchema = z.object({
  content: z.string().max(MESSAGE_MAX_LENGTH),
  messageType: messageTypeSchema.optional().default('text'),
  replyToId: z.string().uuid().optional(),
  attachments: z.array(attachmentSchema).optional().default([]),
  mentions: z.array(mentionSchema).optional().default([]),
  metadata: z.record(z.string(), z.unknown()).optional(),
  // Client-generated message id (UUIDv7). Used to preserve the sender's
  // typed-order even when mutations race to the server. Server INSERTs
  // with this id rather than its own defaultNow() so id-DESC sort on the
  // receiver matches the sender's typed sequence.
  id: z.string().uuid().optional(),
  clientTempId: z.string().uuid().optional(), // ERRATA §4.2 (legacy — id field above supersedes)
}).refine(
  (data) =>
    data.content.length > 0 ||
    (data.attachments && data.attachments.length > 0) ||
    // Shared-item messages carry context in metadata — the receiver renders
    // a preview card via `shared_post_id`, `shared_asset_id`, or
    // `shared_member_id`, so empty content is OK when one of those is present.
    Boolean(data.metadata?.shared_post_id) ||
    Boolean(data.metadata?.shared_asset_id) ||
    Boolean(data.metadata?.shared_member_id),
  {
    message: 'Message must have content, an attachment, or a shared-item reference',
    path: ['content'],
  },
);
// Use z.input for the payload type so optional fields with defaults remain optional in function signatures
export type SendMessagePayload = z.input<typeof sendMessagePayloadSchema>;

/** Unread count per conversation (includes type for DM vs community badge split) */
export const unreadCountSchema = z.object({
  conversation_id: z.string().uuid(),
  count: z.number().int().nonnegative(),
  type: conversationTypeSchema,
  muted: z.boolean(),
});
export type UnreadCount = z.infer<typeof unreadCountSchema>;

/** DM creation result */
export const dmResultSchema = z.object({
  id: z.string().uuid(),
  existing: z.boolean(),
});
export type DMResult = z.infer<typeof dmResultSchema>;

/** Pin toggle result */
export const pinResultSchema = z.object({
  pinned: z.boolean(),
});
export type PinResult = z.infer<typeof pinResultSchema>;

// ===========================================
// Helpers
// ===========================================

async function getAuthUserId(): Promise<string> {
  const user = await getSessionUser();
  if (!user) throw new Error('Not authenticated');
  return user.id;
}

// ===========================================
// Conversations API
// ===========================================

/**
 * Fetch conversation list for the sidebar.
 * Uses get_conversations_for_user RPC for single-query performance (Phase 2 §6).
 * ERRATA §1.2 (pinned), §2.2 (UUIDv7), §2.6 (computed unread), §6.4 (< 200ms target).
 */
export async function fetchConversations(
  _userId: string, // Unused — RPC uses auth.uid() internally
  filters?: ConversationFilter,
): Promise<ConversationListItem[]> {
  const { data, error } = await supabase.rpc('get_conversations_for_user', {
    p_type: filters?.type ?? null,
    p_search: filters?.search ?? null,
    // 200 (was 100): the canonical no-filter fetch also feeds useUnreadCounts,
    // which previously fetched up to 200 rows via its own duplicate RPC call.
    p_limit: 200,
    p_offset: 0,
  });
  if (error) throw error;
  if (!data || data.length === 0) return [];

  return (data as any[]).map((row) => ({
    id: row.id,
    type: row.type,
    title: row.title,
    avatar_url: row.avatar_url,
    last_message_preview: row.last_message_preview,
    last_message_at: row.last_message_at,
    last_message_sender_name: row.last_message_sender_name,
    last_message_sender_id: row.last_message_sender_id ?? null,
    last_message_type: row.last_message_type ?? null,
    last_message_metadata: row.last_message_metadata ?? null,
    unread_count: row.unread_count ?? 0,
    pinned: row.pinned ?? false,
    muted: row.muted ?? false,
    participants: row.participants ?? [],
    metadata: row.metadata ?? {},
    member_count: row.member_count ?? null,
    my_role: row.my_role ?? null,
    left_at: row.left_at ?? null,
    community_tier_id: row.community_tier_id ?? null,
    community_id: row.community_id ?? null,
  }));
}

/**
 * Fetch single conversation with full participant list.
 */
export async function fetchConversation(
  conversationId: string,
): Promise<ConversationDetail> {
  const userId = await getAuthUserId();

  const { data: conv, error } = await supabase
    .from('conversations')
    .select('*')
    .eq('id', conversationId)
    .single();
  if (error) throw error;

  // Fetch participants (no FK join — conversation_participants.user_id has no FK to user_profiles)
  const { data: participants, error: partError } = await supabase
    .from('conversation_participants')
    .select('user_id, role, last_read_at, pinned, muted_until, last_read_message_id')
    .eq('conversation_id', conversationId)
    .is('left_at', null);
  if (partError) throw partError;

  // Fetch profiles separately and merge
  const userIds = (participants || []).map((p: any) => p.user_id);
  let profileMap = new Map<string, { full_name: string; avatar_url: string | null }>();
  if (userIds.length > 0) {
    const { data: profiles } = await supabase
      .from('user_profiles')
      .select('id, full_name, avatar_url')
      .in('id', userIds);
    for (const prof of profiles || []) {
      profileMap.set(prof.id, { full_name: prof.full_name, avatar_url: prof.avatar_url });
    }
  }

  const myRow = participants?.find((p: any) => p.user_id === userId);

  // Unread count (UUIDv7 id > comparison — ERRATA §2.2)
  // Exclude sender's own messages (fix: sender's messages shouldn't count as unread)
  let countQuery = supabase
    .from('messages')
    .select('id', { count: 'exact', head: true })
    .eq('conversation_id', conversationId)
    .neq('sender_id', userId)
    .is('deleted_at', null);
  if (myRow?.last_read_message_id) {
    countQuery = countQuery.gt('id', myRow.last_read_message_id);
  }
  const { count } = await countQuery;

  return {
    id: conv.id,
    type: conv.type,
    title: conv.title,
    avatar_url: conv.avatar_url,
    last_message_preview: conv.last_message_preview,
    last_message_at: conv.last_message_at,
    last_message_sender_name: null,
    unread_count: count || 0,
    pinned: myRow?.pinned || false,
    muted: !!(myRow?.muted_until && new Date(myRow.muted_until) > new Date()),
    muted_until: myRow?.muted_until ?? null,
    created_at: conv.created_at,
    created_by: conv.created_by ?? null,
    metadata: conv.metadata ?? {},
    member_count: (participants || []).length,
    my_role: myRow?.role ?? null,
    community_tier_id: conv.community_tier_id ?? null,
    community_id: conv.community_id ?? null,
    participants: (participants || []).map((p: any) => {
      const profile = profileMap.get(p.user_id);
      return {
        user_id: p.user_id,
        full_name: profile?.full_name || 'Unknown',
        avatar_url: profile?.avatar_url || null,
        role: p.role,
        last_read_at: p.last_read_at,
        last_read_message_id: p.last_read_message_id || null,
      };
    }),
  };
}

/**
 * Create or find existing DM conversation between two users.
 * Uses find_or_create_dm RPC (Phase 1 migration) — ERRATA §8.3
 */
export async function createDMConversation(
  targetUserId: string,
): Promise<DMResult> {
  // ERRATA §8.3: find_or_create_dm uses auth.uid() as caller, takes only target user
  const { data, error } = await supabase.rpc('find_or_create_dm', {
    p_target_user_id: targetUserId,
  });
  if (error) throw error;
  return dmResultSchema.parse({ id: data, existing: false });
}

// ── Admin-only: a member's contact (email + phone) for the warm-intro card ──
const memberContactSchema = z.object({
  email: z.string().nullable(),
  phone: z.string().nullable(),
});
export type MemberContact = z.infer<typeof memberContactSchema>;

/**
 * Admin/super_admin only — returns a member's email + phone via the gated
 * `get_member_contact` RPC (server-enforced; non-admins are denied). Used by the
 * member profile drawer + the warm-intro contact card.
 */
export async function fetchMemberContact(targetUserId: string): Promise<MemberContact> {
  const { data, error } = await supabase.rpc('get_member_contact', {
    target_user_id: targetUserId,
  });
  if (error) throw error;
  const row = Array.isArray(data) ? data[0] : data;
  return memberContactSchema.parse(row ?? { email: null, phone: null });
}

// ── Introduce Member — the single consolidated RPC (mig 370) ──
const shareMemberIntroResultSchema = z.object({
  dm_a_id: z.string(),
  dm_b_id: z.string(),
});
export type ShareMemberIntroResult = z.infer<typeof shareMemberIntroResultSchema>;

/**
 * Brokered mutual warm intro in ONE server round-trip via `share_member_intro`
 * (mig 370). The RPC authorizes the caller server-side (`can_introduce`: global
 * admin OR channel admin of a shared channel OR org owner/admin of a shared org),
 * reads both members' contact, creates the two DMs, inserts the two contact-card
 * messages (A's card → caller↔B, B's card → caller↔A), updates previews, and
 * emits realtime so recipients update live. Replaces the former 8 round-trips.
 * Returns the two DM ids so the caller can refresh its own caches.
 */
export async function shareMemberIntro(
  memberAId: string,
  memberBId: string,
): Promise<ShareMemberIntroResult> {
  const { data, error } = await supabase.rpc('share_member_intro', {
    p_a: memberAId,
    p_b: memberBId,
  });
  if (error) throw error;
  return shareMemberIntroResultSchema.parse(data);
}

/**
 * Get or create the single chat conversation for a community tier and
 * ensure the current user is added as a participant. Chat conversations
 * are keyed directly on `conversations.community_tier_id` (NULL for globals);
 * `community_channels` is unrelated to chat.
 *
 * One SECURITY DEFINER RPC (hermes migration 455) does the whole join
 * atomically. This used to be a client-side find-or-create, which dead-ended
 * for members who weren't participants yet: conversations_select RLS hides
 * the existing row from non-members, so the recovery re-SELECT after the
 * unique-index conflict came back empty ("Couldn't open that channel").
 * Normally the tier-chat provisioning triggers mask that by auto-joining on
 * membership creation, but grant-only members (Basecamp via an org
 * addon_grant, before their lazy membership bootstrap) and any member with a
 * lost participant row hit it with no way out. The RPC gates on
 * user_has_tier_access, joins muted-by-default and heals soft-left rows —
 * the exact semantics of the old client flow, minus the dead end.
 */
export async function getOrCreateTierConversation(
  communityTierId: string,
): Promise<DMResult> {
  const { data, error } = await supabase.rpc('join_tier_channel', {
    p_tier_id: communityTierId,
  });
  if (error) throw error;
  const row = (Array.isArray(data) ? data[0] : data) as
    | { conversation_id: string; existed: boolean }
    | undefined
    | null;
  if (!row?.conversation_id) {
    throw new Error('join_tier_channel returned no conversation');
  }
  return { id: row.conversation_id, existing: row.existed };
}

/**
 * Get or create the single chat conversation for a global channel
 * (tier-independent). Title identifies the global (e.g. "In Person Events").
 */
export async function getOrCreateGlobalConversation(
  title: string,
): Promise<DMResult> {
  const userId = await getAuthUserId();
  let conversationId: string;
  let isExisting = false;

  const { data: existingConv, error: findErr } = await supabase
    .from('conversations')
    .select('id')
    .eq('type', 'community_channel')
    .is('community_tier_id', null)
    .eq('title', title)
    .limit(1)
    .maybeSingle();
  if (findErr) throw findErr;

  if (existingConv?.id) {
    conversationId = existingConv.id;
    isExisting = true;
  } else {
    // Race-safe insert backed by uniq_conversations_community_channel_global
    // (migration 240). Concurrent creates collapse to one row.
    const { data: conv, error: convError } = await supabase
      .from('conversations')
      .upsert(
        {
          type: 'community_channel',
          community_tier_id: null,
          title,
          created_by: userId,
        },
        { onConflict: 'title', ignoreDuplicates: false },
      )
      .select('id')
      .single();
    if (convError) throw convError;
    conversationId = conv.id;
  }

  // Join UNMUTED — channel notifications are on by default (migration 274,
  // reaffirmed by 466). This path used to write the 2099 mute sentinel,
  // silently cutting every global-channel joiner out of notifications;
  // muting stays an explicit per-conversation choice in chat settings.
  const { error: partError } = await supabase
    .from('conversation_participants')
    .upsert(
      {
        conversation_id: conversationId,
        user_id: userId,
        role: 'member',
      },
      { onConflict: 'conversation_id,user_id', ignoreDuplicates: true },
    );
  if (partError) throw partError;

  return { id: conversationId, existing: isExisting };
}

// ===========================================
// Community Channels — Phase 4 §1, D3
// ===========================================

/**
 * Channel row as returned by community_channels query.
 * Composed with channelTypeSchema from Drizzle (D3).
 * Includes the joined tier name for display.
 */
export const communityChannelItemSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  description: z.string().nullable(),
  channel_type: channelTypeSchema,
  // Nullable: NULL = global / tier-independent channel (see migration 199).
  community_tier_id: z.string().uuid().nullable(),
  // Custom-community scope (mig 378): set = the channel belongs to a custom
  // community (member-gated), mutually exclusive with community_tier_id.
  community_id: z.string().uuid().nullable().optional(),
  required_tier_id: z.string().uuid().nullable(),
  default_channel: z.boolean(),
  icon: z.string().nullable().optional(),
  display_order: z.number().nullable().optional(),
  community_tiers: z.object({
    id: z.string().uuid(),
    name: z.string(),
    code: z.string(),
  }).nullable(),
});

export type CommunityChannelItem = z.infer<typeof communityChannelItemSchema>;

/**
 * Fetch all channels for a given community tier, with tier info joined.
 * If no tierId is provided, fetches all channels the user can see.
 */
export async function fetchCommunityChannels(
  tierId?: string,
): Promise<CommunityChannelItem[]> {
  let query = supabase
    .from('community_channels')
    .select(`
      id,
      name,
      description,
      channel_type,
      community_tier_id,
      community_id,
      required_tier_id,
      default_channel,
      icon,
      display_order,
      community_tiers!community_channels_community_tier_id_community_tiers_id_fk (id, name, code)
    `)
    .order('display_order', { ascending: true })
    .order('name');

  if (tierId) {
    query = query.eq('community_tier_id', tierId);
  }

  const { data, error } = await query;
  if (error) throw error;

  // community_tier_id is a direct FK (many-to-one): each channel belongs to
  // exactly one tier. PostgREST returns this as a single object at runtime,
  // but the Supabase TS client may infer an array type. Cast is safe here.
  return (data ?? []) as unknown as CommunityChannelItem[];
}

/**
 * Toggle pinned status on the current user's participant row.
 * ERRATA §1.2: column is `pinned`, not `is_pinned`.
 */
export async function pinConversation(
  conversationId: string,
): Promise<PinResult> {
  const userId = await getAuthUserId();

  const { data: current, error: getError } = await supabase
    .from('conversation_participants')
    .select('pinned')
    .eq('conversation_id', conversationId)
    .eq('user_id', userId)
    .single();
  if (getError) throw getError;

  const newPinned = !current.pinned;
  const { error } = await supabase
    .from('conversation_participants')
    .update({ pinned: newPinned, updated_at: new Date().toISOString() })
    .eq('conversation_id', conversationId)
    .eq('user_id', userId);
  if (error) throw error;

  return pinResultSchema.parse({ pinned: newPinned });
}

/**
 * Set muted_until on the current user's participant row. Pass null to unmute.
 * ERRATA §3.5: OR'd with notification_preferences.muted_conversations.
 */
export async function muteConversation(
  conversationId: string,
  until?: string | null,
): Promise<void> {
  const userId = await getAuthUserId();

  const { error } = await supabase
    .from('conversation_participants')
    .update({
      muted_until: until ?? null,
      updated_at: new Date().toISOString(),
    })
    .eq('conversation_id', conversationId)
    .eq('user_id', userId);
  if (error) throw error;
}

/**
 * Archive a conversation (set archived = true).
 */
export async function archiveConversation(
  conversationId: string,
): Promise<void> {
  const { error } = await supabase
    .from('conversations')
    .update({ archived: true, updated_at: new Date().toISOString() })
    .eq('id', conversationId);
  if (error) throw error;
}

/**
 * Toggle read receipts for a conversation (per-user preference, DB-backed).
 */
export async function updateReadReceipts(
  conversationId: string,
  enabled: boolean,
): Promise<void> {
  const userId = await getAuthUserId();
  const { error } = await supabase
    .from('conversation_participants')
    .update({ read_receipts_enabled: enabled, updated_at: new Date().toISOString() })
    .eq('conversation_id', conversationId)
    .eq('user_id', userId);
  if (error) throw error;
}

/**
 * Toggle online status visibility (global user preference, DB-backed).
 */
export async function updateOnlineVisibility(
  hidden: boolean,
): Promise<void> {
  const userId = await getAuthUserId();
  const { error } = await supabase
    .from('user_profiles')
    .update({ hide_online_status: hidden, updated_at: new Date().toISOString() })
    .eq('id', userId);
  if (error) throw error;
}

/**
 * Leave a conversation (set left_at on participant row).
 */
export async function leaveConversation(
  conversationId: string,
): Promise<void> {
  const userId = await getAuthUserId();
  const { error } = await supabase
    .from('conversation_participants')
    .update({ left_at: new Date().toISOString(), updated_at: new Date().toISOString() })
    .eq('conversation_id', conversationId)
    .eq('user_id', userId);
  if (error) throw error;
}

// ===========================================
// Messages API
// ===========================================

/**
 * Fetch messages with cursor-based pagination (newest first).
 * Each message includes sender profile, reactions, and reply-to preview.
 * Uses UUIDv7 id-based cursor per ERRATA §2.2 — id is lexicographically
 * time-ordered so ORDER BY id DESC matches "newest first" without the
 * created_at tie-break issues under high-write bursts.
 */
/**
 * Current sender profiles (name + avatar) keyed by user id.
 *
 * Sender name/avatar are embedded into each message at fetch time, but the
 * messages query is staleTime:Infinity + persisted to IndexedDB — so a profile
 * rename (e.g. a NULL full_name later backfilled) would otherwise never show in
 * already-cached conversations. This is queried separately with a finite
 * staleTime so the chat can overlay fresh names without re-fetching messages.
 */
export async function fetchSenderProfiles(
  userIds: string[],
): Promise<Record<string, SenderProfile>> {
  if (userIds.length === 0) return {};
  const { data, error } = await supabase
    .from('user_profiles')
    .select('id, full_name, avatar_url')
    .in('id', userIds);
  if (error) throw error;
  const out: Record<string, SenderProfile> = {};
  for (const p of data ?? []) {
    out[p.id as string] = {
      full_name: (p.full_name as string | null) ?? '',
      avatar_url: (p.avatar_url as string | null) ?? null,
    };
  }
  return out;
}

export async function fetchMessages(
  conversationId: string,
  cursor?: string,
  limit: number = 30,
): Promise<MessagePage> {
  const userId = await getAuthUserId();

  let query = supabase
    .from('messages')
    .select(`
      id,
      conversation_id,
      sender_id,
      message_type,
      content,
      attachments,
      mentions,
      metadata,
      reply_to_id,
      edited_at,
      deleted_at,
      created_at,
      sender:user_profiles!messages_sender_id_fkey (
        full_name,
        avatar_url
      )
    `)
    .eq('conversation_id', conversationId)
    .order('id', { ascending: false })
    .limit(limit + 1); // +1 to detect hasMore

  if (cursor) {
    query = query.lt('id', cursor);
  }

  const { data: rows, error } = await query;
  if (error) throw error;

  const hasMore = (rows?.length || 0) > limit;
  const messageRows = hasMore ? rows!.slice(0, limit) : (rows || []);

  // Batch: fetch reactions for these messages
  const messageIds = messageRows.map((m: any) => m.id);
  const reactionsMap = new Map<string, ReactionAggregate[]>();

  if (messageIds.length > 0) {
    const { data: reactions } = await supabase
      .from('message_reactions')
      .select('message_id, emoji, user_id')
      .in('message_id', messageIds);

    // Aggregate reactions per message
    const byMessage = new Map<string, Map<string, { count: number; users: string[] }>>();
    for (const r of reactions || []) {
      if (!byMessage.has(r.message_id)) byMessage.set(r.message_id, new Map());
      const emojiMap = byMessage.get(r.message_id)!;
      if (!emojiMap.has(r.emoji)) emojiMap.set(r.emoji, { count: 0, users: [] });
      const entry = emojiMap.get(r.emoji)!;
      entry.count++;
      entry.users.push(r.user_id);
    }

    for (const [msgId, emojiMap] of byMessage) {
      reactionsMap.set(
        msgId,
        Array.from(emojiMap.entries()).map(([emoji, data]) => ({
          emoji,
          count: data.count,
          reacted_by_me: data.users.includes(userId),
          // Cap carried ids — the tooltip shows the first few + "N more" off
          // `count`, so we never need every id, and an unbounded list would
          // bloat the message cache + IndexedDB persistence.
          user_ids: data.users.slice(0, 100),
        })),
      );
    }
  }

  // Batch: fetch reply-to previews
  const replyToIds = messageRows
    .map((m: any) => m.reply_to_id)
    .filter(Boolean) as string[];
  const replyMap = new Map<string, ReplyToPreview>();

  if (replyToIds.length > 0) {
    const { data: replyMessages } = await supabase
      .from('messages')
      .select(`
        id,
        content,
        metadata,
        sender:user_profiles!messages_sender_id_fkey (
          full_name
        )
      `)
      .in('id', replyToIds);

    for (const r of replyMessages || []) {
      replyMap.set(r.id, {
        id: r.id,
        // Preview only — ChatBubble truncates it visually anyway. Slicing keeps
        // a 16k reply to a 16k message from stacking ~32k into one realtime
        // broadcast payload and halving the headroom for free.
        content: (r.content || '').slice(0, REPLY_PREVIEW_MAX_LENGTH),
        // Carried so the quote can be rendered safely — see the schema note.
        metadata: ((r as any).metadata ?? null) as Record<string, unknown> | null,
        sender_name: (r as any).sender?.full_name || 'Unknown',
      });
    }
  }

  const messages: MessageWithContext[] = messageRows.map((m: any) => ({
    id: m.id,
    conversation_id: m.conversation_id,
    sender_id: m.sender_id,
    sender: {
      full_name: m.sender?.full_name || 'Unknown',
      avatar_url: m.sender?.avatar_url || null,
    },
    content: m.content || '',
    message_type: m.message_type,
    reply_to: m.reply_to_id ? replyMap.get(m.reply_to_id) || null : null,
    attachments: m.attachments || [],
    // `mentions` MUST be in the select above. Without it every fetched
    // message came back with [] and MentionText fell through to its regex
    // fallback, which highlights only the first word of "@First Last".
    // Only realtime-delivered rows (broadcast carries NEW.mentions) kept
    // their full-name chips, so a thread showed both styles at once.
    mentions: m.mentions ?? [],
    metadata: m.metadata || null,
    reactions: reactionsMap.get(m.id) || [],
    edited_at: m.edited_at,
    deleted_at: m.deleted_at,
    created_at: m.created_at,
  }));

  return {
    messages,
    nextCursor: hasMore ? messageRows[messageRows.length - 1]?.id : null,
    hasMore,
  };
}

/**
 * Send a message in a conversation.
 * Validates payload via sendMessagePayloadSchema (D3).
 * Includes client_temp_id in metadata for optimistic reconciliation (ERRATA §4.2).
 */
/** Drop client-only send state (keys starting with `_`) from message metadata. */
export function stripClientMessageState(
  metadata: Record<string, unknown> | null | undefined,
): Record<string, unknown> {
  if (!metadata) return {};
  return Object.fromEntries(
    Object.entries(metadata).filter(([key]) => !key.startsWith('_')),
  );
}

export async function sendMessage(
  conversationId: string,
  payload: SendMessagePayload,
): Promise<MessageWithContext> {
  const validated = sendMessagePayloadSchema.parse(payload);
  const userId = await getAuthUserId();

  // Underscore keys (_optimistic, _status) are client-only send state. The
  // Retry button passes the failed row's metadata back in, and saving them
  // made every reader render the message as failed with a Retry button.
  const metadata: Record<string, unknown> = {
    ...stripClientMessageState(validated.metadata),
    ...(validated.clientTempId ? { client_temp_id: validated.clientTempId } : {}),
  };

  // When sharing a community post into chat, mirror metadata.shared_post_id
  // onto the dedicated FK column so trg_message_post_shared_outbox fires.
  const sharedPostId = typeof metadata.shared_post_id === 'string'
    ? metadata.shared_post_id
    : null;

  const { data, error } = await supabase
    .from('messages')
    .insert({
      // Client-generated UUIDv7 id when present (preserves typed-order on
      // the receiver via id-DESC sort). Falls back to server-side
      // uuid_generate_v7() when omitted.
      ...(validated.id ? { id: validated.id } : {}),
      conversation_id: conversationId,
      sender_id: userId,
      message_type: validated.messageType,
      content: validated.content,
      reply_to_id: validated.replyToId || null,
      attachments: validated.attachments,
      mentions: validated.mentions,
      metadata,
      ...(sharedPostId ? { referenced_post_id: sharedPostId } : {}),
    })
    .select(`
      id,
      conversation_id,
      sender_id,
      message_type,
      content,
      attachments,
      mentions,
      metadata,
      reply_to_id,
      edited_at,
      deleted_at,
      created_at,
      sender:user_profiles!messages_sender_id_fkey (
        full_name,
        avatar_url
      )
    `)
    .single();
  if (error) throw error;

  // Resolve reply-to preview if this is a reply
  let replyTo: ReplyToPreview | null = null;
  if (data.reply_to_id) {
    const { data: replyMsg } = await supabase
      .from('messages')
      .select('id, content, sender:user_profiles!messages_sender_id_fkey ( full_name )')
      .eq('id', data.reply_to_id)
      .single();
    if (replyMsg) {
      replyTo = {
        id: replyMsg.id,
        // Sliced to match what the server-side broadcast trigger sends.
        content: (replyMsg.content || '').slice(0, REPLY_PREVIEW_MAX_LENGTH),
        sender_name: (replyMsg as any).sender?.full_name || 'Unknown',
      };
    }
  }

  const result: MessageWithContext = {
    id: data.id,
    conversation_id: data.conversation_id,
    sender_id: data.sender_id,
    sender: {
      full_name: (data as any).sender?.full_name || 'Unknown',
      avatar_url: (data as any).sender?.avatar_url || null,
    },
    content: data.content || '',
    message_type: data.message_type,
    reply_to: replyTo,
    attachments: data.attachments || [],
    mentions: (data as any).mentions ?? [],
    metadata: data.metadata || null,
    reactions: [],
    edited_at: data.edited_at,
    deleted_at: data.deleted_at,
    created_at: data.created_at,
  };

  // ─── Realtime is SERVER-emitted (hermes migration 468) ───
  // trg_broadcast_message_events fires on this INSERT and sends message.created
  // to conversation:<id> plus conversation.updated to every live participant's
  // private user:<id> topic. The sender's own optimistic echo is reconciled from
  // that broadcast via metadata.client_temp_id, which the trigger forwards.
  // Do NOT re-add a client-side fan-out here: it was unreachable on the private
  // legs (migrations 465/473) and forgeable on the public ones.

  api
    .logActivity({
      type: 'community',
      action: 'sent',
      entity_type: 'community_message',
      entity_id: data.id,
      entity_name: (data.content || '').slice(0, 80) || `[${validated.messageType}]`,
      description: `Sent ${validated.messageType} message in conversation ${conversationId}`,
      metadata: {
        conversation_id: conversationId,
        message_type: validated.messageType,
        has_attachments: (validated.attachments?.length ?? 0) > 0,
        has_reply: !!validated.replyToId,
      },
    })
    .catch(() => {
      // Non-critical: tracking failure must not block message delivery.
    });

  return result;
}

/**
 * Send a one-off broadcast on a topic the caller may not already be subscribed to.
 *
 * If a channel for this topic ALREADY EXISTS (a live UI subscription such as
 * useUserChannel or an open conversation), reuse it and only `.send()` — NEVER
 * remove it, that would destroy the live subscription. Ownership is decided by
 * checking the client registry up-front, not by `_isJoined()`: a real channel
 * that is mid-join or errored reports `_isJoined() === false`, and the previous
 * code would then fall into the ephemeral branch and `removeChannel()` it via the
 * watchdog — killing the live chat subscription under exactly the slow/flaky
 * realtime conditions this guards against (2026-06-15 follow-up).
 *
 * Otherwise subscribe an EPHEMERAL channel WE own, send once, then tear it down.
 * Teardown runs on EVERY terminal outcome — SUBSCRIBED success, CHANNEL_ERROR,
 * TIMED_OUT, CLOSED, or a watchdog timeout — so a failed or slow subscribe can
 * never leak the channel + its WebSocket topic. Because we only ever remove a
 * channel we created here, teardown can never destroy someone else's subscription
 * (previously only the SUBSCRIBED path cleaned up, so every errored/timed-out
 * broadcast — common under load, N-per-message via the participant fan-out —
 * leaked a channel, driving unbounded client memory growth and Realtime-service
 * pressure: the 2026-06-15 realtime channel leak).
 */
async function sendEphemeralBroadcast(
  topic: string,
  broadcastPayload: { type: 'broadcast'; event: string; payload: any },
): Promise<void> {
  // supabase.channel(topic) dedupes by `realtime:${topic}`. If an entry already
  // exists, a live UI subscription owns it — reuse and never remove.
  const realtimeTopic = `realtime:${topic}`;
  const existing = supabase.getChannels().find((c) => c.topic === realtimeTopic);
  if (existing) {
    await existing.send(broadcastPayload);
    return;
  }

  // No channel for this topic yet — create an EPHEMERAL one we own and tear it
  // down on every terminal outcome. Safe to removeChannel because we created it.
  const channel = supabase.channel(topic);
  await new Promise<void>((resolve) => {
    let settled = false;
    let watchdog: ReturnType<typeof setTimeout>;
    const finish = () => {
      if (settled) return;
      settled = true;
      clearTimeout(watchdog);
      supabase.removeChannel(channel);
      resolve();
    };
    // Watchdog: a stuck subscribe must never leak the channel.
    watchdog = setTimeout(finish, 5_000);

    channel.subscribe((status) => {
      if (status === 'SUBSCRIBED') {
        channel.send(broadcastPayload).finally(finish);
      } else if (
        status === 'CHANNEL_ERROR' ||
        status === 'TIMED_OUT' ||
        status === 'CLOSED'
      ) {
        finish();
      }
    });
  });
}

/**
 * Edit a message. Only the sender can edit (RLS enforced). Sets edited_at.
 */
export async function editMessage(
  messageId: string,
  newContent: string,
): Promise<MessageWithContext> {
  const validated = z.string().min(1).max(MESSAGE_MAX_LENGTH).parse(newContent);

  const { data, error } = await supabase
    .from('messages')
    .update({
      content: validated,
      edited_at: new Date().toISOString(),
    })
    .eq('id', messageId)
    .select(`
      id,
      conversation_id,
      sender_id,
      message_type,
      content,
      attachments,
      mentions,
      metadata,
      reply_to_id,
      edited_at,
      deleted_at,
      created_at,
      sender:user_profiles!messages_sender_id_fkey (
        full_name,
        avatar_url
      )
    `)
    .single();
  if (error) throw error;

  const result: MessageWithContext = {
    id: data.id,
    conversation_id: data.conversation_id,
    sender_id: data.sender_id,
    sender: {
      full_name: (data as any).sender?.full_name || 'Unknown',
      avatar_url: (data as any).sender?.avatar_url || null,
    },
    content: data.content || '',
    message_type: data.message_type,
    reply_to: null,
    attachments: data.attachments || [],
    // Keep `mentions` in the select — same rule as fetchMessages, so the
    // returned MessageWithContext is complete (an edit never changes the
    // stored mentions; the DB trigger only rewrites them for tokenised
    // content).
    mentions: (data as any).mentions ?? [],
    metadata: data.metadata || null,
    reactions: [],
    edited_at: data.edited_at,
    deleted_at: data.deleted_at,
    created_at: data.created_at,
  };

  // Broadcast edit so other participants see it in real-time
  await broadcastToConversation(data.conversation_id, {
    type: 'broadcast',
    event: REALTIME_EVENTS.MESSAGE_UPDATED,
    payload: { message_id: data.id, content: validated, edited_at: data.edited_at },
  });

  return result;
}

/**
 * Update a message's content (and optionally metadata) without setting edited_at.
 * Used for streaming Alfie responses so other tabs see progressive updates.
 */
export async function updateMessageContent(
  messageId: string,
  conversationId: string,
  content: string,
  metadata?: Record<string, unknown>,
): Promise<void> {
  const updateFields: Record<string, unknown> = { content };
  if (metadata !== undefined) {
    updateFields.metadata = metadata;
  }

  const { error } = await supabase
    .from('messages')
    .update(updateFields)
    .eq('id', messageId);
  if (error) throw error;

  await broadcastToConversation(conversationId, {
    type: 'broadcast',
    event: REALTIME_EVENTS.MESSAGE_UPDATED,
    payload: {
      message_id: messageId,
      content,
      edited_at: null,
      ...(metadata !== undefined ? { metadata } : {}),
    },
  });
}

/**
 * Soft-delete a message. Sets deleted_at.
 * ERRATA §5.7: clears attachments alongside content on soft delete.
 */
export async function deleteMessage(messageId: string): Promise<void> {
  // Fetch conversation_id before soft-delete for broadcast
  const { data: msg } = await supabase
    .from('messages')
    .select('conversation_id')
    .eq('id', messageId)
    .single();

  const { error } = await supabase
    .from('messages')
    .update({
      deleted_at: new Date().toISOString(),
      content: '',
      attachments: [],
    })
    .eq('id', messageId);
  if (error) throw error;

  // Broadcast deletion so other participants see it in real-time
  if (msg) {
    await broadcastToConversation(msg.conversation_id, {
      type: 'broadcast',
      event: REALTIME_EVENTS.MESSAGE_DELETED,
      payload: { message_id: messageId, deleted_at: new Date().toISOString() },
    });
  }
}

/**
 * Moderator soft-delete of ANOTHER member's message. Goes through the
 * `moderate_delete_message` RPC (mig 367), which authorises the caller as a
 * channel admin/owner (or super_admin) and clears content/attachments server-side
 * — the sender-only `messages_update` RLS would reject a direct update here.
 */
export async function moderateDeleteMessage(messageId: string): Promise<void> {
  // conversation_id for the realtime broadcast (readable via messages_select).
  const { data: msg } = await supabase
    .from('messages')
    .select('conversation_id')
    .eq('id', messageId)
    .single();

  const { error } = await supabase.rpc('moderate_delete_message', { p_message_id: messageId });
  if (error) throw error;

  if (msg) {
    await broadcastToConversation(msg.conversation_id, {
      type: 'broadcast',
      event: REALTIME_EVENTS.MESSAGE_DELETED,
      payload: { message_id: messageId, deleted_at: new Date().toISOString() },
    });
  }
}

/**
 * Add a reaction to a message.
 * Unique constraint (message_id, user_id, emoji) prevents duplicates.
 */
export async function addReaction(
  messageId: string,
  emoji: string,
  conversationId: string,
): Promise<void> {
  const validatedEmoji = z.string().min(1).max(32).parse(emoji);
  const userId = await getAuthUserId();

  const { error } = await supabase.from('message_reactions').insert({
    message_id: messageId,
    user_id: userId,
    emoji: validatedEmoji,
  });
  // Ignore unique constraint violations (already reacted)
  if (error && !error.message.includes('duplicate')) throw error;

  // Broadcast to conversation channel so other participants see it in real-time
  await broadcastToConversation(conversationId, {
    type: 'broadcast',
    event: REALTIME_EVENTS.REACTION_ADDED,
    payload: { message_id: messageId, user_id: userId, emoji: validatedEmoji },
  });

  api
    .logActivity({
      type: 'community',
      action: 'reacted',
      entity_type: 'community_message',
      entity_id: messageId,
      entity_name: validatedEmoji,
      description: `Reacted ${validatedEmoji} on a community message`,
      metadata: { emoji: validatedEmoji, conversation_id: conversationId },
    })
    .catch(() => {
      // Non-critical.
    });
}

/**
 * Remove a reaction from a message.
 */
export async function removeReaction(
  messageId: string,
  emoji: string,
  conversationId: string,
): Promise<void> {
  const userId = await getAuthUserId();

  const { error } = await supabase
    .from('message_reactions')
    .delete()
    .eq('message_id', messageId)
    .eq('user_id', userId)
    .eq('emoji', emoji);
  if (error) throw error;

  // Broadcast to conversation channel so other participants see it in real-time
  await broadcastToConversation(conversationId, {
    type: 'broadcast',
    event: REALTIME_EVENTS.REACTION_REMOVED,
    payload: { message_id: messageId, user_id: userId, emoji },
  });
}

/**
 * Send a broadcast to a conversation channel.
 * Reuses existing channel if already subscribed (e.g. useConversationRealtime).
 */
async function broadcastToConversation(
  conversationId: string,
  broadcastPayload: { type: 'broadcast'; event: string; payload: any },
): Promise<void> {
  await sendEphemeralBroadcast(`conversation:${conversationId}`, broadcastPayload);
}

// NOTE: profile.updated is intentionally NOT broadcast from the client. A member's
// name/avatar change is emitted SERVER-SIDE by a DB trigger on `user_profiles`
// (realtime.send → private `community:profiles` channel) so it can't be spoofed.
// See the community skill: "realtime broadcasts originate from the DB, never the FE."

// ===========================================
// Read State API
// ===========================================

/**
 * Mark conversation as read up to a specific message.
 * Uses mark_as_read RPC with GREATEST() for forward-only (ERRATA §2.3).
 */
export async function markAsRead(
  conversationId: string,
  messageId: string,
  userId?: string,
): Promise<void> {
  // ERRATA §2.4: RPC uses auth.uid() internally — no p_user_id parameter
  const { error } = await supabase.rpc('mark_as_read', {
    p_conversation_id: conversationId,
    p_message_id: messageId,
  });
  if (error) throw error;

  // Broadcast read.updated so the other participant sees blue ticks in real-time.
  // Prefer the caller-supplied (cached) user id; otherwise resolve from the local
  // session. The broadcast payload carries no trust boundary — the RPC above
  // already authorizes via auth.uid().
  const broadcasterId =
    userId ?? (await getSessionUser())?.id;
  if (broadcasterId) {
    await broadcastToConversation(conversationId, {
      type: 'broadcast',
      event: REALTIME_EVENTS.READ_UPDATED,
      payload: { user_id: broadcasterId, last_read_message_id: messageId },
    });
  }
}

// ===========================================
// UUIDv7 generator
// ===========================================

/**
 * Generate a UUIDv7 client-side. Encodes a 48-bit unix-ms timestamp as the
 * leading bits, so id-DESC sort == chronological-DESC at ms precision.
 *
 * Used to preserve sender's typed-order when racing mutations or noisy
 * network paths could otherwise let a slightly later message overtake an
 * earlier one on the server. The server INSERTs with this id rather than
 * generating its own.
 *
 * Format: 8-4-4-4-12 hex (RFC 9562 v7)
 *   tttttttt-tttt-7xxx-vxxx-xxxxxxxxxxxx
 *   t = unix-ms (48 bits)
 *   7 = version 7
 *   v = variant 0b10xx
 *   x = random
 */
export function uuidv7(): string {
  const ts = Date.now();
  const tsHex = ts.toString(16).padStart(12, '0'); // 48 bits = 12 hex chars
  const rand = crypto.getRandomValues(new Uint8Array(10));
  rand[0] = (rand[0] & 0x0f) | 0x70; // version 7
  rand[2] = (rand[2] & 0x3f) | 0x80; // variant 10xx
  const randHex = Array.from(rand, (b) => b.toString(16).padStart(2, '0')).join('');
  return [
    tsHex.slice(0, 8),
    tsHex.slice(8, 12),
    randHex.slice(0, 4),
    randHex.slice(4, 8),
    randHex.slice(8, 20),
  ].join('-');
}

// ===========================================
// Optimistic Message Builder
// ===========================================

/**
 * Build an optimistic message for prepending to cache before server confirms.
 *
 * The id MUST be the same UUIDv7 the sender will pass in the INSERT — that
 * way the realtime broadcast's id matches the optimistic id directly,
 * eliminating the temp-id reconciliation dance and preserving sender-typed
 * ordering on receiver clients (UUIDv7 id-DESC == typed-order-DESC).
 */
export function buildOptimisticMessage(
  conversationId: string,
  payload: SendMessagePayload,
  user: { id: string; full_name: string; avatar_url: string | null },
  replyTo?: ReplyToPreview | null,
): MessageWithContext {
  if (!payload.id) {
    throw new Error('buildOptimisticMessage requires payload.id (use uuidv7())');
  }
  return {
    id: payload.id,
    conversation_id: conversationId,
    sender_id: user.id,
    sender: {
      full_name: user.full_name,
      avatar_url: user.avatar_url,
    },
    content: payload.content,
    message_type: payload.messageType || 'text',
    reply_to: replyTo ?? null,
    attachments: payload.attachments || [],
    mentions: payload.mentions || [],
    metadata: { ...(payload.metadata ?? {}), _optimistic: true },
    reactions: [],
    edited_at: null,
    deleted_at: null,
    created_at: new Date().toISOString(),
  };
}

// ===========================================
// Attachments API — Phase 7 §1-2, ERRATA §5.1-5.6
// ===========================================

/** Per-category file size limits (ERRATA §5.5) — enforced client-side.
 *  Bucket-side cap was set in supabase migration 257 (100 MB), raised to 512 MB
 *  for the chat bucket by migration 477 and for the feed bucket by migration 607.
 *  Both buckets now match these limits — keep them in step.
 *
 *  Video: clips are compressed to ~720p H.264 client-side before upload (see
 *  lib/video-compress), so a real upload is typically a few MB. This 512 MB
 *  ceiling is the SAFETY NET for the fallback path — browsers that can't
 *  transcode (e.g. older iOS webviews) upload the original, and a 1-2 min phone
 *  clip can be a few hundred MB. Resumable (TUS) upload handles the size. */
export const ATTACHMENT_SIZE_LIMITS: Record<string, number> = {
  image: 10 * 1024 * 1024,    // 10 MB
  document: 25 * 1024 * 1024, // 25 MB
  audio: 5 * 1024 * 1024,     // 5 MB
  video: 512 * 1024 * 1024,   // 512 MB (fallback ceiling — see note above)
};


/** MIME types grouped by category — must match bucket allowed_mime_types
 *  (supabase migration 406 for the markdown/zip entries). */
export const ATTACHMENT_MIME_CATEGORIES: Record<string, string[]> = {
  image: ['image/jpeg', 'image/png', 'image/gif', 'image/webp'],
  document: [
    'application/pdf',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    // Markdown and archives — zip lives in `document` rather than getting its
    // own category because the 25 MB cap is right for it and messageTypeFromMime
    // already falls documents through to the generic 'file' renderer.
    'text/markdown',
    'text/x-markdown',
    'text/plain',
    'application/zip',
    'application/x-zip-compressed',
  ],
  audio: ['audio/webm', 'audio/mp4', 'audio/mpeg', 'audio/ogg'],
  // video/quicktime: iOS native camera capture returns .mov. Buckets accept it
  // (migration 427); the transcoder normalises all of these to H.264 HLS.
  video: ['video/mp4', 'video/webm', 'video/quicktime'],
};

/** Extension → canonical MIME, for the types browsers report unreliably.
 *  Deliberately small: this exists to canonicalise the ambiguous cases, not to
 *  become a second allowlist. `.md` arrives as text/markdown, text/plain or ""
 *  depending on OS/browser; `.zip` as application/zip, application/x-zip-compressed,
 *  application/octet-stream or "". */
const EXTENSION_MIME_MAP: Record<string, string> = {
  md: 'text/markdown',
  markdown: 'text/markdown',
  txt: 'text/plain',
  zip: 'application/zip',
};

/**
 * The MIME type we should treat this file as.
 *
 * Extension-first, deliberately: a `.zip` that a stray Android picker reports as
 * `text/plain` is still a zip, and trusting `file.type` there would both
 * mis-categorise it and send a Content-Type the bucket rejects. Falls back to
 * `file.type` for everything the map doesn't cover.
 */
export function resolveEffectiveMime(file: File): string {
  const ext = file.name.split('.').pop()?.toLowerCase() ?? '';
  return EXTENSION_MIME_MAP[ext] ?? file.type;
}

/** Resolve MIME type to category */
function getMimeCategory(mimeType: string): string | null {
  for (const [category, mimes] of Object.entries(ATTACHMENT_MIME_CATEGORIES)) {
    if (mimes.includes(mimeType)) return category;
  }
  return null;
}

/** Validate a file before upload. Returns error string or null if valid. */
export function validateAttachmentFile(file: File): string | null {
  // Try the declared type and the extension-derived type — reject only if
  // neither resolves. This is what lets an empty-`file.type` .zip through.
  const category = getMimeCategory(file.type) ?? getMimeCategory(resolveEffectiveMime(file));
  if (!category) {
    return `File type "${file.type || 'unknown'}" is not supported`;
  }
  const maxSize = ATTACHMENT_SIZE_LIMITS[category];
  if (file.size > maxSize) {
    const maxMB = Math.round(maxSize / (1024 * 1024));
    return `${category.charAt(0).toUpperCase() + category.slice(1)} files must be under ${maxMB} MB`;
  }
  return null;
}

/** Derive message_type from MIME category */
export function messageTypeFromMime(mimeType: string): 'image' | 'file' | 'voice' | 'text' {
  const category = getMimeCategory(mimeType);
  if (category === 'image') return 'image';
  if (category === 'audio') return 'voice';
  // documents and video are sent as 'file' type
  return 'file';
}

/**
 * Kick off background HLS transcoding for a user-recorded video that has already
 * been uploaded (raw) to Supabase Storage via uploadMessageAttachment /
 * uploadPostAttachment. Returns the linked media `assetId` to stamp on the
 * attachment, or null on failure — which is non-fatal: the raw clip still plays
 * from storagePath, transcode is a progressive enhancement.
 *
 * Backend: supabase/functions/enqueue-user-video-transcode (creates the media
 * row + starts the media-processing pipeline).
 */
export async function enqueueUserVideoTranscode(params: {
  surface: 'chat' | 'feed';
  storagePath: string;
  originalFilename: string;
  originalSizeBytes: number;
  originalMimeType: string;
  durationSeconds?: number;
}): Promise<string | null> {
  try {
    const { data, error } = await supabase.functions.invoke<{
      success: boolean;
      assetId: string;
    }>('enqueue-user-video-transcode', { body: params });
    if (error || !data?.assetId) {
      console.error('[enqueueUserVideoTranscode] failed:', error);
      return null;
    }
    return data.assetId;
  } catch (err) {
    console.error('[enqueueUserVideoTranscode] threw:', err);
    return null;
  }
}

/**
 * Upload a file to the message-attachments bucket.
 * Path: {conversation_id}/{client_temp_id}/{filename} (ERRATA §5.2)
 * Returns a fully populated Attachment object (ERRATA §5.1).
 *
 * For images, reads dimensions via createImageBitmap.
 * For audio/video, duration is provided by the caller (not computed here).
 */
export async function uploadMessageAttachment(
  conversationId: string,
  file: File,
  options?: {
    onProgress?: (percent: number) => void;
    durationSeconds?: number;
    waveformData?: number[];
  },
): Promise<z.infer<typeof attachmentSchema>> {
  // Validate
  const error = validateAttachmentFile(file);
  if (error) throw new Error(error);

  const attachmentId = crypto.randomUUID();
  const clientTempId = crypto.randomUUID();
  // Use a UUID-based key (not the raw filename) — original names can contain
  // characters Supabase Storage rejects (spaces, the macOS U+202F before AM/PM),
  // which produced "Invalid key" upload failures. The display name is preserved
  // separately in `filename` below. Mirrors uploadPostImage/uploadPostAttachment.
  const ext = (file.name.split('.').pop() || 'bin').toLowerCase().replace(/[^a-z0-9]/g, '');
  const storagePath = `${conversationId}/${clientTempId}/${attachmentId}.${ext || 'bin'}`;

  // Supabase matches the bucket's allowed_mime_types against the Content-Type
  // header. Without an explicit contentType, supabase-js derives it from
  // `file.type` — which is "" or application/x-zip-compressed for a .zip on many
  // platforms, so the upload 400s even though the allowlist and client-side
  // validation both pass. Send the canonicalised type instead.
  const effectiveMime = resolveEffectiveMime(file);

  // Upload to Supabase Storage
  const { error: uploadError } = await supabase.storage
    .from('message-attachments')
    .upload(storagePath, file, {
      cacheControl: '3600',
      upsert: false,
      contentType: effectiveMime,
    });
  if (uploadError) throw uploadError;

  // Read dimensions if applicable
  let width: number | null = null;
  let height: number | null = null;
  if (file.type.startsWith('image/')) {
    try {
      const bitmap = await createImageBitmap(file);
      width = bitmap.width;
      height = bitmap.height;
      bitmap.close();
    } catch {
      // Non-critical — dimensions remain null
    }
  } else if (effectiveMime.startsWith('video/')) {
    // VIDEO DIMENSIONS ARE WHAT LET THE BUBBLE RESERVE THE RIGHT BOX.
    //
    // Ticket 86d40p593. Until this, only images were measured, so every video
    // attachment persisted width/height null — measured 18 Sep, null on all
    // three dev chat clips. `AttachmentVideoPlayer` passes these straight into
    // VideoPlayer's aspect-ratio, so a null pair means the player falls back to
    // 16:9 and a 720x1280 phone clip renders pillarboxed at a third of its
    // height, then resizes again once the media row arrives with the real
    // shape.
    //
    // The media row does eventually carry the dimensions (Cloudflare stamps
    // them during the Stream copy), but that is a fetch away. Measuring here
    // means the very first frame the sender and every reader paint is already
    // the right shape, with nothing to correct.
    //
    // `effectiveMime` rather than `file.type`: a recorder-produced clip can
    // arrive with an empty or vendor-prefixed type, which is the case
    // resolveEffectiveMime exists to canonicalise.
    const dimensions = await getVideoDimensions(file);
    if (dimensions) {
      width = dimensions.width;
      height = dimensions.height;
    }
  }

  return {
    id: attachmentId,
    filename: file.name,
    storagePath,
    thumbnailPath: null,
    // Store the canonicalised type, not the raw file.type — otherwise an
    // empty-type .zip persists "" and the render-side icon/category logic has
    // nothing to work from.
    mimeType: effectiveMime,
    sizeBytes: file.size,
    width,
    height,
    durationSeconds: options?.durationSeconds ?? null,
    waveformData: options?.waveformData ?? null,
  };
}

/**
 * A video's display dimensions, read in the browser.
 *
 * Returns null rather than throwing on anything unexpected — dimensions are an
 * optimisation for layout, and a clip must still send if they cannot be read.
 *
 * `videoWidth`/`videoHeight` are the DISPLAY dimensions, so a phone clip that
 * carries a rotation matrix reports the rotated shape, which is the one the
 * bubble has to reserve.
 *
 * The timeout matters: a corrupt or unsupported file can leave a detached video
 * element that never fires either event, and without it the send would hang
 * behind an await that never settles.
 */
function getVideoDimensions(
  file: File,
  timeoutMs = 5000,
): Promise<{ width: number; height: number } | null> {
  return new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const video = document.createElement('video');
    let settled = false;

    const finish = (value: { width: number; height: number } | null) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      try {
        video.removeAttribute('src');
        video.load();
      } catch {
        // Teardown only — never let it affect the result.
      }
      URL.revokeObjectURL(url);
      resolve(value);
    };

    const timer = setTimeout(() => finish(null), timeoutMs);

    video.preload = 'metadata';
    video.muted = true;
    video.playsInline = true;
    video.onloadedmetadata = () => {
      const w = video.videoWidth;
      const h = video.videoHeight;
      finish(w > 0 && h > 0 ? { width: w, height: h } : null);
    };
    video.onerror = () => finish(null);
    video.src = url;
  });
}

/**
 * Generate a signed URL for an attachment (ERRATA §5.6).
 * 7-day expiry — long enough that a stable URL survives PWA reopens and the
 * browser HTTP cache hits the same URL for image bytes across sessions.
 * Caller still caches the URL (see useChatImageSignedUrl + file-cache.ts) to
 * avoid regenerating it on every render.
 */
/**
 * Default lifetime, kept at seven days for IMAGES. `useChatImageSignedUrl`
 * caches for 6.5 days directly under it so the same storagePath resolves to the
 * same URL across PWA reopens and the browser HTTP cache keeps hitting the image
 * bytes for a week. Shortening this globally would throw that away.
 */
export const ATTACHMENT_SIGNED_URL_TTL_SECONDS = 7 * 24 * 3600;

/**
 * Lifetime for VIDEO. Ticket 86d40p593.
 *
 * A signed URL is a bearer token pasted into a URL: whoever holds the string
 * gets the bytes, with no account and no membership, until it expires. Seven
 * days of that for a member's personal video message is the exposure — copy it
 * out of the network tab and it is forwardable for a week.
 *
 * An hour is the safe first cut rather than the smallest possible one: it
 * matches the window the in-memory cache already used, so nothing about refresh
 * behaviour changes, and it is comfortably longer than any chat clip plus
 * pauses. That is 168x shorter than before for the same playback experience.
 *
 * It drops to minutes once Cloudflare Stream serves chat, because from then on
 * this URL is only the fallback for the window between upload and the copy
 * completing — not the delivery path.
 */
export const VIDEO_ATTACHMENT_SIGNED_URL_TTL_SECONDS = 60 * 60;

export async function getAttachmentSignedUrl(
  storagePath: string,
  ttlSeconds: number = ATTACHMENT_SIGNED_URL_TTL_SECONDS,
): Promise<string> {
  const { data, error } = await supabase.storage
    .from('message-attachments')
    .createSignedUrl(storagePath, ttlSeconds);
  if (error) throw error;
  return data.signedUrl;
}

// ===========================================
// Message Search — Phase 7 §4
// ERRATA §6.6: auth.uid() used server-side, no p_user_id param
// ===========================================

/** Search result row from search_messages RPC */
export const searchMessageResultSchema = z.object({
  message_id: z.string().uuid(),
  conversation_id: z.string().uuid(),
  conversation_name: z.string().nullable(),
  sender_name: z.string().nullable(),
  content: z.string(), // contains <mark> highlights
  sent_at: z.string(),
  rank: z.number(),
});
export type SearchMessageResult = z.infer<typeof searchMessageResultSchema>;

/**
 * Full-text search across messages the user participates in.
 * Optionally scoped to a single conversation.
 */
export async function searchMessages(
  query: string,
  options?: { conversationId?: string; limit?: number; offset?: number },
): Promise<SearchMessageResult[]> {
  if (!query.trim()) return [];

  const { data, error } = await supabase.rpc('search_messages', {
    p_query: query,
    p_conversation_id: options?.conversationId ?? null,
    p_limit: options?.limit ?? 20,
    p_offset: options?.offset ?? 0,
  });

  if (error) throw error;
  return z.array(searchMessageResultSchema).parse(data ?? []);
}

// ===========================================
// Directory API — Phase 6
// ===========================================

/**
 * Directory member as returned by search_directory RPC.
 * One row per COMMUNITY MEMBER (live seat or live add-on grant - the same set
 * as the @mention roster) since migration 514; the org is attached for display
 * and is null for members with no org (per-seat K&C, staff, grant-only).
 */
export const directoryMemberSchema = z.object({
  user_id: z.string().uuid(),
  full_name: z.string().nullable(),
  avatar_url: z.string().nullable(),
  organization_id: z.string().uuid().nullable(),
  company_name: z.string().nullable(),
  trade: z.string().nullable(),
  location_state: z.string().nullable(),
  location_city: z.string().nullable(),
  lat: z.number().nullable(),
  lng: z.number().nullable(),
  employee_count: z.number().nullable(),
  website: z.string().nullable(),
  current_bss_score: z.number().nullable(),
  current_lifecycle_stage: z.string().nullable(),
  years_in_business: z.number().nullable(),
  industry_vertical: z.string().nullable(),
  company_size: z.string().nullable(),
  tier_name: z.string().nullable(),
  tier_slug: z.string().nullable(),
  total_count: z.number(),
  // Expert fields — Phase 6 §9 (nullable: only present for experts)
  is_expert: z.boolean().optional().default(false),
  /**
   * Explicit mentor flag (organizations.mentor), set by a super_admin in
   * /admin/mentorship — NEVER derived from current_bss_score. Optional so the FE
   * stays compatible with a search_directory() that predates migration 394.
   * NOTE: fetchDirectoryMembers casts rather than .parse()s, so this `.default(false)`
   * does not run at runtime — the real guard is `?? false` at each mapper.
   */
  is_mentor: z.boolean().optional().default(false),
  specialties: z.array(z.string()).nullable().optional(),
  hourly_rate: z.string().nullable().optional(),
  availability_status: z.string().nullable().optional(),
  directory_bio: z.string().nullable().optional(),
  years_experience: z.number().nullable().optional(),
  state: z.string().nullable().optional(),
  pillar: z.string().nullable().optional(),
  // True iff auth.users.last_sign_in_at IS NOT NULL for this user.
  // Only populated by fetchDirectoryMember (drawer) — list fetchers leave it
  // undefined, so consumers must default to false.
  has_signed_in: z.boolean().optional(),
});

export type DirectoryMember = z.infer<typeof directoryMemberSchema>;

/** Directory filter params (D3: Zod schema) */
export const directoryFiltersSchema = z.object({
  query: z.string().optional(),
  state: z.string().optional(),
  trade: z.string().optional(),
  pillar: z.string().optional(),
  tier: z.string().optional(),
  page: z.number().int().positive().optional(),
  limit: z.number().int().positive().optional(),
});
export type DirectoryFilters = z.infer<typeof directoryFiltersSchema>;

export async function fetchDirectoryMembers(
  filters: DirectoryFilters = {},
): Promise<{ members: DirectoryMember[]; total: number }> {
  // The directory page filters/groups client-side and draws the map from this
  // one fetch, so it needs the whole member set on page 1. Since migration 514
  // the RPC lists every community member (prod ~270 and growing); the old cap of
  // 200 would silently truncate. 1000 matches the prewarmed member-map cap.
  const limit = filters.limit ?? 1000;
  const offset = ((filters.page ?? 1) - 1) * limit;

  const { data, error } = await supabase.rpc('search_directory', {
    p_query: filters.query || null,
    p_state: filters.state || null,
    p_trade: filters.trade || null,
    p_tier: filters.tier || null,
    p_limit: limit,
    p_offset: offset,
  });

  if (error) throw error;
  const members = (data || []) as DirectoryMember[];
  const total = members.length > 0 ? members[0].total_count : 0;
  return { members, total };
}

// ===========================================
// User Search — for New Chat member picker
// Queries user_profiles directly. No directory gate (BSS, visibility).
// Any authenticated user can be found for DMs.
// ===========================================

export const chatUserSchema = z.object({
  user_id: z.string().uuid(),
  full_name: z.string().nullable(),
  avatar_url: z.string().nullable(),
});
export type ChatUser = z.infer<typeof chatUserSchema>;

export async function searchUsersForChat(
  query: string,
): Promise<ChatUser[]> {
  const { data: { session } } = await supabase.auth.getSession();
  const currentUserId = session?.user?.id;

  const { data, error } = await supabase
    .from('user_profiles')
    .select('id, full_name, avatar_url')
    .ilike('full_name', `%${query}%`)
    .limit(20);

  if (error) throw error;

  // Exclude current user and map to expected shape
  return (data || [])
    .filter(u => u.id !== currentUserId)
    .map(u => ({
      user_id: u.id,
      full_name: u.full_name,
      avatar_url: u.avatar_url,
    }));
}

export interface NewDmMember extends ChatUser {
  email: string | null;
  isOrgMember: boolean;
}

/**
 * Fetch up to 1000 users for the "new DM" picker via the get_users_for_dm RPC.
 * RPC returns email from auth.users (not readable via PostgREST directly) and
 * orders org members first, then alphabetical. Client-side search filters the
 * capped list — no pagination.
 */
export async function fetchMembersForNewDm(): Promise<NewDmMember[]> {
  const { data, error } = await supabase.rpc('get_users_for_dm', { p_limit: 1000 });
  if (error) throw error;
  if (!data) return [];

  // RPC OUT params are prefixed with `out_` to avoid PL/pgSQL column ambiguity
  // with organization_members.user_id (migration 182b fix).
  return (data as Array<{
    out_user_id: string;
    out_full_name: string | null;
    out_email: string | null;
    out_avatar_url: string | null;
    out_is_org_member: boolean;
  }>).map((u) => ({
    user_id: u.out_user_id,
    full_name: u.out_full_name ?? '',
    avatar_url: u.out_avatar_url,
    email: u.out_email,
    isOrgMember: u.out_is_org_member,
  }));
}

/** Map pin data — lightweight (D3: Zod schema) */
export const mapPinSchema = z.object({
  organization_id: z.string().uuid(),
  lat: z.number(),
  lng: z.number(),
  company_name: z.string(),
  trade: z.string().nullable(),
});
export type MapPin = z.infer<typeof mapPinSchema>;

export async function fetchDirectoryMapPins(): Promise<MapPin[]> {
  const { data, error } = await supabase.rpc('get_directory_map_pins');
  if (error) throw error;
  return (data || []) as MapPin[];
}

/** Single member profile for profile card */
export async function fetchDirectoryMember(
  userId: string,
): Promise<DirectoryMember | null> {
  // 1. User profile (maybeSingle — experts without accounts won't match)
  const { data: profile } = await supabase
    .from('user_profiles')
    .select('id, full_name, avatar_url')
    .eq('id', userId)
    .maybeSingle();

  // 2. Expert data — use search_directory_experts RPC (bypasses RLS on experts table)
  //    The RPC returns COALESCE(e.user_id, e.id) as user_id, so we can match on that.
  const { data: expertRows } = await supabase.rpc('search_directory_experts', {
    p_query: null,
    p_limit: 200,
    p_offset: 0,
  });
  const expertRow = ((expertRows || []) as any[]).find((r: any) => r.user_id === userId);
  const isExpert = !!expertRow;

  // If no user profile AND no expert, nothing to show
  if (!profile && !expertRow) return null;

  // 3. Organization via organization_members (only if we have a user profile).
  //    maybeSingle: org-less members (per-seat, staff, grant-only) are first-class
  //    directory rows since migration 514; zero rows must not 406. Oldest active
  //    membership first, matching search_directory's membership branch.
  let org: any = null;
  if (profile) {
    const { data: orgData } = await supabase
      .from('organization_members')
      .select(`
        organizations (
          id, name, trade, location_state, location_city,
          lat, lng, employee_count, website,
          current_bss_score, current_lifecycle_stage, years_in_business,
          industry_vertical, company_size, mentor
        )
      `)
      .eq('user_id', userId)
      .eq('status', 'active')
      .order('created_at', { ascending: true })
      .limit(1)
      .maybeSingle();
    org = (orgData as any)?.organizations;
  }

  // 4. Community tier (only if we have a user profile)
  let tier: any = null;
  if (profile) {
    const { data: tierData } = await supabase
      .from('user_community_memberships')
      .select('community_tiers (name, code)')
      .eq('user_id', userId)
      .limit(1)
      .maybeSingle();
    tier = (tierData as any)?.community_tiers;
  }

  // 5. Has the underlying auth user ever signed in? Drives Message/Request Quote
  //    gating on the drawer.
  let hasSignedIn = false;
  const { data: signedInData } = await supabase.rpc('has_user_signed_in', {
    p_user_id: userId,
  });
  if (typeof signedInData === 'boolean') hasSignedIn = signedInData;

  return {
    user_id: profile?.id ?? expertRow?.user_id ?? userId,
    full_name: profile?.full_name ?? expertRow?.full_name ?? null,
    avatar_url: profile?.avatar_url ?? expertRow?.avatar_url ?? null,
    organization_id: org?.id ?? expertRow?.organization_id ?? null,
    company_name: org?.name ?? expertRow?.company_name ?? null,
    trade: org?.trade ?? expertRow?.trade ?? null,
    location_state: org?.location_state ?? expertRow?.location_state ?? null,
    location_city: org?.location_city ?? expertRow?.location_city ?? null,
    lat: org?.lat ? Number(org.lat) : (expertRow?.lat ? Number(expertRow.lat) : null),
    lng: org?.lng ? Number(org.lng) : (expertRow?.lng ? Number(expertRow.lng) : null),
    employee_count: org?.employee_count ?? null,
    website: org?.website ?? expertRow?.website ?? null,
    current_bss_score: org?.current_bss_score ? Number(org.current_bss_score) : null,
    current_lifecycle_stage: org?.current_lifecycle_stage ?? null,
    years_in_business: org?.years_in_business ?? null,
    industry_vertical: org?.industry_vertical ?? null,
    company_size: org?.company_size ?? null,
    tier_name: tier?.name ?? expertRow?.tier_name ?? null,
    tier_slug: tier?.code?.toLowerCase() ?? expertRow?.tier_slug ?? null,
    total_count: 1,
    // Expert fields
    is_expert: isExpert,
    // Expert-only rows have no org → not a mentor.
    is_mentor: (org as { mentor?: boolean } | null | undefined)?.mentor ?? false,
    specialties: isExpert ? expertRow.specialties : null,
    hourly_rate: isExpert && expertRow.hourly_rate != null ? String(expertRow.hourly_rate) : null,
    availability_status: isExpert ? expertRow.availability_status : null,
    directory_bio: isExpert ? expertRow.directory_bio : null,
    years_experience: isExpert ? expertRow.years_experience : null,
    state: expertRow?.state ?? null,
    pillar: expertRow?.pillar ?? null,
    has_signed_in: hasSignedIn,
  };
}

/**
 * Fetch directory experts — Phase 6 §9
 * Calls search_directory_experts RPC which returns confirmed, directory-visible
 * experts joined with user_profiles, organizations, and community_tiers.
 * Results are merged client-side with org-based directory results.
 */
export async function fetchDirectoryExperts(
  filters: DirectoryFilters = {},
): Promise<{ members: DirectoryMember[]; total: number }> {
  const limit = filters.limit ?? 20;
  const offset = ((filters.page ?? 1) - 1) * limit;

  const { data, error } = await supabase.rpc('search_directory_experts', {
    p_query: filters.query || null,
    p_state: filters.state || null,
    p_pillar: filters.pillar || null,
    p_limit: limit,
    p_offset: offset,
  });

  if (error) throw error;

  const members: DirectoryMember[] = ((data || []) as any[]).map((row) => ({
    user_id: row.user_id,
    full_name: row.full_name,
    avatar_url: row.avatar_url,
    organization_id: row.organization_id,
    company_name: row.company_name,
    trade: row.trade,
    location_state: row.location_state,
    location_city: row.location_city,
    lat: row.lat ? Number(row.lat) : null,
    lng: row.lng ? Number(row.lng) : null,
    employee_count: row.employee_count,
    website: row.website,
    current_bss_score: row.current_bss_score ? Number(row.current_bss_score) : null,
    current_lifecycle_stage: row.current_lifecycle_stage,
    years_in_business: row.years_in_business,
    industry_vertical: row.industry_vertical,
    company_size: row.company_size,
    tier_name: row.tier_name,
    tier_slug: row.tier_slug,
    total_count: Number(row.total_count),
    is_expert: true,
    // Pure expert rows come from search_directory_experts() (FROM experts), which has
    // no org mentor flag. A user who is BOTH a member and an expert keeps their member
    // row's is_mentor through the merge in MembersDirectory.
    is_mentor: false,
    specialties: row.specialties,
    hourly_rate: row.hourly_rate != null ? String(row.hourly_rate) : null,
    availability_status: row.availability_status,
    directory_bio: row.directory_bio,
    years_experience: row.years_experience,
  }));

  const total = members.length > 0 ? members[0].total_count : 0;
  return { members, total };
}

// ===========================================
// Find or Create DM — ERRATA §8.3
// ===========================================

export async function findOrCreateDm(targetUserId: string): Promise<string> {
  const { data, error } = await supabase.rpc('find_or_create_dm', {
    p_target_user_id: targetUserId,
  });
  if (error) throw error;
  return data as string;
}

// ===========================================
// Leads API — Phase 6 §7
// ===========================================

/** Lead record — D3: uses leadStatusSchema/leadSourceSchema from Drizzle */
export const leadSchema = z.object({
  id: z.string().uuid(),
  conversation_id: z.string().uuid().nullable(),
  requester_user_id: z.string().uuid(),
  requester_org_id: z.string().uuid().nullable(),
  provider_user_id: z.string().uuid(),
  provider_org_id: z.string().uuid().nullable(),
  service_requested: z.string(),
  description: z.string().nullable(),
  status: leadStatusSchema,
  source: leadSourceSchema,
  quoted_amount: z.string().nullable(),
  booked_at: z.string().nullable(),
  completed_at: z.string().nullable(),
  lost_reason: z.string().nullable(),
  crm_deal_id: z.string().uuid().nullable(),
  metadata: z.record(z.string(), z.unknown()).nullable(),
  created_at: z.string(),
  updated_at: z.string(),
});

export type LeadRecord = z.infer<typeof leadSchema>;

/** Enriched lead with requester/provider names — for dashboard display */
const leadPartySchema = z.object({
  full_name: z.string().nullable(),
  avatar_url: z.string().nullable(),
});

export const leadWithPartiesSchema = leadSchema.extend({
  requester: leadPartySchema,
  provider: leadPartySchema,
  requester_org: z.object({ name: z.string().nullable() }).nullable(),
  provider_org: z.object({ name: z.string().nullable() }).nullable(),
});

export type LeadWithParties = z.infer<typeof leadWithPartiesSchema>;

// Supabase select string for leads with joined party data
// FK constraint names from migration 0030
const LEADS_WITH_PARTIES_SELECT = `
  *,
  requester:user_profiles!leads_requester_user_id_user_profiles_id_fk(full_name, avatar_url),
  provider:user_profiles!leads_provider_user_id_user_profiles_id_fk(full_name, avatar_url),
  requester_org:organizations!leads_requester_org_id_organizations_id_fk(name),
  provider_org:organizations!leads_provider_org_id_organizations_id_fk(name)
`;

export async function fetchLeads(
  role: 'provider' | 'requester',
  status?: string,
): Promise<LeadWithParties[]> {
  const userId = await getAuthUserId();

  let query = supabase
    .from('leads')
    .select(LEADS_WITH_PARTIES_SELECT)
    .order('created_at', { ascending: false });

  if (role === 'provider') {
    query = query.eq('provider_user_id', userId);
  } else {
    query = query.eq('requester_user_id', userId);
  }

  if (status) {
    query = query.eq('status', status);
  }

  const { data, error } = await query;
  if (error) throw error;
  return data as LeadWithParties[];
}

export async function updateLeadStatus(
  leadId: string,
  status: string,
  extra?: { quoted_amount?: string; lost_reason?: string },
): Promise<void> {
  const update: Record<string, unknown> = { status };

  if (status === 'booked') update.booked_at = new Date().toISOString();
  if (status === 'completed') update.completed_at = new Date().toISOString();
  if (extra?.quoted_amount) update.quoted_amount = extra.quoted_amount;
  if (extra?.lost_reason) update.lost_reason = extra.lost_reason;

  const { error } = await supabase
    .from('leads')
    .update(update)
    .eq('id', leadId);
  if (error) throw error;
}

// ---------------------------------------------------------------------------
// createLead — Phase 6 §6-7: Request Quote via create_lead RPC
// ---------------------------------------------------------------------------

/** Input shape for the create_lead RPC — D3 */
export const createLeadInputSchema = z.object({
  providerUserId: z.string().uuid(),
  serviceRequested: z.string().min(1).max(500),
  description: z.string().max(2000).optional(),
  providerOrgId: z.string().uuid().optional(),
  source: leadSourceSchema.default('directory'),
});

export type CreateLeadInput = z.infer<typeof createLeadInputSchema>;

/** Return shape from create_lead RPC */
export const createLeadResultSchema = z.object({
  lead_id: z.string().uuid(),
  conversation_id: z.string().uuid(),
});

export type CreateLeadResult = z.infer<typeof createLeadResultSchema>;

export async function createLead(input: CreateLeadInput): Promise<CreateLeadResult> {
  const validated = createLeadInputSchema.parse(input);

  const { data, error } = await supabase.rpc('create_lead', {
    p_provider_user_id: validated.providerUserId,
    p_service_requested: validated.serviceRequested,
    p_description: validated.description ?? null,
    p_provider_org_id: validated.providerOrgId ?? null,
    p_source: validated.source,
  });

  if (error) throw error;
  return createLeadResultSchema.parse(data);
}

// ===========================================
// Block & Report API — Phase 8a
// ===========================================

/**
 * Block a user. Prevents DMs and hides from directory.
 * RLS ensures only the blocker can manage their own blocks.
 */
export async function blockUser(blockedUserId: string): Promise<UserBlock> {
  const userId = await getAuthUserId();

  const { data, error } = await supabase
    .from('user_blocks')
    .insert({
      blocker_user_id: userId,
      blocked_user_id: blockedUserId,
    })
    .select()
    .single();

  if (error) throw error;
  return data as unknown as UserBlock;
}

/**
 * Unblock a user.
 */
export async function unblockUser(blockedUserId: string): Promise<void> {
  const userId = await getAuthUserId();

  const { error } = await supabase
    .from('user_blocks')
    .delete()
    .eq('blocker_user_id', userId)
    .eq('blocked_user_id', blockedUserId);

  if (error) throw error;
}

/**
 * Check if a user is blocked by the current user.
 */
export async function isUserBlocked(blockedUserId: string): Promise<boolean> {
  const userId = await getAuthUserId();

  const { data, error } = await supabase
    .from('user_blocks')
    .select('id')
    .eq('blocker_user_id', userId)
    .eq('blocked_user_id', blockedUserId)
    .limit(1);

  if (error) throw error;
  return (data?.length ?? 0) > 0;
}

/** Input schema for reporting content — D3 typed */
export const reportContentInputSchema = z.object({
  reportedUserId: z.string().uuid().optional(),
  conversationId: z.string().uuid().optional(),
  messageId: z.string().uuid().optional(),
  /** A Speedy report (hermes migration 696): no conversation or message with it. */
  speedyId: z.string().uuid().optional(),
  reason: reportReasonSchema,
  details: z.string().max(2000).optional(),
});
export type ReportContentInput = z.infer<typeof reportContentInputSchema>;

/**
 * What the reporter reads back. Members can read only these report columns: the reviewer and the
 * reviewer's note are kept from them (hermes migration 696 column grant), so the insert names its
 * columns rather than returning the whole row.
 */
export interface ReportReceipt {
  id: string;
  status: string;
  created_at: string;
}

/**
 * Report content (user, conversation, message, or Speedy) for moderation review.
 */
export async function reportContent(input: ReportContentInput): Promise<ReportReceipt> {
  const userId = await getAuthUserId();
  const validated = reportContentInputSchema.parse(input);

  const { data, error } = await supabase
    .from('message_reports')
    .insert({
      reporter_user_id: userId,
      reported_user_id: validated.reportedUserId ?? null,
      conversation_id: validated.conversationId ?? null,
      message_id: validated.messageId ?? null,
      ...(validated.speedyId ? { speedy_id: validated.speedyId } : {}),
      reason: validated.reason,
      details: validated.details ?? null,
    })
    .select('id, status, created_at')
    .single();

  if (error) throw error;
  return data as ReportReceipt;
}

// ===========================================
// Group chats (hermes migration 647)
// ===========================================
// Every membership and detail change goes through a SECURITY DEFINER RPC that
// also writes the centred system line; direct table writes to a group are
// blocked by RLS. The one exception is "Remove from list": after leaving, the
// member deletes their own participant row.

export const GROUP_NAME_MAX = 50;
export const GROUP_MAX_PEOPLE = 100;

export interface GroupDetailsInput {
  title: string;
  color: string | null;
  avatarUrl: string | null;
}

export async function createGroupConversation(
  input: GroupDetailsInput & { memberIds: string[] },
): Promise<{ id: string }> {
  const { data, error } = await supabase.rpc('create_group_conversation', {
    p_title: input.title,
    p_member_ids: input.memberIds,
    p_color: input.color,
    p_avatar_url: input.avatarUrl,
  });
  if (error) throw error;
  return { id: data as string };
}

/** Returns how many people were actually added (live members are skipped). */
export async function addGroupMembers(conversationId: string, userIds: string[]): Promise<number> {
  const { data, error } = await supabase.rpc('add_group_members', {
    p_conversation_id: conversationId,
    p_user_ids: userIds,
  });
  if (error) throw error;
  return (data as number) ?? 0;
}

export async function removeGroupMember(conversationId: string, userId: string): Promise<void> {
  const { error } = await supabase.rpc('remove_group_member', {
    p_conversation_id: conversationId,
    p_user_id: userId,
  });
  if (error) throw error;
}

export async function setGroupMemberRole(
  conversationId: string,
  userId: string,
  role: 'admin' | 'member',
): Promise<void> {
  const { error } = await supabase.rpc('set_group_member_role', {
    p_conversation_id: conversationId,
    p_user_id: userId,
    p_role: role,
  });
  if (error) throw error;
}

/** Sets the full desired state: name, colour and photo (null removes it). */
export async function updateGroup(conversationId: string, input: GroupDetailsInput): Promise<void> {
  const { error } = await supabase.rpc('update_group', {
    p_conversation_id: conversationId,
    p_title: input.title,
    p_color: input.color,
    p_avatar_url: input.avatarUrl,
  });
  if (error) throw error;
}

export async function leaveGroup(conversationId: string): Promise<void> {
  const { error } = await supabase.rpc('leave_group', { p_conversation_id: conversationId });
  if (error) throw error;
}

/** After leaving a group: drop it from the chat list (and its history). */
export async function removeGroupFromList(conversationId: string): Promise<void> {
  const userId = await getAuthUserId();
  const { error } = await supabase
    .from('conversation_participants')
    .delete()
    .eq('conversation_id', conversationId)
    .eq('user_id', userId)
    .not('left_at', 'is', null);
  if (error) throw error;
}

/** Largest photo a person can pick. It is shrunk before upload, so this is only a sanity cap. */
export const GROUP_PHOTO_MAX_BYTES = 30 * 1024 * 1024;

/**
 * Uploads a group photo to the public avatars bucket under the caller's own
 * folder (the bucket's existing per-user policy) and returns its public URL.
 * The picked image is shrunk in the browser first (512px longest side, WebP),
 * so a 30 MB phone photo lands as roughly 100 KB — the tile never shows above
 * 88px — and the bucket's 5 MB cap never bites. Paths are content-addressed,
 * so a new photo is always a new URL.
 */
export async function uploadGroupPhoto(file: File): Promise<string> {
  const userId = await getAuthUserId();
  if (!file.type.startsWith('image/')) throw new Error('The photo must be an image');
  if (file.size > GROUP_PHOTO_MAX_BYTES) throw new Error('The photo must be under 30 MB');
  const photo = await downscaleImage(file, 512);
  const ext = photo.type === 'image/webp' ? 'webp' : 'png';
  const path = `${userId}/group-${crypto.randomUUID()}.${ext}`;
  const { error } = await supabase.storage
    .from('avatars')
    .upload(path, photo, { cacheControl: '31536000', upsert: false, contentType: photo.type });
  if (error) throw error;
  return supabase.storage.from('avatars').getPublicUrl(path).data.publicUrl;
}

// ===========================================
// Org Members — fetch members of user's org
// ===========================================

export interface OrgMember {
  user_id: string;
  full_name: string;
  avatar_url: string | null;
  role: string; // 'admin' | 'member' | 'viewer' (owner is identified by organizations.created_by)
}

/** The current user's own organization membership, resolved in one query. */
export interface MyOrgMembership {
  organizationId: string | null;
  /** Drives the "Everyone at <Org>" row and the suggested group name. */
  organizationName: string | null;
  annualRevenueRange: string | null;
  companySize: string | null;
}

/**
 * The current user's organization membership row — organization_id plus the
 * org's revenue range and size — in ONE organization_members read.
 *
 * Canonical source for the hooks that each used to resolve "my org"
 * independently (org id, revenue range, and the org-members roster's own id
 * lookup), which fanned out 3–4 duplicate organization_members queries on every
 * community load (2026-07-02 audit). useMyOrgMembership caches it; the org-id
 * and revenue-range hooks are client-side selects over the same query.
 */
export async function fetchMyOrgMembership(): Promise<MyOrgMembership | null> {
  const userId = await getAuthUserId();
  const { data, error } = await supabase
    .from('organization_members')
    .select('organization_id, organizations ( name, annual_revenue_range, company_size )')
    .eq('user_id', userId)
    .limit(1)
    .maybeSingle();
  if (error) throw error;
  if (!data) return null;
  const org = (data as any).organizations;
  return {
    organizationId: (data as any).organization_id ?? null,
    organizationName: org?.name?.trim() || null,
    annualRevenueRange: org?.annual_revenue_range ?? null,
    companySize: org?.company_size ?? null,
  };
}

/**
 * Fetch the members of a given organization with their profiles (includes the
 * current user so the FE can determine own role). Split out from fetchOrgMembers
 * so callers that already know the org id (via useMyOrgMembership) skip the
 * redundant org-id lookup.
 */
export async function fetchOrgRoster(orgId: string): Promise<OrgMember[]> {
  const { data: members, error } = await supabase
    .from('organization_members')
    .select('user_id, role, user_profiles (id, full_name, avatar_url)')
    .eq('organization_id', orgId);
  if (error) throw error;

  return (members || []).map((m: any) => ({
    user_id: m.user_id,
    full_name: m.user_profiles?.full_name ?? 'Unknown',
    avatar_url: m.user_profiles?.avatar_url ?? null,
    role: m.role,
  }));
}

/**
 * Fetch all members of the current user's organization.
 * Returns user_id, full_name, avatar_url for each member (includes current user).
 */
export async function fetchOrgMembers(): Promise<OrgMember[]> {
  const membership = await fetchMyOrgMembership();
  if (!membership?.organizationId) return [];
  return fetchOrgRoster(membership.organizationId);
}

/** A lightweight id→profile entry for the prewarmed community member map. */
export interface MemberProfile {
  user_id: string;
  full_name: string | null;
  avatar_url: string | null;
}

/**
 * Fetch a lightweight directory of community members (id, name, avatar) for the
 * prewarmed id→profile map. Powers INSTANT reactor / mention / participant
 * tooltips without a per-hover fetch — resolve names/avatars from this map
 * client-side instead of joining user_profiles per popover. RLS scopes the
 * result to the profiles this user can already see (the people they interact
 * with), so it stays small (a few KB).
 */
export async function fetchCommunityMemberMap(): Promise<MemberProfile[]> {
  const { data, error } = await supabase
    .from('user_profiles')
    .select('id, full_name, avatar_url')
    // Bounded prewarm: load up to 1000 members. Reactor/mention/participant
    // tooltips for ids beyond this fall back to a targeted fetch (ReactionPills),
    // so correctness holds; this just caps the prewarmed payload. See the
    // community skill — "prewarmed directory is capped at 1000".
    .limit(1000);
  if (error) throw error;
  return ((data || []) as any[]).map((p) => ({
    user_id: p.id as string,
    full_name: (p.full_name ?? null) as string | null,
    avatar_url: (p.avatar_url ?? null) as string | null,
  }));
}

/** A (member, scope_key) pair the current user is allowed to introduce (mig 370). */
export interface IntroduciblePair {
  member_id: string;
  scope_key: string;
}

const introduciblePairSchema = z.object({
  member_id: z.string(),
  scope_key: z.string(),
});

/**
 * Prewarm: the (member, scope_key) pairs the current user may introduce, via the
 * `introducible_pairs` RPC (mig 370). Lets the "Introduce" button visibility AND
 * the picker candidate list be computed ENTIRELY client-side — no per-profile /
 * per-keystroke calls. `scope_key` (`chan:<id>` / `org:<id>`) lets the picker
 * offer only B's that share A's scope. Empty for global admins (they use the
 * global member search). Capped at 1000 server-side; members beyond the cap just
 * don't get the button hint — the server `can_introduce` check is authoritative.
 */
export async function fetchIntroduciblePairs(): Promise<IntroduciblePair[]> {
  const { data, error } = await supabase.rpc('introducible_pairs');
  if (error) throw error;
  return z.array(introduciblePairSchema).parse(data ?? []);
}

// ── Tier chat label (display alias) — super_admin, mig 376 ──
// The tier "display alias" reuses the per-chat rename lever (conversations.title
// diverging from the tier name), written through a super_admin RPC because
// conversations RLS blocks a super_admin from renaming another tier's chat.

const tierChatTitleResultSchema = z.object({
  conversation_id: z.string(),
  tier_name: z.string(),
  title: z.string(),
  is_aliased: z.boolean(),
});
export type TierChatTitleResult = z.infer<typeof tierChatTitleResultSchema>;

/** super_admin: rename a tier's community chat (the display alias). Empty/null
 *  resets to the tier's canonical name. Server-gated (set_tier_chat_title). */
export async function setTierChatTitle(
  tierId: string,
  title: string | null,
): Promise<TierChatTitleResult> {
  const { data, error } = await supabase.rpc('set_tier_chat_title', {
    p_tier_id: tierId,
    p_title: title,
  });
  if (error) throw error;
  return tierChatTitleResultSchema.parse(data);
}

export interface TierChatLabel {
  tier_id: string;
  code: string;
  tier_name: string;
  conversation_id: string | null;
  title: string | null;
  is_aliased: boolean;
}
const tierChatLabelSchema = z.object({
  tier_id: z.string(),
  code: z.string(),
  tier_name: z.string(),
  conversation_id: z.string().nullable(),
  title: z.string().nullable(),
  is_aliased: z.boolean(),
});

/** super_admin: every tier + its chat conversation title (list_tier_chat_labels). */
export async function fetchTierChatLabels(): Promise<TierChatLabel[]> {
  const { data, error } = await supabase.rpc('list_tier_chat_labels');
  if (error) throw error;
  return z.array(tierChatLabelSchema).parse(data ?? []);
}

/**
 * Fetch a map of user_id → annual_revenue_range for all org members.
 * Used for client-side pod filtering on conversations.
 */
export async function fetchParticipantRevenueMap(): Promise<Map<string, string>> {
  const { data } = await supabase
    .from('organization_members')
    .select('user_id, organizations!inner ( annual_revenue_range )');

  const map = new Map<string, string>();
  for (const row of (data ?? []) as any[]) {
    const range = row.organizations?.annual_revenue_range;
    if (range) map.set(row.user_id, range);
  }
  return map;
}

// ===========================================
// User Tier — fetch current user's community tier level
// ===========================================

export interface UserTierInfo {
  tierLevel: number;
  tierName: string;
  tierSlug: string;
}

/**
 * Fetch the current user's community tier info.
 * Returns tier level, name, slug. Defaults to level 0 if no membership.
 */
export async function fetchUserTier(): Promise<UserTierInfo> {
  const userId = await getAuthUserId();

  // Self-heal: ensure a membership row exists. Resolves via
  // allowed_signups.community_tier_code, falling back to BASECAMP.
  // Idempotent — returns the existing tier_id when one is already present.
  // Throw on failure so React Query retries with backoff instead of silently
  // returning the no-membership default and stranding the UI on a spinner.
  {
    const { error: bootstrapError } = await supabase.rpc('bootstrap_community_membership');
    if (bootstrapError) throw bootstrapError;
  }

  // `tierLevel` is the GATING value, so it must be the same effective level the
  // RLS policies compute — max(coalesce(tier_override, tier_level)) — which is
  // exactly what get_user_tier returns. Reading tier_level off the joined
  // membership row (as this used to) silently ignored tier_override, so an admin
  // override granted higher access everywhere except the surfaces gated here.
  //
  // `tierName`/`tierSlug` name the plan the user HOLDS — the highest tier across
  // their seat(s) AND any active, unexpired community-tier add-on grant (WS3
  // parity: an add-on is a comped seat, so an add-on-only holder is named by the
  // granted tier, exactly like a paid seat — the server's get_user_tier already
  // counts it). Ranked by BASE tier_level: a `tier_override` raises what someone
  // can REACH (tierLevel above), it does not rename the plan, so naming ignores
  // it. The slug drives default hub selection and one-shot channel auto-join in
  // Community.tsx; both must follow the tier the member actually holds so an
  // add-on holder lands on their tier's hub, not the bootstrap Basecamp row.
  const nowIso = new Date().toISOString();
  const [membershipResult, addonResult, levelResult] = await Promise.all([
    supabase
      .from('user_community_memberships')
      .select('community_tiers (tier_level, name, code)')
      .eq('user_id', userId),
    // RLS (`addon_grants_select`, mig 307) scopes this to the caller's own grants
    // + their active orgs' org-scoped grants. Package grants (community_tier_id
    // NULL) are content-only and excluded.
    supabase
      .from('addon_grants')
      .select('community_tiers!inner (tier_level, name, code)')
      .eq('status', 'active')
      .not('community_tier_id', 'is', null)
      .or(`expires_at.is.null,expires_at.gt.${nowIso}`),
    supabase.rpc('get_user_tier', { p_user_id: userId }),
  ]);

  if (membershipResult.error) throw membershipResult.error;
  if (addonResult.error) throw addonResult.error;

  type DisplayTier = { tier_level: number; name: string | null; code: string | null };
  const embed = (t: unknown): DisplayTier | null => {
    const v = Array.isArray(t) ? t[0] : t;
    return (v as DisplayTier | null) ?? null;
  };

  // Highest tier by base level across seats + add-ons drives the display name.
  const candidates: DisplayTier[] = [];
  for (const row of (membershipResult.data ?? []) as Array<{ community_tiers: unknown }>) {
    const t = embed(row.community_tiers);
    if (t) candidates.push(t);
  }
  for (const row of (addonResult.data ?? []) as Array<{ community_tiers: unknown }>) {
    const t = embed(row.community_tiers);
    if (t) candidates.push(t);
  }
  const chosen = candidates.reduce<DisplayTier | null>(
    (best, t) => (!best || t.tier_level > best.tier_level ? t : best),
    null,
  );

  const effectiveLevel = typeof levelResult.data === 'number'
    ? levelResult.data
    : (chosen?.tier_level ?? 0);

  if (!chosen) return { tierLevel: effectiveLevel, tierName: 'Free', tierSlug: 'free' };

  return {
    tierLevel: effectiveLevel,
    tierName: chosen.name ?? 'Free',
    tierSlug: chosen.code?.toLowerCase() ?? 'free',
  };
}

/**
 * Fetch community channels with their required tier level resolved.
 * Returns channels enriched with the required tier_level for gating.
 */
export async function fetchCommunityChannelsWithTiers(): Promise<
  (CommunityChannelItem & { required_tier_level: number })[]
> {
  const { data, error } = await supabase
    .from('community_channels')
    .select(`
      id,
      name,
      description,
      channel_type,
      community_tier_id,
      community_id,
      required_tier_id,
      default_channel,
      icon,
      display_order,
      community_tiers!community_channels_community_tier_id_community_tiers_id_fk (id, name, code)
    `)
    // Sidebar hides archived channels even for super_admin — they surface in
    // the Channel Management section's Archived tab.
    .is('archived_at', null)
    .order('display_order', { ascending: true })
    .order('name');

  if (error) throw error;

  // Now resolve required_tier_level for each channel
  // Get all tier levels in one query
  const { data: tiers } = await supabase
    .from('community_tiers')
    .select('id, tier_level');

  const tierLevelMap = new Map<string, number>();
  for (const t of tiers || []) {
    tierLevelMap.set(t.id, t.tier_level);
  }

  return ((data ?? []) as unknown as CommunityChannelItem[]).map((ch) => ({
    ...ch,
    // Global channels (community_tier_id IS NULL) have no tier gate at the
    // channel level — RLS still requires active community membership. Treat
    // their required_tier_level as 0 so any UI tier-comparison passes.
    required_tier_level: ch.required_tier_id
      ? (tierLevelMap.get(ch.required_tier_id) ?? 0)
      : ch.community_tier_id
        ? (tierLevelMap.get(ch.community_tier_id) ?? 0)
        : 0,
  }));
}

// ===========================================
// Community Feed — Posts, Reactions, Comments
// Types derived from Drizzle schema (D3)
// ===========================================

/** Author profile joined onto a post */
export const postAuthorSchema = z.object({
  full_name: z.string(),
  avatar_url: z.string().nullable(),
});
export type PostAuthor = z.infer<typeof postAuthorSchema>;

/** The System bot user. Authors AI recap posts (rendered as "Alfie"). */
export const SYSTEM_USER_ID = '00000000-0000-0000-0000-000000000001';
/** Display name for System-authored recap posts (proxy_display_name IS NULL). */
export const ALFIE_DISPLAY_NAME = 'Alfie';
/**
 * Alfie's weekly "what was your win?" prompt (hermes migration 572).
 * A plain string, deliberately NOT `PostType`: Prometheus CI builds against a
 * PINNED @hermes/db tag (HERMES_DB_VERSION in the workflows), so the enum value
 * only lands here after that pin is bumped. Comparing raw row values keeps the
 * chip working on either side of the bump.
 */
export const WINS_PROMPT_POST_TYPE = 'wins_prompt';

/**
 * Resolve the display author for a post/comment row.
 *
 * - proxy_display_name set → legacy archive import attributed to the system
 *   user; synthesise "System · for <name>" (the join is null — system user is
 *   RLS-hidden, migration 202).
 * - author = system user, proxy null → an AI recap; render as "Alfie".
 * - otherwise → the joined profile, or the fallback.
 */
function resolvePostAuthor(
  row: { author_id?: string | null; user_profiles?: unknown; proxy_display_name?: string | null },
  fallback: string,
): PostAuthor {
  if (row.proxy_display_name) {
    return {
      full_name: `System · for ${row.proxy_display_name}`,
      avatar_url: null,
    };
  }
  if (row.author_id === SYSTEM_USER_ID) {
    return {
      full_name: ALFIE_DISPLAY_NAME,
      avatar_url: null,
    };
  }
  return (row.user_profiles as PostAuthor | null | undefined) ?? {
    full_name: fallback,
    avatar_url: null,
  };
}

/** Aggregated reaction count for a post */
export const postReactionCountSchema = z.object({
  emoji: z.string(),
  count: z.number(),
  reacted_by_me: z.boolean(),
});
export type PostReactionCount = z.infer<typeof postReactionCountSchema>;

/** Post with author + reaction summaries — what the feed returns */
export const feedPostSchema = z.object({
  id: z.string().uuid(),
  // Nullable: NULL = post in a global channel (see migration 199).
  community_tier_id: z.string().uuid().nullable(),
  channel_id: z.string().uuid().nullable(),
  author_id: z.string().uuid(),
  title: z.string().nullable(),
  content: z.string().nullable(),
  images: z.array(postImageSchema),
  attachments: z.array(postAttachmentSchema),
  location: postLocationSchema.nullable(),
  pinned: z.boolean(),
  post_type: postTypeSchema,
  content_format: z.enum(['plain', 'markdown']).default('plain'),
  created_at: z.string(),
  updated_at: z.string().nullable(),
  author: postAuthorSchema,
  reactions: z.array(postReactionCountSchema),
  comment_count: z.number(),
  channel_name: z.string().nullable().optional(),
  author_revenue_range: z.string().nullable().optional(),
  mentions: z.array(mentionSchema).default([]),
  // When set, the post is a "share to feed" of this community_event. The
  // feed UI renders an inline event-embed card with title/time/RSVP.
  referenced_event_id: z.string().uuid().nullable().optional(),
  // When the share targets one session of a recurring series, the canonical
  // occurrence slot — the embed renders that session (its date + agenda).
  referenced_event_occurrence_start: z.string().nullable().optional(),
  // Speedies (F-129): the post shares this Speedy. Read only while the speedies flag is on.
  speedy_id: z.string().uuid().nullable().optional(),
});
export type FeedPost = z.infer<typeof feedPostSchema>;

/**
 * A post select with the shared-Speedy column added, only while the speedies flag is on: with it
 * off the feed reads exactly what it did before, and never asks for a column an environment may
 * not have yet. The select text keeps its literal type, so the row types are unchanged (the column
 * is read with a cast).
 */
function withSpeedyColumn<T extends string>(select: T): T {
  return (isSpeediesEnabled() ? select.replace(/(\n\s*)created_at,/, '$1speedy_id,$1created_at,') : select) as T;
}

/** A page of posts for infinite scroll */
export interface FeedPostPage {
  posts: FeedPost[];
  nextCursor: string | undefined;
}

/** Comment with author profile */
export const postCommentWithAuthorSchema = z.object({
  id: z.string().uuid(),
  post_id: z.string().uuid(),
  author_id: z.string().uuid(),
  parent_comment_id: z.string().uuid().nullable(),
  content: z.string(),
  created_at: z.string(),
  deleted_at: z.string().nullable(),
  author: postAuthorSchema,
  mentions: z.array(mentionSchema).default([]),
  images: z.array(postImageSchema).default([]),
  attachments: z.array(postAttachmentSchema).default([]),
  location: postLocationSchema.nullable().default(null),
});
export type PostCommentWithAuthor = z.infer<typeof postCommentWithAuthorSchema>;

/** Hub card — community tier with aggregate counts */
export const communityHubCardSchema = z.object({
  id: z.string().uuid(),
  code: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  tier_level: z.number(),
  member_count: z.number(),
  post_count: z.number(),
  last_post_at: z.string().nullable(),
});
export type CommunityHubCard = z.infer<typeof communityHubCardSchema>;

/**
 * Hub card — CUSTOM community (migrations 378-383). Standalone peer of the
 * tier communities in the switcher: identity is data-driven (icon key +
 * color), membership-gated (the RPC only returns communities the caller
 * belongs to, or all for super admins).
 */
export const customCommunityCardSchema = z.object({
  kind: z.literal('community'),
  id: z.string().uuid(),
  name: z.string(),
  description: z.string().nullable(),
  icon: z.string().nullable(),
  color: z.string().nullable(),
  member_count: z.number(),
  post_count: z.number(),
  last_post_at: z.string().nullable(),
  chat_conversation_id: z.string().uuid().nullable(),
  member_role: z.string().nullable(),
});
export type CustomCommunityCard = z.infer<typeof customCommunityCardSchema>;

/**
 * Unified switcher card. Components must branch through getHubVisual
 * (lib/hub-visual) — passing a community card to getTierConfig would silently
 * fall back to BASECAMP branding.
 */
export type HubCard = (CommunityHubCard & { kind: 'tier' }) | CustomCommunityCard;

export async function fetchCustomCommunitiesForHub(): Promise<CustomCommunityCard[]> {
  const { data, error } = await supabase.rpc('get_custom_communities_for_hub');
  if (error) throw error;
  return (data ?? []).map((row: any) =>
    customCommunityCardSchema.parse({
      kind: 'community' as const,
      id: row.community_id,
      name: row.name,
      description: row.description ?? null,
      icon: row.icon ?? null,
      color: row.color ?? null,
      member_count: Number(row.member_count ?? 0),
      post_count: Number(row.post_count ?? 0),
      last_post_at: row.last_post_at ?? null,
      chat_conversation_id: row.chat_conversation_id ?? null,
      member_role: row.member_role ?? null,
    }),
  );
}

// ===========================================
// Post CRUD
// ===========================================

/**
 * Fetch paginated feed posts for a community tier.
 * Ordered: pinned desc, created_at desc.
 * RLS ensures tier-gated visibility.
 */
export async function fetchPosts(
  tierId: string,
  cursor?: string,
  limit = 20,
  channelId?: string,
  pods?: PodNumber[],
  /** Custom-community scope: the all-channels view filters by community_id
   *  instead of community_tier_id (tierId is ignored). */
  communityId?: string,
): Promise<FeedPostPage> {
  const userId = await getAuthUserId();

  // Pod filtering: pre-fetch matching author IDs when one or more pods are selected.
  //
  // Semantics:
  //   - Empty or omitted pods → no filter (show everything).
  //   - A null `annual_revenue_range` means the author has no assigned pod; they
  //     default to pod 3 (the entry-level bucket). Null-pod authors only appear
  //     when the selected pods include pod 3.
  //   - Otherwise, an author matches when their revenue range maps to a pod in
  //     the selected list.
  let podAuthorIds: string[] | null = null;
  if (pods && pods.length > 0) {
    const { data: orgMembers } = await supabase
      .from('organization_members')
      .select('user_id, organizations!inner ( annual_revenue_range )');

    const includesPod3 = pods.includes(3);
    const selectedRanges = new Set<string>(pods.map(getRevenueRangeForPod));

    podAuthorIds = (orgMembers ?? [])
      .filter((m: any) => {
        const range = m.organizations?.annual_revenue_range;
        if (range == null) return includesPod3;
        return selectedRanges.has(range);
      })
      .map((m: any) => m.user_id);

    if (podAuthorIds.length === 0) return { posts: [], nextCursor: undefined };
  }

  // Pagination cursor is the post's UUIDv7 id, not created_at.
  // UUIDv7 is lexicographically time-ordered so ORDER BY id DESC matches
  // "newest first" and sidesteps same-second collisions that created_at
  // cursors suffered under high-write bursts (ERRATA §1.1 / §2.2).
  // Pinned posts still come first via composite ordering; this inherits
  // the pre-existing edge case where a newly-pinned older post could
  // skip past a cursor — not a regression from the timestamp cursor.
  let query = supabase
    .from('community_posts')
    .select(withSpeedyColumn(`
      id,
      community_tier_id,
      channel_id,
      author_id,
      title,
      content,
      images,
      attachments,
      location,
      pinned,
      post_type,
      content_format,
      mentions,
      referenced_event_id,
      referenced_event_occurrence_start,
      created_at,
      updated_at,
      proxy_display_name,
      user_profiles!community_posts_author_id_user_profiles_id_fk ( full_name, avatar_url )
    `))
    .is('deleted_at', null)
    .order('pinned', { ascending: false })
    .order('id', { ascending: false })
    .limit(limit + 1); // fetch one extra for cursor

  if (channelId) {
    // Channel-specific view: the channel itself scopes the result. Tier-gated
    // channels naturally only have tier-matching posts; global channels only
    // have NULL-tier posts. RLS enforces the visibility either way.
    query = query.eq('channel_id', channelId);
  } else if (communityId) {
    // Custom-community all-channels view: posts stamped with community_id
    // (member-gated by RLS).
    query = query.eq('community_id', communityId);
  } else {
    // All-channels view: tier posts only. System/global channels (Events,
    // Masterminds — community_tier_id IS NULL) have their own surfaces and
    // intentionally do NOT aggregate into this feed. Invariant upheld by
    // createPost (below): system-channel posts always have tier=NULL,
    // tier-channel posts always carry their tier id.
    query = query.eq('community_tier_id', tierId);
  }

  if (podAuthorIds) {
    query = query.in('author_id', podAuthorIds);
  }

  if (cursor) {
    query = query.lt('id', cursor);
  }

  const { data, error } = await query;
  if (error) throw error;

  const rows = (data ?? []) as any[];
  const hasMore = rows.length > limit;
  const pageRows = hasMore ? rows.slice(0, limit) : rows;
  const nextCursor = hasMore ? pageRows[pageRows.length - 1]?.id : undefined;

  if (pageRows.length === 0) {
    return { posts: [], nextCursor: undefined };
  }

  // Batch fetch reaction counts, user's own reactions, and author revenue ranges
  const postIds = pageRows.map((r: any) => r.id);
  const authorIds = [...new Set(pageRows.map((r: any) => r.author_id))];

  const [reactionsResult, userReactionsResult, commentCountsResult, authorRevenueResult] = await Promise.all([
    supabase
      .from('post_reactions')
      .select('post_id, emoji')
      .in('post_id', postIds),
    supabase
      .from('post_reactions')
      .select('post_id, emoji')
      .in('post_id', postIds)
      .eq('user_id', userId),
    supabase
      .from('post_comments')
      .select('post_id')
      .in('post_id', postIds)
      .is('deleted_at', null),
    supabase
      .from('organization_members')
      .select('user_id, organizations ( annual_revenue_range )')
      .in('user_id', authorIds),
  ]);

  // Build author → revenue range map for pod badges
  const authorRevenueMap = new Map<string, string>();
  for (const row of (authorRevenueResult.data ?? []) as any[]) {
    const range = row.organizations?.annual_revenue_range;
    if (range) authorRevenueMap.set(row.user_id, range);
  }

  // Build reaction counts per post
  const reactionMap = new Map<string, Map<string, number>>();
  for (const r of reactionsResult.data ?? []) {
    if (!reactionMap.has(r.post_id)) reactionMap.set(r.post_id, new Map());
    const emojiMap = reactionMap.get(r.post_id)!;
    emojiMap.set(r.emoji, (emojiMap.get(r.emoji) ?? 0) + 1);
  }

  // Build user's own reactions set
  const userReactionSet = new Set<string>();
  for (const r of userReactionsResult.data ?? []) {
    userReactionSet.add(`${r.post_id}:${r.emoji}`);
  }

  // Build comment counts per post
  const commentCountMap = new Map<string, number>();
  for (const c of commentCountsResult.data ?? []) {
    commentCountMap.set(c.post_id, (commentCountMap.get(c.post_id) ?? 0) + 1);
  }

  const posts: FeedPost[] = pageRows.map((row: any) => {
    const emojiMap = reactionMap.get(row.id) ?? new Map<string, number>();
    const reactions: PostReactionCount[] = Array.from(emojiMap.entries()).map(([emoji, count]) => ({
      emoji,
      count,
      reacted_by_me: userReactionSet.has(`${row.id}:${emoji}`),
    }));

    return {
      id: row.id,
      community_tier_id: row.community_tier_id,
      channel_id: row.channel_id ?? null,
      author_id: row.author_id,
      title: row.title ?? null,
      content: row.content,
      images: row.images ?? [],
      attachments: row.attachments ?? [],
      location: row.location ?? null,
      pinned: row.pinned,
      post_type: row.post_type,
      content_format: row.content_format ?? 'plain',
      mentions: row.mentions ?? [],
      referenced_event_id: row.referenced_event_id ?? null,
      referenced_event_occurrence_start:
        (row as { referenced_event_occurrence_start?: string | null })
          .referenced_event_occurrence_start ?? null,
      speedy_id: (row as { speedy_id?: string | null }).speedy_id ?? null,
      created_at: row.created_at,
      updated_at: row.updated_at,
      author: resolvePostAuthor(row, 'Unknown'),
      reactions,
      comment_count: commentCountMap.get(row.id) ?? 0,
      author_revenue_range: authorRevenueMap.get(row.author_id) ?? null,
    };
  });

  return { posts, nextCursor };
}

/**
 * Fetch a single post by id for the deep-link page (`/community/post/$postId`).
 * RLS enforces tier-gated visibility — if the caller can't read the row, this
 * returns null. Mirrors the row-shape work that fetchPosts does, but for one
 * post, so the single-post page can reuse RichFeedPost directly.
 */
export async function fetchPost(postId: string): Promise<FeedPost | null> {
  const userId = await getAuthUserId();

  const { data: row, error } = await supabase
    .from('community_posts')
    .select(withSpeedyColumn(`
      id,
      community_tier_id,
      channel_id,
      author_id,
      title,
      content,
      images,
      attachments,
      location,
      pinned,
      post_type,
      content_format,
      mentions,
      referenced_event_id,
      referenced_event_occurrence_start,
      created_at,
      updated_at,
      proxy_display_name,
      user_profiles!community_posts_author_id_user_profiles_id_fk ( full_name, avatar_url )
    `))
    .eq('id', postId)
    .is('deleted_at', null)
    .maybeSingle();

  if (error) throw error;
  if (!row) return null;

  const [reactionsResult, userReactionsResult, commentCountsResult, authorRevenueResult] = await Promise.all([
    supabase
      .from('post_reactions')
      .select('emoji')
      .eq('post_id', row.id),
    supabase
      .from('post_reactions')
      .select('emoji')
      .eq('post_id', row.id)
      .eq('user_id', userId),
    supabase
      .from('post_comments')
      .select('id', { count: 'exact', head: true })
      .eq('post_id', row.id)
      .is('deleted_at', null),
    supabase
      .from('organization_members')
      .select('organizations ( annual_revenue_range )')
      .eq('user_id', row.author_id)
      .maybeSingle(),
  ]);

  const emojiMap = new Map<string, number>();
  for (const r of reactionsResult.data ?? []) {
    emojiMap.set(r.emoji, (emojiMap.get(r.emoji) ?? 0) + 1);
  }
  const userReactionSet = new Set<string>();
  for (const r of userReactionsResult.data ?? []) userReactionSet.add(r.emoji);

  const reactions: PostReactionCount[] = Array.from(emojiMap.entries()).map(([emoji, count]) => ({
    emoji,
    count,
    reacted_by_me: userReactionSet.has(emoji),
  }));

  return {
    id: row.id,
    community_tier_id: row.community_tier_id,
    channel_id: row.channel_id ?? null,
    author_id: row.author_id,
    title: row.title ?? null,
    content: row.content,
    images: row.images ?? [],
    attachments: row.attachments ?? [],
    location: row.location ?? null,
    pinned: row.pinned,
    post_type: row.post_type,
    content_format: row.content_format ?? 'plain',
    mentions: row.mentions ?? [],
    referenced_event_id: (row as { referenced_event_id?: string | null }).referenced_event_id ?? null,
    referenced_event_occurrence_start:
      (row as { referenced_event_occurrence_start?: string | null })
        .referenced_event_occurrence_start ?? null,
    speedy_id: (row as { speedy_id?: string | null }).speedy_id ?? null,
    created_at: row.created_at,
    updated_at: row.updated_at,
    author: resolvePostAuthor(row, 'Unknown'),
    reactions,
    comment_count: commentCountsResult.count ?? 0,
    author_revenue_range:
      (authorRevenueResult.data as any)?.organizations?.annual_revenue_range ?? null,
  };
}

/**
 * Create a new post in a community tier, or in a global channel when tierId
 * is null. Posts in global channels MUST have community_tier_id=null —
 * setting it to a tier would route the post through the tier-gated RLS
 * branch and hide it from members of other tiers (defeats the global scope).
 */
export async function createPost(
  tierId: string | null,
  content: string,
  images: PostImage[] = [],
  postType: PostType = 'post',
  channelId?: string,
  attachments: PostAttachment[] = [],
  location?: PostLocation | null,
  title?: string | null,
  mentions?: Mention[],
  /** Custom-community scope: stamps community_id (tier must be null). */
  communityId?: string | null,
  /**
   * Embed a community event on the post (the /recording composer command).
   * Same columns the "Share event to feed" path writes — the feed renders
   * an EventPostEmbed card, recording-aware when the event has one.
   */
  referencedEvent?: { eventId: string; occurrenceStart?: string | null } | null,
  /** Speedies (F-129): share this Speedy on the post (`/speedy` in the composer). */
  speedyId?: string | null,
): Promise<FeedPost> {
  const userId = await getAuthUserId();
  const validatedMentions = mentions
    ? z.array(mentionSchema).parse(mentions)
    : [];

  const { data, error } = await supabase
    .from('community_posts')
    .insert({
      community_tier_id: communityId ? null : tierId,
      ...(communityId ? { community_id: communityId } : {}),
      author_id: userId,
      // Persist mentions inline as [mention:…] tokens so they move with the
      // text and can't drift. The DB trigger derives mentions[] from the tokens.
      content: tokenizeContent(content, validatedMentions),
      images,
      attachments,
      mentions: validatedMentions,
      ...(location ? { location } : {}),
      post_type: postType,
      ...(channelId ? { channel_id: channelId } : {}),
      ...(title != null && title.trim() !== '' ? { title: title.trim() } : {}),
      ...(referencedEvent
        ? {
          referenced_event_id: referencedEvent.eventId,
          referenced_event_occurrence_start: referencedEvent.occurrenceStart ?? null,
        }
        : {}),
      ...(speedyId ? { speedy_id: speedyId } : {}),
    })
    .select(withSpeedyColumn(`
      id,
      community_tier_id,
      channel_id,
      author_id,
      title,
      content,
      images,
      attachments,
      location,
      pinned,
      post_type,
      content_format,
      mentions,
      referenced_event_id,
      referenced_event_occurrence_start,
      created_at,
      updated_at,
      proxy_display_name,
      user_profiles!community_posts_author_id_user_profiles_id_fk ( full_name, avatar_url )
    `))
    .single();

  if (error) throw error;

  const row = data as any;

  api
    .logActivity({
      type: 'community',
      action: 'created',
      entity_type: 'community_post',
      entity_id: row.id,
      entity_name: (row.content || '').slice(0, 80) || `[${postType}]`,
      description: `Created ${postType} in community`,
      metadata: {
        community_tier_id: tierId,
        channel_id: channelId ?? null,
        post_type: postType,
        has_images: (images?.length ?? 0) > 0,
        has_attachments: (attachments?.length ?? 0) > 0,
      },
    })
    .catch(() => {
      // Non-critical: tracking failure must not block post creation.
    });

  // Fire-and-forget: drop the post into every relevant conversation
  // (user DMs, channel/tier conversation for #channel and @everyone).
  // Recipients see it instantly via Realtime; the post_mention outbox
  // row still fires for legacy audit but the handler now skips kinds
  // that this share covers.
  void sharePostFromMentions({
    postId: row.id,
    authorId: userId,
    postTierId: row.community_tier_id ?? null,
    mentions: validatedMentions,
  });

  return {
    id: row.id,
    community_tier_id: row.community_tier_id,
    channel_id: row.channel_id ?? null,
    author_id: row.author_id,
    title: row.title ?? null,
    content: row.content,
    images: row.images ?? [],
    attachments: row.attachments ?? [],
    location: row.location ?? null,
    pinned: row.pinned,
    post_type: row.post_type,
    content_format: row.content_format ?? 'plain',
    mentions: row.mentions ?? [],
    referenced_event_id: row.referenced_event_id ?? null,
    referenced_event_occurrence_start: row.referenced_event_occurrence_start ?? null,
    speedy_id: row.speedy_id ?? null,
    created_at: row.created_at,
    updated_at: row.updated_at,
    author: resolvePostAuthor(row, 'You'),
    reactions: [],
    comment_count: 0,
  };
}

export interface UpdatePostFields {
  title?: string | null;
  content?: string;
  images?: PostImage[];
  attachments?: PostAttachment[];
  location?: PostLocation | null;
  mentions?: Mention[];
  /** Move the post to another channel; null clears it. Omit to leave it alone.
   *  Legal destinations are enforced by trg_guard_channel_move (migration 505),
   *  not here — the same guard covers the admin_update_system_post path. */
  channelId?: string | null;
}

export interface UpdatedPost {
  id: string;
  title: string | null;
  content: string;
  images: PostImage[];
  attachments: PostAttachment[];
  location: PostLocation | null;
  updated_at: string;
}

/**
 * Update a post. Only the author can update (RLS enforced). Sets updated_at
 * so the FE can derive an "Edited" marker from `updated_at !== created_at`.
 * Omitted fields are not modified.
 */
export async function updatePost(
  postId: string,
  fields: UpdatePostFields,
): Promise<UpdatedPost> {
  const update: Record<string, unknown> = {
    updated_at: new Date().toISOString(),
  };
  if (fields.title !== undefined) {
    if (fields.title === null) {
      update.title = null;
    } else {
      const trimmed = z.string().max(200).parse(fields.title).trim();
      update.title = trimmed === '' ? null : trimmed;
    }
  }
  if (fields.content !== undefined) {
    update.content = z.string().max(16000).parse(fields.content);
  }
  if (fields.images !== undefined) {
    update.images = z.array(postImageSchema).parse(fields.images);
  }
  if (fields.attachments !== undefined) {
    update.attachments = z.array(postAttachmentSchema).parse(fields.attachments);
  }
  if (fields.location !== undefined) {
    update.location = fields.location;
  }
  if (fields.channelId !== undefined) {
    update.channel_id = fields.channelId;
  }
  let parsedMentions: Mention[] | undefined;
  if (fields.mentions !== undefined) {
    parsedMentions = z.array(mentionSchema).parse(fields.mentions);
    update.mentions = parsedMentions;
  }
  // Persist mentions inline as [mention:…] tokens (see createPost). The editor
  // sends content + mentions together, so tokenise whenever both are present.
  if (typeof update.content === 'string' && parsedMentions) {
    update.content = tokenizeContent(update.content, parsedMentions);
  }

  const { data, error } = await supabase
    .from('community_posts')
    .update(update)
    .eq('id', postId)
    .select('id, title, content, images, attachments, location, updated_at, author_id, community_tier_id, mentions')
    .single();

  if (error) throw error;

  // Fire-and-forget: reconcile the post's auto-share chat messages against its
  // CURRENT mentions (insert new, restore archived, archive removed). Only runs
  // when mentions are part of the update — content edits without a mentions
  // payload can't change the mention set (mentions are derived from the same
  // editor payload). Mirrors how createPost calls sharePostFromMentions; a
  // failure here must never roll back the post edit.
  if (parsedMentions !== undefined) {
    const row = data as { author_id?: string | null; community_tier_id?: string | null };
    void reconcilePostMentionShares({
      postId: data.id,
      authorId: (row.author_id ?? '') as string,
      postTierId: (row.community_tier_id ?? null) as string | null,
      mentions: parsedMentions,
    });
  }

  return {
    id: data.id,
    title: (data.title ?? null) as string | null,
    content: data.content ?? '',
    images: (data.images ?? []) as PostImage[],
    attachments: (data.attachments ?? []) as PostAttachment[],
    location: (data.location ?? null) as PostLocation | null,
    updated_at: data.updated_at,
  };
}

/**
 * Edit a System-authored recap post (title + content only) via the
 * admin_update_system_post RPC (mig 401). The RPC enforces server-side that the
 * post is System-authored with proxy_display_name NULL, and that the caller is a
 * super_admin or a chat admin of the post's community — the author-only
 * community_posts UPDATE RLS can't express that, so this is a SECURITY DEFINER
 * RPC, not a direct update. content_format stays 'markdown'.
 */
export async function updateSystemPost(
  postId: string,
  fields: {
    title: string | null;
    content: string;
    channelId?: string | null;
    /**
     * Rich fields. Present => the 8-argument overload (migration 651), which writes them.
     * Absent => the older signatures, which leave them alone.
     *
     * These exist because the composer offers attach/photo/location on a system post exactly
     * as it does on any other. Before 651 the RPC had nowhere to put them, so an admin could
     * attach a document, get a 204 and a "Post updated" toast, and lose it.
     */
    images?: PostImage[];
    attachments?: PostAttachment[];
    location?: PostLocation | null;
    mentions?: Mention[];
  },
): Promise<void> {
  const title = fields.title === null
    ? null
    : (z.string().max(200).parse(fields.title).trim() || null);
  const content = z.string().max(16000).parse(fields.content);

  // THREE signatures exist on purpose (migrations 505 and 651). Omitting channelId calls the
  // original 3-arg form, which leaves the channel untouched; passing it calls the 4-arg
  // overload, where null means "no channel"; passing any rich field calls the 8-arg overload.
  // None takes a DEFAULT, so the overload resolves unambiguously in every case.
  const wantsRich = fields.images !== undefined || fields.attachments !== undefined ||
    fields.location !== undefined || fields.mentions !== undefined;

  let args: Record<string, unknown>;
  if (wantsRich) {
    args = {
      p_post_id: postId,
      p_title: title,
      p_content: content,
      p_channel_id: fields.channelId ?? null,
      p_images: fields.images ?? [],
      p_attachments: fields.attachments ?? [],
      p_location: fields.location ?? null,
      // `mentions` is NOT NULL on the table. Null here means "leave it"; [] clears it.
      p_mentions: fields.mentions ?? null,
    };
  } else if (fields.channelId === undefined) {
    args = { p_post_id: postId, p_title: title, p_content: content };
  } else {
    args = {
      p_post_id: postId,
      p_title: title,
      p_content: content,
      p_channel_id: fields.channelId,
    };
  }

  const { error } = await supabase.rpc('admin_update_system_post', args);
  if (error) throw error;
}

/**
 * The communities (tier or custom) where the current user is a chat admin/owner.
 * Backed by get_my_chat_admin_scopes (mig 401). Cached once; the FE derives
 * per-post edit-button visibility with no further round trips.
 */
export async function fetchMyChatAdminScopes(): Promise<{
  tierIds: string[];
  communityIds: string[];
}> {
  const { data, error } = await supabase.rpc('get_my_chat_admin_scopes');
  if (error) throw error;
  const tierIds: string[] = [];
  const communityIds: string[] = [];
  for (
    const row of (data ?? []) as {
      community_tier_id: string | null;
      community_id: string | null;
    }[]
  ) {
    if (row.community_tier_id) tierIds.push(row.community_tier_id);
    if (row.community_id) communityIds.push(row.community_id);
  }
  return { tierIds, communityIds };
}

/**
 * Soft-delete a post. Uses the soft_delete_post RPC (migration 203) which
 * enforces permission (author / super_admin / org admin of the author's org)
 * server-side.
 */
export async function deletePost(postId: string): Promise<void> {
  const { error } = await supabase.rpc('soft_delete_post', {
    p_post_id: postId,
  });
  if (error) throw error;
}

/**
 * Soft-delete a comment. Uses the soft_delete_comment RPC (migration 203)
 * with the same permission model as posts.
 */
export async function deleteComment(commentId: string): Promise<void> {
  const { error } = await supabase.rpc('soft_delete_comment', {
    p_comment_id: commentId,
  });
  if (error) throw error;
}

// ===========================================
// Post Reactions
// ===========================================

/**
 * Toggle a reaction on a post.
 * If the user already has this emoji on this post, removes it. Otherwise adds it.
 * Returns 'added' or 'removed'.
 */
/** A reaction row joined with the reactor's profile. Shape is shared
 *  between post and message reactions so the same hover-card UI can render
 *  for either entity. */
export interface Reactor {
  emoji: string;
  user_id: string;
  full_name: string | null;
  avatar_url: string | null;
}

/** Back-compat alias — older imports use `PostReactor`. */
export type PostReactor = Reactor;

/**
 * Fetch all reactors on a post (for the hover-card "who reacted" tooltip).
 * Capped at 200 — extreme outliers see a "+N others" tail in the UI.
 */
export async function fetchPostReactors(postId: string): Promise<PostReactor[]> {
  const { data, error } = await supabase
    .from('post_reactions')
    .select('emoji, user_id, user_profiles!inner(full_name, avatar_url)')
    .eq('post_id', postId)
    .order('created_at', { ascending: true })
    .limit(200);
  if (error) throw error;
  return ((data || []) as any[]).map((r) => ({
    emoji: r.emoji,
    user_id: r.user_id,
    full_name: r.user_profiles?.full_name ?? null,
    avatar_url: r.user_profiles?.avatar_url ?? null,
  }));
}

/**
 * Fetch all reactors on a chat message (for the hover-card on reaction
 * pills). Mirrors fetchPostReactors. Capped at 200.
 */
export async function fetchMessageReactors(messageId: string): Promise<Reactor[]> {
  const { data, error } = await supabase
    .from('message_reactions')
    .select('emoji, user_id, user_profiles!inner(full_name, avatar_url)')
    .eq('message_id', messageId)
    .order('created_at', { ascending: true })
    .limit(200);
  if (error) throw error;
  return ((data || []) as any[]).map((r) => ({
    emoji: r.emoji,
    user_id: r.user_id,
    full_name: r.user_profiles?.full_name ?? null,
    avatar_url: r.user_profiles?.avatar_url ?? null,
  }));
}

export async function togglePostReaction(
  postId: string,
  emoji: string,
): Promise<'added' | 'removed'> {
  const userId = await getAuthUserId();

  // Check if reaction exists
  const { data: existing } = await supabase
    .from('post_reactions')
    .select('id')
    .eq('post_id', postId)
    .eq('user_id', userId)
    .eq('emoji', emoji)
    .maybeSingle();

  if (existing) {
    const { error } = await supabase
      .from('post_reactions')
      .delete()
      .eq('id', existing.id);
    if (error) throw error;
    return 'removed';
  } else {
    const { error } = await supabase
      .from('post_reactions')
      .insert({
        post_id: postId,
        user_id: userId,
        emoji,
      });
    if (error) throw error;
    api
      .logActivity({
        type: 'community',
        action: 'reacted',
        entity_type: 'post_reaction',
        entity_id: postId,
        entity_name: emoji,
        description: `Reacted ${emoji} on community post`,
        metadata: { emoji, target: 'community_post' },
      })
      .catch(() => {
        // Non-critical: tracking failure must not affect reactions.
      });
    return 'added';
  }
}

// ===========================================
// Post Comments
// ===========================================

/**
 * Fetch comments for a post with author profiles.
 * Returns top-level comments + replies, ordered by created_at asc.
 */
export async function fetchPostComments(
  postId: string,
): Promise<PostCommentWithAuthor[]> {
  const { data, error } = await supabase
    .from('post_comments')
    .select(`
      id,
      post_id,
      author_id,
      parent_comment_id,
      content,
      mentions,
      images,
      attachments,
      location,
      created_at,
      deleted_at,
      proxy_display_name,
      user_profiles!post_comments_author_id_user_profiles_id_fk ( full_name, avatar_url )
    `)
    .eq('post_id', postId)
    .is('deleted_at', null)
    .order('created_at', { ascending: true });

  if (error) throw error;

  // Deleting a comment cascades to its replies (soft_delete_comment), so a
  // simple deleted_at filter never leaves orphaned replies.
  return (data ?? []).map((row: any) => ({
    id: row.id,
    post_id: row.post_id,
    author_id: row.author_id,
    parent_comment_id: row.parent_comment_id,
    content: row.content,
    created_at: row.created_at,
    deleted_at: row.deleted_at,
    author: resolvePostAuthor(row, 'Unknown'),
    mentions: row.mentions ?? [],
    images: row.images ?? [],
    attachments: row.attachments ?? [],
    location: row.location ?? null,
  }));
}

/**
 * Add a comment to a post. Supports reply threading via parentCommentId.
 */
export async function addPostComment(
  postId: string,
  content: string,
  parentCommentId?: string,
  mentions?: Mention[],
  images?: PostImage[],
  attachments?: PostAttachment[],
  location?: PostLocation | null,
): Promise<PostCommentWithAuthor> {
  const userId = await getAuthUserId();
  const validatedMentions = mentions
    ? z.array(mentionSchema).parse(mentions)
    : [];
  const validatedImages = images ? z.array(postImageSchema).parse(images) : [];
  const validatedAttachments = attachments
    ? z.array(postAttachmentSchema).parse(attachments)
    : [];
  const validatedLocation = location ? postLocationSchema.parse(location) : null;

  const { data, error } = await supabase
    .from('post_comments')
    .insert({
      post_id: postId,
      author_id: userId,
      parent_comment_id: parentCommentId ?? null,
      content,
      mentions: validatedMentions,
      images: validatedImages,
      attachments: validatedAttachments,
      location: validatedLocation,
    })
    .select(`
      id,
      post_id,
      author_id,
      parent_comment_id,
      content,
      mentions,
      images,
      attachments,
      location,
      created_at,
      deleted_at,
      proxy_display_name,
      user_profiles!post_comments_author_id_user_profiles_id_fk ( full_name, avatar_url )
    `)
    .single();

  if (error) throw error;

  const row = data as any;

  api
    .logActivity({
      type: 'community',
      action: 'commented',
      entity_type: 'post_comment',
      entity_id: row.id,
      entity_name: (row.content || '').slice(0, 80) || '[empty]',
      description: `Commented on community post ${postId}`,
      metadata: {
        post_id: postId,
        parent_comment_id: parentCommentId ?? null,
        is_reply: !!parentCommentId,
      },
    })
    .catch(() => {
      // Non-critical.
    });

  // Fire-and-forget: same auto-share as posts, scoped to the comment's
  // parent post. We need the parent post's tier for @everyone resolution.
  void (async () => {
    let postTierId: string | null = null;
    if (validatedMentions.some((m) => m.kind === 'everyone')) {
      const { data: parent } = await supabase
        .from('community_posts')
        .select('community_tier_id')
        .eq('id', postId)
        .single();
      postTierId = (parent as { community_tier_id: string | null } | null)
        ?.community_tier_id ?? null;
    }
    await sharePostFromMentions({
      postId,
      authorId: userId,
      postTierId,
      mentions: validatedMentions,
    });
  })();

  return {
    id: row.id,
    post_id: row.post_id,
    author_id: row.author_id,
    parent_comment_id: row.parent_comment_id,
    content: row.content,
    created_at: row.created_at,
    deleted_at: row.deleted_at,
    author: resolvePostAuthor(row, 'You'),
    mentions: row.mentions ?? [],
    images: row.images ?? [],
    attachments: row.attachments ?? [],
    location: row.location ?? null,
  };
}

// ===========================================
// Community Hub — Tier Cards
// ===========================================

/**
 * Fetch community tiers with aggregate member & post counts for hub cards.
 */
export async function fetchCommunityTiersForHub(): Promise<CommunityHubCard[]> {
  const userId = await getAuthUserId();

  // Self-heal membership before computing visible tiers, otherwise a wiped /
  // never-bootstrapped user has maxEffectiveTierLevel=0 and sees an empty hub.
  // Throw on failure so React Query retries with backoff.
  {
    const { error: bootstrapError } = await supabase.rpc('bootstrap_community_membership');
    if (bootstrapError) throw bootstrapError;
  }

  // Fetch tiers + stats + the caller's effective entitlement in parallel.
  //
  // Entitlement comes from the SERVER functions, not a client-side replay of the
  // rule. `user_content_access_codes` is the same SECURITY DEFINER function the
  // RLS policies use, and it unions coded memberships WITH active add-on grants;
  // `get_user_tier` is max(coalesce(tier_override, tier_level)). Reading the
  // membership rows directly (as this used to) drifted from both: it never saw
  // add-on grants at all, so a user granted a higher tier as an ADD-ON had that
  // tier's hub card filtered out — which also stripped the icon and name off its
  // chat, since ConversationItem resolves those by looking the tier up in these
  // cards. It also meant tier_override was bypassed whenever any coded
  // membership existed, which is now every tier rather than only WunderPack.
  const [tiersResult, statsResult, codesResult, levelResult, chatTitlesResult] = await Promise.all([
    supabase
      .from('community_tiers')
      .select('id, code, name, description, tier_level, content_access_codes')
      .order('tier_level', { ascending: true }),
    // SECURITY DEFINER RPC bypasses RLS to get accurate counts
    supabase.rpc('get_community_hub_stats'),
    supabase.rpc('user_content_access_codes', { p_user_id: userId }),
    supabase.rpc('get_user_tier', { p_user_id: userId }),
    // Per-tier chat conversation titles — the display alias lives here (a tier's
    // chat title diverging from its name). Resolving it into the hub card `name`
    // makes the alias show on the feed header + community header + sidebar, not
    // just the chat. RLS returns only tiers the caller can access (== visible hub).
    supabase
      .from('conversations')
      .select('community_tier_id, title')
      .eq('type', 'community_channel'),
  ]);

  if (tiersResult.error) throw tiersResult.error;
  if (!tiersResult.data || tiersResult.data.length === 0) return [];

  // (a) the caller's full code set — coded memberships UNION active add-on
  // grants — and (b) their max effective tier_level for the ladder fallback.
  // Both straight from the server, so there is one definition of each.
  const explicitCodes = new Set<string>(
    ((codesResult.data ?? []) as string[]).map((c) => c.toLowerCase()),
  );
  const anyExplicitMembership = explicitCodes.size > 0;
  const maxEffectiveTierLevel = typeof levelResult.data === 'number' ? levelResult.data : 0;

  const statsMap = new Map<string, { member_count: number; post_count: number; last_post_at: string | null }>();
  for (const s of statsResult.data ?? []) {
    statsMap.set(s.tier_id, {
      member_count: Number(s.member_count),
      post_count: Number(s.post_count),
      last_post_at: s.last_post_at,
    });
  }

  // Mirrors user_has_tier_access (migration 267): when the caller has any
  // content access codes, gate visibility by code-set membership; otherwise
  // fall back to the tier_level ladder.
  //
  // Every K&C tier now populates content_access_codes (not just WunderPack, as
  // an earlier version of this comment said), so the code path is what runs for
  // essentially every member and the ladder is a genuine edge case — a caller
  // with no coded entitlement at all. That is why the codes must come from
  // user_content_access_codes: it is the only source that includes add-on
  // grants, and a missing code here silently hides a whole tier.
  const visible = (tier: { code: string; tier_level: number }) =>
    anyExplicitMembership
      ? explicitCodes.has(tier.code.toLowerCase())
      : tier.tier_level <= maxEffectiveTierLevel;

  // Map tier_id -> its chat conversation title (the alias source).
  const titleByTier = new Map<string, string>();
  for (const c of (chatTitlesResult.data ?? []) as Array<{ community_tier_id: string | null; title: string | null }>) {
    if (c.community_tier_id && c.title) titleByTier.set(c.community_tier_id, c.title);
  }

  return tiersResult.data
    .filter(visible)
    .map(tier => {
      const stats = statsMap.get(tier.id);
      // Display alias: the tier's chat title when it has been intentionally
      // renamed (differs from the tier name); otherwise the tier name.
      const alias = titleByTier.get(tier.id);
      const displayName = alias && alias !== tier.name ? alias : tier.name;
      return {
        id: tier.id,
        code: tier.code,
        name: displayName,
        description: tier.description,
        tier_level: tier.tier_level,
        member_count: stats?.member_count ?? 0,
        post_count: stats?.post_count ?? 0,
        last_post_at: stats?.last_post_at ?? null,
      };
    });
}

// ===========================================
// Tier visual identity (data-driven badges)
// ===========================================

export interface TierIdentityRow {
  code: string;
  icon_url: string | null;
  accent_color: string | null;
}

/**
 * Fetch every tier's visual identity (badge + accent) for the frontend override
 * store (lib/tier-config). RLS returns the authenticated-visible rows; anything
 * unreadable or missing simply falls back to the shipped defaults the DB is
 * seeded to (migration 463), so this is best-effort and never blocks paint.
 */
export async function fetchTierIdentities(): Promise<TierIdentityRow[]> {
  const { data, error } = await supabase
    .from('community_tiers')
    .select('code, icon_url, accent_color');
  if (error) throw error;
  return (data ?? []) as TierIdentityRow[];
}

/**
 * Upload a replacement tier badge to the `tier-badges` bucket (super_admin-only
 * by RLS) and return its public URL. Path: {code}/{uuid}.{ext}.
 */
export async function uploadTierBadge(code: string, file: File): Promise<string> {
  const ext = (file.name.split('.').pop() || 'svg').toLowerCase();
  const path = `${code}/${crypto.randomUUID()}.${ext}`;
  const { error } = await supabase.storage
    .from('tier-badges')
    .upload(path, file, {
      upsert: true,
      contentType: file.type || 'image/svg+xml',
      cacheControl: '3600',
    });
  if (error) throw error;
  const { data } = supabase.storage.from('tier-badges').getPublicUrl(path);
  return data.publicUrl;
}

/**
 * super_admin: set a tier's badge + accent via the admin_set_tier_identity RPC.
 * Passing null/empty for either clears that override (frontend falls back to its
 * shipped default). Both columns are written every call, so always pass both.
 */
export async function setTierIdentity(
  code: string,
  iconUrl: string | null,
  accentColor: string | null,
): Promise<TierIdentityRow> {
  const { data, error } = await supabase.rpc('admin_set_tier_identity', {
    p_code: code,
    p_icon_url: iconUrl,
    p_accent_color: accentColor,
  });
  if (error) throw error;
  const row = Array.isArray(data) ? data[0] : data;
  return row as TierIdentityRow;
}

// ===========================================
// Top-tier resolver (for share-to-community flows)
// ===========================================

export interface TopTierCommunity {
  tier_id: string;
  tier_code: string;
  tier_name: string;
  tier_level: number;
}

/**
 * Resolve the caller's highest-level community tier membership.
 * Effective level respects `user_community_memberships.tier_override` over
 * `community_tiers.tier_level` (mirrors the pattern in fetchCommunityTiersForHub).
 *
 * Add-on parity (WS3): an active, unexpired community-tier add-on grant is a
 * comped seat — the server's `get_user_tier` counts it, so this resolver must
 * too. Both a membership row and an add-on grant feed the "highest tier" pick,
 * so an add-on-only holder resolves to their granted tier (and share-to-community
 * flows can target it). RLS (`addon_grants_select`, mig 307) scopes the add-on
 * read to the caller's own grants + their active orgs' org-scoped grants.
 *
 * Returns null if the user has no active community membership or tier add-on.
 */
export async function getMyTopTierCommunity(): Promise<TopTierCommunity | null> {
  const userId = await getAuthUserId();
  const nowIso = new Date().toISOString();

  const [membershipRes, addonRes] = await Promise.all([
    supabase
      .from('user_community_memberships')
      .select('tier_override, community_tiers!inner(id, code, name, tier_level)')
      .eq('user_id', userId)
      .eq('status', 'active'),
    supabase
      .from('addon_grants')
      .select('community_tiers!inner(id, code, name, tier_level)')
      .eq('status', 'active')
      .not('community_tier_id', 'is', null)
      .or(`expires_at.is.null,expires_at.gt.${nowIso}`),
  ]);

  if (membershipRes.error) throw membershipRes.error;
  if (addonRes.error) throw addonRes.error;

  type Tier = { id: string; code: string; name: string; tier_level: number };
  const embed = (t: Tier | Tier[] | null | undefined): Tier | null =>
    Array.isArray(t) ? t[0] ?? null : t ?? null;

  let best: TopTierCommunity | null = null;
  const consider = (tier: Tier | null, effective: number) => {
    if (!tier) return;
    if (!best || effective > best.tier_level) {
      best = { tier_id: tier.id, tier_code: tier.code, tier_name: tier.name, tier_level: effective };
    }
  };

  for (const row of (membershipRes.data ?? []) as Array<{
    tier_override: number | null;
    community_tiers: Tier | Tier[];
  }>) {
    const tier = embed(row.community_tiers);
    consider(tier, row.tier_override ?? tier?.tier_level ?? 0);
  }
  // Add-on grants carry no tier_override — the effective level is the tier's own.
  for (const row of (addonRes.data ?? []) as Array<{ community_tiers: Tier | Tier[] }>) {
    const tier = embed(row.community_tiers);
    consider(tier, tier?.tier_level ?? 0);
  }
  return best;
}

// ===========================================
// Auto-enroll in Chat Channels
// ===========================================

/**
 * Auto-join all default channels for a community tier.
 * Fire-and-forget — doesn't block UI. Silently skips already-joined channels.
 */
/**
 * Return the id of the tier's default channel (the one marked
 * `default_channel = true`). If no channel is flagged default, fall back to
 * the oldest non-archived channel in the tier so the share still lands in a
 * visible channel. Used by share flows that post from "outside" a channel
 * context (certificate shares, prime-score shares). Fully dynamic — no tier
 * or channel names are hardcoded; works on local/dev/prod.
 */
export async function getDefaultChannelIdForTier(tierId: string): Promise<string | null> {
  // 1. Prefer the flagged default channel.
  const { data: def, error: defErr } = await supabase
    .from('community_channels')
    .select('id')
    .eq('community_tier_id', tierId)
    .eq('default_channel', true)
    .limit(1)
    .maybeSingle();
  if (defErr) {
    console.warn('[community] default channel lookup failed', defErr);
  } else if (def?.id) {
    return def.id;
  }

  // 2. Fallback — any channel in the tier, lowest display_order first.
  const { data: fallback, error: fbErr } = await supabase
    .from('community_channels')
    .select('id')
    .eq('community_tier_id', tierId)
    .order('display_order', { ascending: true })
    .order('created_at', { ascending: true })
    .limit(1)
    .maybeSingle();
  if (fbErr) {
    console.warn('[community] fallback channel lookup failed', fbErr);
    return null;
  }
  return fallback?.id ?? null;
}

/**
 * On first community page entry: join the user to their HIGHEST tier's chat
 * conversation. Idempotent via getOrCreateTierConversation.
 *
 * `tierId` must be the user's highest tier. Chat is keyed on tier, not on
 * community_channels.
 *
 * As of 2026-04-24 no global conversations exist — "In Person Events" /
 * "Online Events" / "Mastermind" moved to K2K feed-only (migration 212).
 * `getOrCreateGlobalConversation` remains exported for when a real global
 * chat is introduced; it is intentionally not called from this auto-join
 * path so stale orphan rows don't re-generate on every community entry.
 */
export async function autoJoinTopTierAndGlobals(tierId: string): Promise<void> {
  await Promise.allSettled([
    getOrCreateTierConversation(tierId),
  ]);
}

// ===========================================
// Post Image Upload
// ===========================================

/**
 * Upload a post image to Supabase Storage.
 * Returns the storage path (not a signed URL — resolve via CDN or signed URL later).
 */
export async function uploadPostImage(file: File): Promise<PostImage> {
  const userId = await getAuthUserId();
  // Sanitised: real user filenames reach this function now that the composer
  // passes Files directly (previously everything arrived as post-image-<ts>.jpg
  // via the blob round-trip, so the extension was always already clean).
  // Uppercase or punctuated extensions otherwise produce keys Storage rejects.
  const ext = (file.name.split('.').pop() || 'jpg').toLowerCase().replace(/[^a-z0-9]/g, '');
  const path = `${userId}/${crypto.randomUUID()}.${ext || 'jpg'}`;

  const { error } = await supabase.storage
    .from('post-images')
    .upload(path, file, {
      contentType: file.type,
      upsert: false,
    });

  if (error) throw error;

  // Get dimensions if possible (browser-side)
  const dimensions = await getImageDimensions(file);

  return {
    url: path,
    width: dimensions?.width,
    height: dimensions?.height,
    alt: file.name,
  };
}

/** Get image dimensions from a File (browser-side) */
function getImageDimensions(file: File): Promise<{ width: number; height: number } | null> {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      resolve({ width: img.naturalWidth, height: img.naturalHeight });
      URL.revokeObjectURL(img.src);
    };
    img.onerror = () => resolve(null);
    img.src = URL.createObjectURL(file);
  });
}

/**
 * Get a signed URL for a post image stored in Supabase Storage.
 */
export async function getPostImageSignedUrl(storagePath: string): Promise<string> {
  const { data, error } = await supabase.storage
    .from('post-images')
    .createSignedUrl(storagePath, 3600); // 1 hour

  if (error) throw error;
  return data.signedUrl;
}

// ===========================================
// Post Attachment Upload
// ===========================================

/** MIME types allowed for post attachments (image + document only, no audio/video) */
const POST_ATTACHMENT_MIME_CATEGORIES: Record<string, string[]> = {
  image: ATTACHMENT_MIME_CATEGORIES.image,
  document: ATTACHMENT_MIME_CATEGORIES.document,
  // User-recorded video on feed posts (migration 427 widened post-attachments).
  video: ATTACHMENT_MIME_CATEGORIES.video,
};

/** Resolve a MIME type to a post-attachment category, or null. */
function getPostMimeCategory(mimeType: string): string | null {
  for (const [cat, mimes] of Object.entries(POST_ATTACHMENT_MIME_CATEGORIES)) {
    if (mimes.includes(mimeType)) return cat;
  }
  return null;
}

/** Validate a post attachment file. Returns error string or null if valid. */
export function validatePostAttachment(file: File): string | null {
  // Same fallback as the chat validator: try the declared type, then the
  // extension-derived one. Browsers report "" or application/octet-stream for
  // .md and .zip often enough that declared-type-only rejects valid files.
  const category =
    getPostMimeCategory(file.type) ?? getPostMimeCategory(resolveEffectiveMime(file));
  if (!category) {
    return `File type "${file.type || 'unknown'}" is not supported. Allowed: images, PDF, Word, Excel, Markdown, and ZIP files.`;
  }
  // Chat and feed share these limits, and their buckets agree: migration 477 put
  // message-attachments at 512 MB, migration 607 put post-attachments there too.
  // If those buckets ever diverge again this has to become surface-aware, or the
  // client promises a size Storage will reject after the upload completes.
  const maxSize = ATTACHMENT_SIZE_LIMITS[category];
  if (file.size > maxSize) {
    const maxMB = Math.round(maxSize / (1024 * 1024));
    return `${category.charAt(0).toUpperCase() + category.slice(1)} files must be under ${maxMB} MB`;
  }
  return null;
}

/**
 * Upload a post attachment to Supabase Storage.
 * Path: {userId}/{uuid}.{ext} in the post-attachments bucket.
 * Returns a PostAttachment object with storagePath (not URL — ERRATA §5.1).
 */
export async function uploadPostAttachment(file: File): Promise<PostAttachment> {
  const userId = await getAuthUserId();
  // Strip anything Storage rejects in a key (spaces, punctuation) — mirrors
  // uploadMessageAttachment. The display name is preserved in `filename` below.
  const ext = (file.name.split('.').pop() || 'bin').toLowerCase().replace(/[^a-z0-9]/g, '');
  const storagePath = `${userId}/${crypto.randomUUID()}.${ext || 'bin'}`;

  // Canonicalised, not file.type — the bucket matches its allowed_mime_types
  // against this header, and file.type is "" or application/x-zip-compressed
  // for a .zip on many platforms. Same fix as uploadMessageAttachment.
  const effectiveMime = resolveEffectiveMime(file);

  const { error } = await supabase.storage
    .from('post-attachments')
    .upload(storagePath, file, {
      contentType: effectiveMime,
      upsert: false,
    });
  if (error) throw error;

  return {
    id: crypto.randomUUID(),
    filename: file.name,
    storagePath,
    // Store what was actually uploaded, so the record matches the object and
    // the read-side icon logic has a real type to switch on.
    mimeType: effectiveMime,
    sizeBytes: file.size,
  };
}

/**
 * Get a signed URL for a post attachment stored in Supabase Storage.
 */
export async function getPostAttachmentUrl(storagePath: string): Promise<string> {
  const { data, error } = await supabase.storage
    .from('post-attachments')
    .createSignedUrl(storagePath, 3600); // 1 hour
  if (error) throw error;
  return data.signedUrl;
}

// ===========================================
// COMMUNITY STORIES (24hr ephemeral content)
// ===========================================

/** Story item — API response shape, typed from CommunityStory (D3) */
export const storyItemSchema = z.object({
  id: z.string().uuid(),
  story_type: storyTypeSchema,
  image_url: z.string().nullable(),
  caption: z.string(),
  background_gradient: z.string().nullable(),
  text_color: z.string().nullable(),
  text_position_x: z.number().default(50),
  text_position_y: z.number().default(50),
  text_width: z.number().default(80),
  created_at: z.string(),
  expires_at: z.string(),
});
export type StoryItem = z.infer<typeof storyItemSchema>;

/** Input for creating a story — typed from NewCommunityStory (D3) */
export const createStoryInputSchema = z.object({
  storyType: storyTypeSchema.default('photo'),
  imageUrl: z.string().optional(),
  caption: z.string().max(2000).optional(),
  backgroundGradient: z.string().optional(),
  textColor: z.string().optional(),
  textPositionX: z.number().min(0).max(100).default(50),
  textPositionY: z.number().min(0).max(100).default(50),
  textWidth: z.number().min(10).max(100).default(80),
});
export type CreateStoryInput = z.infer<typeof createStoryInputSchema>;

/** Story viewer — schema-derived (D3) from selectStoryViewSchema + joined profile */
export const storyViewerSchema = z.object({
  user_id: z.string().uuid(),
  full_name: z.string(),
  avatar_url: z.string().nullable(),
  viewed_at: z.string(),
});
export type StoryViewer = z.infer<typeof storyViewerSchema>;

/** A group of stories by the same author */
export interface StoryGroup {
  author_id: string;
  author: { full_name: string; avatar_url: string | null };
  stories: StoryItem[];
  has_unviewed: boolean;
  is_own: boolean;
}

/** Stories grouped by community tier — composed from CommunityHubCard + StoryGroup (D3) */
export const tierStoryGroupSchema = z.object({
  tier_id: z.string().uuid(),
  tier_code: z.string(),
  tier_name: z.string(),
  tier_level: z.number(),
  story_groups: z.array(z.custom<StoryGroup>()),
});
export type TierStoryGroup = z.infer<typeof tierStoryGroupSchema>;

/**
 * Fetch active stories for a community tier, grouped by author.
 * Sorted: own stories first → unviewed → viewed.
 * RLS enforces tier gating + 24hr expiry.
 */
export async function fetchStories(tierId: string): Promise<StoryGroup[]> {
  const userId = await getAuthUserId();

  // Fetch active stories for this tier (RLS handles expiry + tier gating)
  const { data: stories, error } = await supabase
    .from('community_stories')
    .select(`
      id,
      community_tier_id,
      author_id,
      story_type,
      image_url,
      caption,
      background_gradient,
      text_color,
      text_position_x,
      text_position_y,
      text_width,
      created_at,
      expires_at,
      user_profiles!community_stories_author_id_user_profiles_id_fk (
        full_name,
        avatar_url
      )
    `)
    .eq('community_tier_id', tierId)
    .order('created_at', { ascending: true });

  if (error) throw error;
  if (!stories || stories.length === 0) return [];

  // Fetch which stories the current user has viewed
  const storyIds = stories.map(s => s.id);
  const { data: views } = await supabase
    .from('story_views')
    .select('story_id')
    .eq('user_id', userId)
    .in('story_id', storyIds);

  const viewedSet = new Set((views ?? []).map(v => v.story_id));

  // Group by author
  const groupMap = new Map<string, StoryGroup>();
  for (const story of stories) {
    const authorId = story.author_id;
    const profile = story.user_profiles as any;

    if (!groupMap.has(authorId)) {
      groupMap.set(authorId, {
        author_id: authorId,
        author: {
          full_name: profile?.full_name ?? 'Unknown',
          avatar_url: profile?.avatar_url ?? null,
        },
        stories: [],
        has_unviewed: false,
        is_own: authorId === userId,
      });
    }

    const group = groupMap.get(authorId)!;
    group.stories.push({
      id: story.id,
      story_type: story.story_type as StoryType,
      image_url: story.image_url,
      caption: story.caption ?? '',
      background_gradient: story.background_gradient,
      text_color: (story as any).text_color ?? null,
      text_position_x: (story as any).text_position_x ?? 50,
      text_position_y: (story as any).text_position_y ?? 50,
      text_width: (story as any).text_width ?? 80,
      created_at: story.created_at,
      expires_at: story.expires_at,
    });

    if (!viewedSet.has(story.id)) {
      group.has_unviewed = true;
    }
  }

  // Sort: own first → unviewed → viewed
  const groups = Array.from(groupMap.values());
  groups.sort((a, b) => {
    if (a.is_own !== b.is_own) return a.is_own ? -1 : 1;
    if (a.has_unviewed !== b.has_unviewed) return a.has_unviewed ? -1 : 1;
    // Most recent story first within same category
    const aLatest = a.stories[a.stories.length - 1]?.created_at ?? '';
    const bLatest = b.stories[b.stories.length - 1]?.created_at ?? '';
    return bLatest.localeCompare(aLatest);
  });

  return groups;
}

/**
 * Create a new story in a community tier.
 */
export async function createStory(
  tierId: string,
  input: CreateStoryInput,
): Promise<StoryItem> {
  const userId = await getAuthUserId();
  const expiresAt = new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString();

  const { data, error } = await supabase
    .from('community_stories')
    .insert({
      community_tier_id: tierId,
      author_id: userId,
      story_type: input.storyType ?? 'photo',
      image_url: input.imageUrl ?? null,
      caption: input.caption ?? '',
      background_gradient: input.backgroundGradient ?? null,
      text_color: input.textColor ?? '#ffffff',
      text_position_x: input.textPositionX ?? 50,
      text_position_y: input.textPositionY ?? 50,
      text_width: input.textWidth ?? 80,
      expires_at: expiresAt,
    })
    .select()
    .single();

  if (error) throw error;

  api
    .logActivity({
      type: 'community',
      action: 'created',
      entity_type: 'story',
      entity_id: data.id,
      entity_name: (data.caption || '').slice(0, 80) || `[${data.story_type}]`,
      description: `Created ${data.story_type} story in tier ${tierId}`,
      metadata: {
        community_tier_id: tierId,
        story_type: data.story_type,
        has_image: !!data.image_url,
      },
    })
    .catch(() => {
      // Non-critical.
    });

  return {
    id: data.id,
    story_type: data.story_type as StoryType,
    image_url: data.image_url,
    caption: data.caption ?? '',
    background_gradient: data.background_gradient,
    text_color: (data as any).text_color ?? '#ffffff',
    text_position_x: (data as any).text_position_x ?? 50,
    text_position_y: (data as any).text_position_y ?? 50,
    text_width: (data as any).text_width ?? 80,
    created_at: data.created_at,
    expires_at: data.expires_at,
  };
}

/**
 * Soft-delete a story (author only).
 */
export async function deleteStory(storyId: string): Promise<void> {
  const { error } = await supabase
    .from('community_stories')
    .update({ deleted_at: new Date().toISOString() })
    .eq('id', storyId);

  if (error) throw error;
}

/**
 * Record that the current user has viewed a story.
 * Uses upsert with ignoreDuplicates to avoid errors on re-view.
 */
export async function recordStoryView(storyId: string): Promise<void> {
  const userId = await getAuthUserId();

  const { error } = await supabase
    .from('story_views')
    .upsert(
      { story_id: storyId, user_id: userId },
      { onConflict: 'story_id,user_id', ignoreDuplicates: true },
    );

  if (error) throw error;

  api
    .logActivity({
      type: 'community',
      action: 'viewed',
      entity_type: 'story',
      entity_id: storyId,
      entity_name: 'story',
      description: `Viewed story ${storyId}`,
      metadata: {},
    })
    .catch(() => {
      // Non-critical.
    });
}

// ===========================================
// Story Reactions (mirror post reactions, scoped to community_stories)
// ===========================================

/** One aggregated emoji reaction on a story. `reacted` = the current user
 *  reacted with this emoji. `userIds` drives the cached reactor-name hover. */
export interface StoryReactionSummary {
  emoji: string;
  count: number;
  userIds: string[];
  reacted: boolean;
}

/** storyId -> summaries (sorted by count desc). */
export type StoryReactionsByStory = Record<string, StoryReactionSummary[]>;

/**
 * Fetch + aggregate reactions for a set of stories in one query.
 * RLS on story_reactions inherits story visibility, so this only returns
 * reactions on stories the caller can see.
 */
export async function fetchStoryReactions(
  storyIds: string[],
): Promise<StoryReactionsByStory> {
  if (storyIds.length === 0) return {};
  const userId = await getAuthUserId();

  const { data, error } = await supabase
    .from('story_reactions')
    .select('story_id, user_id, emoji')
    .in('story_id', storyIds);
  if (error) throw error;

  const rows = (data ?? []) as { story_id: string; user_id: string; emoji: string }[];
  const tmp: Record<
    string,
    Record<string, { count: number; userIds: string[]; reacted: boolean }>
  > = {};
  for (const r of rows) {
    const byEmoji = (tmp[r.story_id] ??= {});
    const agg = (byEmoji[r.emoji] ??= { count: 0, userIds: [], reacted: false });
    agg.count += 1;
    agg.userIds.push(r.user_id);
    if (r.user_id === userId) agg.reacted = true;
  }

  const out: StoryReactionsByStory = {};
  for (const [storyId, byEmoji] of Object.entries(tmp)) {
    out[storyId] = Object.entries(byEmoji)
      .map(([emoji, v]) => ({ emoji, ...v }))
      .sort((a, b) => b.count - a.count);
  }
  return out;
}

/**
 * Toggle the current user's emoji reaction on a story (delete-or-insert).
 * Mirrors togglePostReaction.
 */
export async function toggleStoryReaction(
  storyId: string,
  emoji: string,
): Promise<'added' | 'removed'> {
  const userId = await getAuthUserId();

  const { data: existing } = await supabase
    .from('story_reactions')
    .select('id')
    .eq('story_id', storyId)
    .eq('user_id', userId)
    .eq('emoji', emoji)
    .maybeSingle();

  if (existing) {
    const { error } = await supabase
      .from('story_reactions')
      .delete()
      .eq('id', existing.id);
    if (error) throw error;
    return 'removed';
  }

  const { error } = await supabase
    .from('story_reactions')
    .insert({ story_id: storyId, user_id: userId, emoji });
  if (error) throw error;

  api
    .logActivity({
      type: 'community',
      action: 'reacted',
      entity_type: 'story_reaction',
      entity_id: storyId,
      entity_name: emoji,
      description: `Reacted ${emoji} on story`,
      metadata: { emoji, target: 'community_story' },
    })
    .catch(() => {
      // Non-critical.
    });
  return 'added';
}

/**
 * Fetch viewers of a story (for story creator's "seen by" UI).
 */
export async function fetchStoryViewers(storyId: string): Promise<StoryViewer[]> {
  const { data, error } = await supabase
    .from('story_views')
    .select(`
      user_id,
      viewed_at,
      user_profiles!story_views_user_id_user_profiles_id_fk (
        full_name,
        avatar_url
      )
    `)
    .eq('story_id', storyId)
    .order('viewed_at', { ascending: false });

  if (error) throw error;

  return (data ?? []).map((row: any) => ({
    user_id: row.user_id,
    full_name: row.user_profiles?.full_name ?? 'Unknown',
    avatar_url: row.user_profiles?.avatar_url ?? null,
    viewed_at: row.viewed_at,
  }));
}

/**
 * Upload a story image to Supabase Storage (story-media bucket).
 * Returns the storage path (not a signed URL).
 */
export async function uploadStoryImage(file: File): Promise<string> {
  const userId = await getAuthUserId();
  const ext = file.name.split('.').pop() || 'jpg';
  const path = `${userId}/${crypto.randomUUID()}.${ext}`;

  const { error } = await supabase.storage
    .from('story-media')
    .upload(path, file, {
      contentType: file.type,
      upsert: false,
    });

  if (error) throw error;
  return path;
}

/**
 * Get a signed URL for a story image stored in Supabase Storage.
 */
export async function getStoryImageSignedUrl(storagePath: string): Promise<string> {
  const { data, error } = await supabase.storage
    .from('story-media')
    .createSignedUrl(storagePath, 3600); // 1 hour

  if (error) throw error;
  return data.signedUrl;
}

// ===========================================
// Community Engagement / Gamification
// ===========================================

/** Leaderboard entry — from get_community_leaderboard RPC */
export const communityLeaderboardEntrySchema = z.object({
  user_id: z.string().uuid(),
  full_name: z.string().nullable(),
  avatar_url: z.string().nullable(),
  organization_name: z.string().nullable(),
  total_points: z.number(),
  message_count: z.number(),
  post_count: z.number(),
  reaction_count: z.number(),
  badge_count: z.number(),
  current_streak: z.number(),
});
export type CommunityLeaderboardEntry = z.infer<typeof communityLeaderboardEntrySchema>;

/** My community stats — from get_my_community_stats RPC */
export const myCommunityStatsSchema = z.object({
  total_points: z.number(),
  message_count: z.number(),
  post_count: z.number(),
  reaction_count: z.number(),
  comment_count: z.number(),
  badge_count: z.number(),
  current_streak: z.number(),
  longest_streak: z.number(),
  total_active_days: z.number(),
  active_dates: z.array(z.string()),
});
export type MyCommunityStats = z.infer<typeof myCommunityStatsSchema>;

/** Community badge definition */
export const communityBadgeDefSchema = z.object({
  id: z.string().uuid(),
  code: z.string(),
  name: z.string(),
  description: z.string(),
  icon: z.string().nullable(),
  color: z.string().nullable(),
  rarity: z.string(),
  points: z.number().nullable(),
  criteria: z.any(),
  is_active: z.boolean().nullable(),
  display_order: z.number().nullable(),
});
export type CommunityBadgeDef = z.infer<typeof communityBadgeDefSchema>;

/** Community perk definition */
export const communityPerkDefSchema = z.object({
  id: z.string().uuid(),
  code: z.string(),
  name: z.string(),
  description: z.string(),
  icon: z.string().nullable(),
  cost: z.number(),
  is_active: z.boolean().nullable(),
});
export type CommunityPerkDef = z.infer<typeof communityPerkDefSchema>;

export async function fetchCommunityLeaderboard(
  timeframe: 'all_time' | 'monthly' | 'weekly' = 'monthly',
): Promise<CommunityLeaderboardEntry[]> {
  const { data, error } = await supabase.rpc('get_community_leaderboard', {
    p_timeframe: timeframe,
    p_limit: 20,
  });
  if (error) throw error;
  return ((data || []) as any[]).map((row) => ({
    user_id: row.user_id,
    full_name: row.full_name,
    avatar_url: row.avatar_url,
    organization_name: row.organization_name,
    total_points: Number(row.total_points),
    message_count: Number(row.message_count),
    post_count: Number(row.post_count),
    reaction_count: Number(row.reaction_count),
    badge_count: Number(row.badge_count),
    current_streak: Number(row.current_streak),
  }));
}

export async function fetchMyCommunityStats(): Promise<MyCommunityStats> {
  const { data, error } = await supabase.rpc('get_my_community_stats');
  if (error) throw error;
  const row = (data as any[])?.[0];
  if (!row) {
    return {
      total_points: 0, message_count: 0, post_count: 0,
      reaction_count: 0, comment_count: 0, badge_count: 0,
      current_streak: 0, longest_streak: 0, total_active_days: 0,
      active_dates: [],
    };
  }
  return {
    total_points: Number(row.total_points),
    message_count: Number(row.message_count),
    post_count: Number(row.post_count),
    reaction_count: Number(row.reaction_count),
    comment_count: Number(row.comment_count),
    badge_count: Number(row.badge_count),
    current_streak: Number(row.current_streak),
    longest_streak: Number(row.longest_streak),
    total_active_days: Number(row.total_active_days),
    active_dates: row.active_dates || [],
  };
}

/**
 * Has the signed-in member ever posted, commented or sent a chat message?
 *
 * Reads the content tables, not the points ledger: the ledger only records 'message' and
 * 'reaction' rows, so a post or comment never showed up there. One row from any of the three
 * reads is enough. A read that fails counts as "nothing found", so the item can stay unticked
 * but is never ticked by mistake; it throws only when all three fail. RLS limits each read to
 * rows the member can still see, so a message in a chat they have left does not count.
 */
export async function fetchHasCommunityActivity(): Promise<boolean> {
  const userId = (await getSessionUser())?.id;
  if (!userId) return false;
  const reads = await Promise.all([
    supabase.from('community_posts').select('id').eq('author_id', userId).is('deleted_at', null).limit(1),
    supabase.from('post_comments').select('id').eq('author_id', userId).is('deleted_at', null).limit(1),
    supabase.from('messages').select('id').eq('sender_id', userId).is('deleted_at', null).limit(1),
  ]);
  if (reads.some((r) => !r.error && (r.data?.length ?? 0) > 0)) return true;
  if (reads.every((r) => r.error)) throw reads[0].error;
  return false;
}

export async function fetchCommunityBadges(): Promise<{
  definitions: CommunityBadgeDef[];
  earned: string[];
}> {
  const [{ data: defs }, { data: earned }] = await Promise.all([
    supabase.from('community_badges').select('*').eq('is_active', true).order('display_order'),
    supabase.from('community_user_badges').select('badge_id'),
  ]);
  return {
    definitions: (defs || []) as CommunityBadgeDef[],
    earned: ((earned || []) as any[]).map((r) => r.badge_id),
  };
}

export async function fetchCommunityPerks(): Promise<{
  definitions: CommunityPerkDef[];
  owned: string[];
}> {
  const [{ data: defs }, { data: owned }] = await Promise.all([
    supabase.from('community_perks').select('*').eq('is_active', true).order('display_order'),
    supabase.from('community_user_perks').select('perk_id'),
  ]);
  return {
    definitions: (defs || []) as CommunityPerkDef[],
    owned: ((owned || []) as any[]).map((r) => r.perk_id),
  };
}

const purchasePerkResultSchema = z.object({
  ok: z.boolean(),
  reason: z.enum(['inactive', 'owned', 'balance']).optional(),
  balance: z.number().optional(),
});

/**
 * Buy a perk. The server (purchase_community_perk, migration 693) checks the balance under a lock
 * and writes the spend and the perk together; the browser writes no points.
 * `cost` is kept for the existing callers and is not sent: the server reads the perk's price.
 */
export async function purchaseCommunityPerk(perkId: string, _cost: number): Promise<boolean> {
  const { data, error } = await supabase.rpc('purchase_community_perk', { p_perk_id: perkId });
  if (error) throw error;
  return purchasePerkResultSchema.parse(data).ok;
}

// ===========================================
// Channel Settings (migration 204 — community_channel_settings)
// ===========================================

export const channelPostAudienceSchema = z.enum([
  'everyone',
  'admins_mods',
  'admins',
]);
export type ChannelPostAudience = z.infer<typeof channelPostAudienceSchema>;

export const channelPermissionsSchema = z.object({
  who_can_post: channelPostAudienceSchema.default('everyone'),
  who_can_comment: channelPostAudienceSchema.default('everyone'),
  approve_new_members: z.boolean().default(false),
  allow_media_uploads: z.boolean().default(true),
  allow_links: z.boolean().default(true),
  allow_polls: z.boolean().default(true),
});
export type ChannelPermissions = z.infer<typeof channelPermissionsSchema>;

export const channelModerationSchema = z.object({
  auto_mod_enabled: z.boolean().default(false),
  spam_detection: z.boolean().default(false),
  link_preview: z.boolean().default(true),
  mute_new_members: z.boolean().default(false),
  flag_threshold: z.number().int().min(1).max(100).default(3),
});
export type ChannelModeration = z.infer<typeof channelModerationSchema>;

export const channelSettingsSchema = z.object({
  channel_id: z.string().uuid(),
  permissions: channelPermissionsSchema,
  moderation: channelModerationSchema,
  updated_at: z.string(),
  updated_by: z.string().uuid().nullable(),
});
export type ChannelSettings = z.infer<typeof channelSettingsSchema>;

/**
 * Fetch the settings row for a channel. Returns defaults when the row doesn't
 * exist yet (new channel) — callers don't need to distinguish.
 */
export async function fetchChannelSettings(
  channelId: string,
): Promise<ChannelSettings> {
  const { data, error } = await supabase
    .from('community_channel_settings')
    .select('channel_id, permissions, moderation, updated_at, updated_by')
    .eq('channel_id', channelId)
    .maybeSingle();

  if (error) throw error;

  if (!data) {
    return {
      channel_id: channelId,
      permissions: channelPermissionsSchema.parse({}),
      moderation: channelModerationSchema.parse({}),
      updated_at: new Date().toISOString(),
      updated_by: null,
    };
  }
  return channelSettingsSchema.parse(data);
}

export interface UpdateChannelSettingsPatch {
  permissions?: Partial<ChannelPermissions>;
  moderation?: Partial<ChannelModeration>;
}

/**
 * Upsert the settings row for a channel. Shallow-merges permissions and
 * moderation JSONB objects — caller sends only the keys that change.
 * Permission-gated by RLS (super_admin only — see migration 204).
 */
export async function updateChannelSettings(
  channelId: string,
  patch: UpdateChannelSettingsPatch,
): Promise<ChannelSettings> {
  const current = await fetchChannelSettings(channelId);

  const merged = {
    permissions: { ...current.permissions, ...(patch.permissions ?? {}) },
    moderation: { ...current.moderation, ...(patch.moderation ?? {}) },
  };

  const userId = (await getSessionUser())?.id ?? null;

  const { data, error } = await supabase
    .from('community_channel_settings')
    .upsert(
      {
        channel_id: channelId,
        permissions: merged.permissions,
        moderation: merged.moderation,
        updated_by: userId,
        updated_at: new Date().toISOString(),
      },
      { onConflict: 'channel_id' },
    )
    .select('channel_id, permissions, moderation, updated_at, updated_by')
    .single();

  if (error) throw error;
  return channelSettingsSchema.parse(data);
}

// ===========================================
// Channel Members (migration 205 — member management RPCs)
// ===========================================

export const channelMemberRoleSchema = z.enum(['owner', 'admin', 'member']);
export type ChannelMemberRole = z.infer<typeof channelMemberRoleSchema>;

export const channelMemberSchema = z.object({
  user_id: z.string().uuid(),
  full_name: z.string(),
  avatar_url: z.string().nullable(),
  role: channelMemberRoleSchema,
  is_super_admin: z.boolean(),
  joined_at: z.string().nullable(),
});
export type ChannelMember = z.infer<typeof channelMemberSchema>;

/**
 * Lightweight member-name search for pickers that exist BEFORE their target
 * does (create flows) — unlike searchAddableCommunityUsers there is no
 * channel/community to exclude members of; callers filter already-selected
 * ids client-side. Returns ChannelMember stubs.
 */
export async function searchCommunityUsers(
  query: string,
): Promise<ChannelMember[]> {
  const q = query.trim();
  if (!q) return [];
  const { data, error } = await supabase
    .from('user_profiles')
    .select('id, full_name, avatar_url')
    .ilike('full_name', `%${q}%`)
    .limit(20);
  if (error) throw error;
  return (data ?? []).map((row: any) => ({
    user_id: row.id,
    full_name: row.full_name ?? 'Unknown',
    avatar_url: row.avatar_url ?? null,
    role: 'member' as ChannelMemberRole,
    is_super_admin: false,
    joined_at: null,
  }));
}

/**
 * List active members of a channel (conversation_participants.left_at IS NULL).
 * Joins user_profiles for display name + avatar. Ordered by joined_at asc.
 *
 * Implementation note: `conversation_participants.user_id` has no FK to
 * `user_profiles.id`, so PostgREST can't embed the join directly. We fetch
 * participants and profiles in two queries and merge client-side.
 */
export async function fetchChannelMembers(
  channelId: string,
): Promise<ChannelMember[]> {
  // Chat membership is now scoped per tier (conversations.community_tier_id),
  // not per community_channels row. community_channels is feed-only. Resolve
  // the channel's tier, then list members of that tier's chat conversation.
  const { data: channel, error: channelErr } = await supabase
    .from('community_channels')
    .select('community_tier_id')
    .eq('id', channelId)
    .maybeSingle();

  if (channelErr) throw channelErr;
  if (!channel?.community_tier_id) return [];

  const { data: conv, error: convErr } = await supabase
    .from('conversations')
    .select('id')
    .eq('type', 'community_channel')
    .eq('community_tier_id', channel.community_tier_id)
    .maybeSingle();

  if (convErr) throw convErr;
  if (!conv?.id) return [];

  const { data: participants, error: partErr } = await supabase
    .from('conversation_participants')
    .select('user_id, role, joined_at')
    .eq('conversation_id', conv.id)
    .is('left_at', null)
    .order('joined_at', { ascending: true });

  if (partErr) throw partErr;
  if (!participants || participants.length === 0) return [];

  const userIds = participants.map((p: any) => p.user_id);
  const [profilesRes, globalRolesRes] = await Promise.all([
    supabase
      .from('user_profiles')
      .select('id, full_name, avatar_url')
      .in('id', userIds),
    supabase
      .from('global_user_roles')
      .select('user_id')
      .in('user_id', userIds)
      .eq('role', 'super_admin'),
  ]);

  if (profilesRes.error) throw profilesRes.error;
  if (globalRolesRes.error) throw globalRolesRes.error;

  const profileById = new Map<string, { full_name: string | null; avatar_url: string | null }>();
  for (const p of profilesRes.data ?? []) {
    profileById.set((p as any).id, {
      full_name: (p as any).full_name ?? null,
      avatar_url: (p as any).avatar_url ?? null,
    });
  }

  const superAdminIds = new Set<string>(
    (globalRolesRes.data ?? []).map((r: any) => r.user_id as string),
  );

  return participants.map((p: any) => {
    const prof = profileById.get(p.user_id);
    return {
      user_id: p.user_id,
      role: p.role,
      is_super_admin: superAdminIds.has(p.user_id),
      joined_at: p.joined_at,
      full_name: prof?.full_name ?? 'Unknown',
      avatar_url: prof?.avatar_url ?? null,
    };
  });
}

/**
 * Search community users who are:
 *   - not currently active participants of this channel
 *   - have an active user_community_memberships row
 *   - (RPC-side) meet the channel's tier requirement
 * Returns up to 20 results matching `query` against user_profiles.full_name.
 */
export async function searchAddableCommunityUsers(
  channelId: string,
  query: string,
): Promise<ChannelMember[]> {
  // Current channel members — exclude from candidates.
  const existing = await fetchChannelMembers(channelId);
  const existingIds = new Set(existing.map((m) => m.user_id));

  const { data, error } = await supabase
    .from('user_profiles')
    .select('id, full_name, avatar_url')
    .ilike('full_name', `%${query}%`)
    .limit(30);

  if (error) throw error;

  return (data ?? [])
    .filter((row: any) => !existingIds.has(row.id))
    .slice(0, 20)
    .map((row: any) => ({
      user_id: row.id,
      full_name: row.full_name ?? 'Unknown',
      avatar_url: row.avatar_url ?? null,
      role: 'member' as ChannelMemberRole,
      is_super_admin: false,
      joined_at: null,
    }));
}

/**
 * Add existing community users to a channel. Server-side RPC enforces
 * super_admin + tier eligibility. Returns count of members actually added
 * (excludes duplicates).
 */
export async function addChannelMembers(
  channelId: string,
  userIds: string[],
): Promise<number> {
  const { data, error } = await supabase.rpc('add_channel_members', {
    p_channel_id: channelId,
    p_user_ids: userIds,
  });
  if (error) throw error;
  return typeof data === 'number' ? data : 0;
}

/**
 * Remove a member from a channel (soft-leave). Optionally soft-deletes their
 * posts in this channel. Returns count of posts archived.
 */
export async function removeChannelMember(
  channelId: string,
  userId: string,
  archivePosts: boolean,
): Promise<number> {
  const { data, error } = await supabase.rpc('remove_channel_member', {
    p_channel_id: channelId,
    p_user_id: userId,
    p_archive_posts: archivePosts,
  });
  if (error) throw error;
  return typeof data === 'number' ? data : 0;
}

/**
 * Change a channel member's role.
 */
export async function updateChannelMemberRole(
  channelId: string,
  userId: string,
  role: ChannelMemberRole,
): Promise<void> {
  const { error } = await supabase.rpc('update_channel_member_role', {
    p_channel_id: channelId,
    p_user_id: userId,
    p_role: role,
  });
  if (error) throw error;
}

// ===========================================
// Channel Management (CRUD + archive)
// ===========================================

/**
 * Full-shape channel row as returned from `community_channels`. Keyed to the
 * server column names (snake_case) and includes the archive fields added in
 * migration 213. Super-admin-only UI surfaces archived rows.
 */
export const communityChannelRowSchema = z.object({
  id: z.string().uuid(),
  community_tier_id: z.string().uuid().nullable(),
  name: z.string(),
  description: z.string().nullable(),
  channel_type: z.string(),
  icon: z.string().nullable(),
  display_order: z.number().int().nullable(),
  required_tier_id: z.string().uuid().nullable(),
  default_channel: z.boolean(),
  archived_at: z.string().nullable(),
  archived_by: z.string().uuid().nullable(),
  created_at: z.string().nullable(),
  updated_at: z.string().nullable(),
});

export type CommunityChannelRow = z.infer<typeof communityChannelRowSchema>;

/**
 * Zod input schema for creating a new channel. `makeGlobal` is a FE-facing
 * convenience — when true, community_tier_id is sent as null to the RPC.
 */
export const channelCreateInputSchema = z.object({
  tierId: z.string().uuid().nullable(),
  name: z.string().min(1).max(100),
  description: z.string().max(500).optional().nullable(),
  channelType: z.enum(['text', 'announcements', 'events']).default('text'),
  icon: z.string().max(32).optional().nullable(),
  displayOrder: z.number().int().optional().nullable(),
  requiredTierId: z.string().uuid().optional().nullable(),
  defaultChannel: z.boolean().optional(),
  /** Custom-community scope (mig 383): mutually exclusive with tierId. */
  communityId: z.string().uuid().optional().nullable(),
});

export type ChannelCreateInput = z.infer<typeof channelCreateInputSchema>;

/**
 * Zod input schema for editing an existing channel. channelType + tierId
 * are deliberately absent — they're immutable after creation (migration 213).
 */
export const channelUpdateInputSchema = z.object({
  channelId: z.string().uuid(),
  name: z.string().min(1).max(100),
  description: z.string().max(500).optional().nullable(),
  icon: z.string().max(32).optional().nullable(),
  displayOrder: z.number().int().optional().nullable(),
  requiredTierId: z.string().uuid().optional().nullable(),
  defaultChannel: z.boolean().optional(),
});

export type ChannelUpdateInput = z.infer<typeof channelUpdateInputSchema>;

/**
 * Fetch all channels scoped to a tier (or the global bucket when tierId is
 * null). Excludes archived rows by default; pass { includeArchived: true }
 * to include them (super-admin use case for the Archived tab).
 */
export async function fetchTierChannels(
  tierId: string | null,
  opts: { includeArchived?: boolean } = {},
): Promise<CommunityChannelRow[]> {
  let query = supabase.from('community_channels').select('*').order('display_order', { ascending: true });
  if (tierId === null) {
    query = query.is('community_tier_id', null);
  } else {
    query = query.eq('community_tier_id', tierId);
  }
  if (!opts.includeArchived) {
    query = query.is('archived_at', null);
  }
  const { data, error } = await query;
  if (error) throw error;
  return z.array(communityChannelRowSchema).parse(data ?? []);
}

/**
 * Fetch archived channels scoped to a tier. Super-admin only (RLS bypass);
 * non-admins receive zero rows by policy regardless.
 */
export async function fetchArchivedTierChannels(
  tierId: string | null,
): Promise<CommunityChannelRow[]> {
  let query = supabase
    .from('community_channels')
    .select('*')
    .not('archived_at', 'is', null)
    .order('archived_at', { ascending: false });
  if (tierId === null) {
    query = query.is('community_tier_id', null);
  } else {
    query = query.eq('community_tier_id', tierId);
  }
  const { data, error } = await query;
  if (error) throw error;
  return z.array(communityChannelRowSchema).parse(data ?? []);
}

/**
 * Create a new channel via the create_community_channel RPC. Returns the
 * new channel id. Super-admin only (enforced server-side).
 */
export async function createChannel(input: ChannelCreateInput): Promise<string> {
  const parsed = channelCreateInputSchema.parse(input);
  const { data, error } = await supabase.rpc('create_community_channel', {
    p_tier_id: parsed.tierId,
    p_name: parsed.name,
    p_description: parsed.description ?? null,
    p_channel_type: parsed.channelType,
    p_icon: parsed.icon ?? null,
    p_display_order: parsed.displayOrder ?? 0,
    p_required_tier_id: parsed.requiredTierId ?? null,
    p_default_channel: parsed.defaultChannel ?? false,
    p_community_id: parsed.communityId ?? null,
  });
  if (error) throw error;
  return z.string().uuid().parse(data);
}

/**
 * Update a channel's editable fields. channel_type and community_tier_id
 * are immutable post-creation by design.
 */
export async function updateChannel(input: ChannelUpdateInput): Promise<void> {
  const parsed = channelUpdateInputSchema.parse(input);
  const { error } = await supabase.rpc('update_community_channel', {
    p_channel_id: parsed.channelId,
    p_name: parsed.name,
    p_description: parsed.description ?? null,
    p_icon: parsed.icon ?? null,
    p_display_order: parsed.displayOrder ?? null,
    p_required_tier_id: parsed.requiredTierId ?? null,
    p_default_channel: parsed.defaultChannel ?? null,
  });
  if (error) throw error;
}

/**
 * Archive a channel — hides it and its posts/comments from non-super-admin
 * members. Idempotent (re-archiving is a no-op).
 */
export async function archiveChannel(channelId: string): Promise<void> {
  const { error } = await supabase.rpc('archive_community_channel', {
    p_channel_id: channelId,
  });
  if (error) throw error;
}

/**
 * Unarchive a channel — restores visibility. Idempotent.
 */
export async function unarchiveChannel(channelId: string): Promise<void> {
  const { error } = await supabase.rpc('unarchive_community_channel', {
    p_channel_id: channelId,
  });
  if (error) throw error;
}

// ===========================================
// Quick Links Management (CRUD + archive)
// ===========================================

/**
 * Quick links surfaced in the sidebar between Channels and Global. A link is
 * scoped to EITHER a community tier OR a custom community (mig 389) — exactly
 * one of community_tier_id / community_id is set. CRUD flows through SECURITY
 * DEFINER RPCs: tier links are super-admin only (mig 216), community links are
 * managed by the community's owners/admins (mig 389). See specs/quick-links.md.
 */
export const communityQuickLinkRowSchema = z.object({
  id: z.string().uuid(),
  community_tier_id: z.string().uuid().nullable(),
  community_id: z.string().uuid().nullable(),
  name: z.string(),
  url: z.string(),
  icon: z.string().nullable(),
  display_order: z.number().int(),
  archived_at: z.string().nullable(),
  archived_by: z.string().uuid().nullable(),
  created_by: z.string().uuid().nullable(),
  created_at: z.string().nullable(),
  updated_at: z.string().nullable(),
});

export type CommunityQuickLinkRow = z.infer<typeof communityQuickLinkRowSchema>;

/**
 * Which bucket a set of quick links belongs to. Exactly one id is set; the
 * other is null/absent. Mirrors the tier-OR-community scope model used by
 * channels (mig 383).
 */
export type QuickLinkScope = {
  tierId?: string | null;
  communityId?: string | null;
};

// http(s) only — rejects javascript:, data:, ftp:, relative paths.
const quickLinkUrlSchema = z
  .string()
  .url()
  .refine((v) => /^https?:\/\//i.test(v), {
    message: 'URL must start with http:// or https://',
  });

export const quickLinkCreateInputSchema = z
  .object({
    tierId: z.string().uuid().nullable(),
    /** Custom-community scope (mig 389): mutually exclusive with tierId. */
    communityId: z.string().uuid().optional().nullable(),
    name: z.string().trim().min(1).max(80),
    url: quickLinkUrlSchema,
    icon: z.string().max(80).optional().nullable(),
    displayOrder: z.number().int().min(0).optional().nullable(),
  })
  .refine((v) => (v.tierId == null) !== (v.communityId == null), {
    message: 'A quick link belongs to a tier OR a community, not both',
    path: ['tierId'],
  });

export type QuickLinkCreateInput = z.infer<typeof quickLinkCreateInputSchema>;

export const quickLinkUpdateInputSchema = z.object({
  id: z.string().uuid(),
  name: z.string().trim().min(1).max(80),
  url: quickLinkUrlSchema,
  icon: z.string().max(80).optional().nullable(),
  displayOrder: z.number().int().min(0).optional().nullable(),
});

export type QuickLinkUpdateInput = z.infer<typeof quickLinkUpdateInputSchema>;

/**
 * Fetch active quick links for a scope (a tier or a custom community).
 * Excludes archived rows by default; pass { includeArchived: true } to include
 * them (the Archived management tab).
 */
export async function fetchScopedQuickLinks(
  scope: QuickLinkScope,
  opts: { includeArchived?: boolean } = {},
): Promise<CommunityQuickLinkRow[]> {
  let query = supabase
    .from('community_quick_links')
    .select('*')
    .order('display_order', { ascending: true });
  query = scope.communityId
    ? query.eq('community_id', scope.communityId)
    : query.eq('community_tier_id', scope.tierId as string);
  if (!opts.includeArchived) {
    query = query.is('archived_at', null);
  }
  const { data, error } = await query;
  if (error) throw error;
  return z.array(communityQuickLinkRowSchema).parse(data ?? []);
}

/**
 * Fetch archived quick links for a scope. RLS returns rows only to callers who
 * may manage that scope (super admins for tiers; owners/admins for communities);
 * everyone else receives zero rows.
 */
export async function fetchArchivedScopedQuickLinks(
  scope: QuickLinkScope,
): Promise<CommunityQuickLinkRow[]> {
  let query = supabase
    .from('community_quick_links')
    .select('*')
    .not('archived_at', 'is', null)
    .order('archived_at', { ascending: false });
  query = scope.communityId
    ? query.eq('community_id', scope.communityId)
    : query.eq('community_tier_id', scope.tierId as string);
  const { data, error } = await query;
  if (error) throw error;
  return z.array(communityQuickLinkRowSchema).parse(data ?? []);
}

/**
 * Bulk fetch of every active quick link visible to the caller, used by the
 * sidebar. RLS handles tier- and community-gated visibility.
 */
export async function fetchQuickLinksWithTiers(): Promise<
  CommunityQuickLinkRow[]
> {
  const { data, error } = await supabase
    .from('community_quick_links')
    .select('*')
    .is('archived_at', null)
    .order('display_order', { ascending: true });
  if (error) throw error;
  return z.array(communityQuickLinkRowSchema).parse(data ?? []);
}

/**
 * Create a new quick link via the create_community_quick_link RPC. Returns
 * the new id. Server-side authz: super-admin for tier scope, community
 * owner/admin for community scope.
 */
export async function createQuickLink(
  input: QuickLinkCreateInput,
): Promise<string> {
  const parsed = quickLinkCreateInputSchema.parse(input);
  const { data, error } = await supabase.rpc('create_community_quick_link', {
    p_tier_id: parsed.tierId ?? null,
    p_community_id: parsed.communityId ?? null,
    p_name: parsed.name,
    p_url: parsed.url,
    p_icon: parsed.icon ?? null,
    p_display_order: parsed.displayOrder ?? 0,
  });
  if (error) throw error;
  return z.string().uuid().parse(data);
}

/**
 * Update a quick link's editable fields. Scope (tier/community) is immutable
 * post-creation by design (re-create instead).
 */
export async function updateQuickLink(
  input: QuickLinkUpdateInput,
): Promise<void> {
  const parsed = quickLinkUpdateInputSchema.parse(input);
  const { error } = await supabase.rpc('update_community_quick_link', {
    p_id: parsed.id,
    p_name: parsed.name,
    p_url: parsed.url,
    p_icon: parsed.icon ?? null,
    p_display_order: parsed.displayOrder ?? null,
  });
  if (error) throw error;
}

/**
 * Archive a quick link — hides it from non-super-admin members. Idempotent.
 */
export async function archiveQuickLink(id: string): Promise<void> {
  const { error } = await supabase.rpc('archive_community_quick_link', {
    p_id: id,
  });
  if (error) throw error;
}

/**
 * Unarchive a quick link — restores visibility. Idempotent.
 */
export async function unarchiveQuickLink(id: string): Promise<void> {
  const { error } = await supabase.rpc('unarchive_community_quick_link', {
    p_id: id,
  });
  if (error) throw error;
}

// ===========================================
// Mentions — autocomplete suggestion search
// ===========================================

export interface MentionUserSuggestion {
  id: string;
  full_name: string;
  avatar_url: string | null;
}

export interface MentionChannelSuggestion {
  id: string;
  name: string;
  icon: string | null;
}

export interface MentionablesResult {
  channels: MentionChannelSuggestion[];
  internal: MentionUserSuggestion[];
  users: MentionUserSuggestion[];
}

/**
 * Searches the three mention groups in parallel.
 * - channels: tier-based chat conversations the current user is in
 *             (the @mention picker drops the post into a chat — formerly
 *             this listed feed `community_channels`, which was a misread of
 *             "channel" — the kind name is kept for back-compat but the id
 *             now references `conversations.id`).
 * - internal: active organisation members of the current user's org
 * - users: the community roster via the search_mentionable_members RPC
 *          (hermes migration 454) — live seats PLUS live add-on grants,
 *          which is the same entitlement authority content access uses.
 *          The old raw user_community_memberships read missed org-scoped
 *          K&C seats (hidden from non-admins by the peers RLS policy) and
 *          Alfie-plan members whose Basecamp lives in an org addon_grant
 *          with no membership row until their first community visit. The
 *          RPC also runs the name match server-side: every word of the
 *          query must appear in full_name, any order, so multi-word
 *          searches ("james edd", "eddleston j") work. The system bot and
 *          the caller are excluded server-side; real super_admin users
 *          (e.g. Tim) ARE mentionable.
 *
 * Empty query returns top results per group (alphabetical, prefix matches
 * first for users). Users are capped by MENTION_USERS_LIMIT — comfortably
 * above what the dropdown shows, small enough to keep the payload sane.
 */
const MENTION_USERS_LIMIT = 24;
export async function searchMentionables(opts: {
  query: string;
  tierId?: string | null;
  /** Cap per group. `users` ignores this and returns the full roster. */
  limit?: number;
}): Promise<MentionablesResult> {
  const limit = opts.limit ?? 8;
  const trimmed = opts.query.trim();
  const ilike = trimmed === '' ? null : `%${trimmed}%`;
  const userId = await getAuthUserId();

  // 1. Chats — community_channel conversations the user is a participant of.
  //    Match by title. RLS on conversations already gates visibility.
  const chatsQuery = supabase
    .from('conversations')
    .select('id, title, conversation_participants!inner(user_id)')
    .eq('type', 'community_channel')
    .eq('archived', false)
    .eq('conversation_participants.user_id', userId)
    .order('title', { ascending: true })
    .limit(limit);
  if (ilike) chatsQuery.ilike('title', ilike);

  // 2. Internal — the caller's organisation colleagues. Resolve the org first.
  const orgPromise = supabase
    .from('user_profiles')
    .select('current_organization_id')
    .eq('id', userId)
    .single();

  // 3. Users — the community roster RPC (see the function doc above). One
  //    round-trip: candidate set, tier scoping (ladder + add-on layer via
  //    user_has_tier_access), every-word name match, bot/self exclusion and
  //    prefix-first ordering all happen in the database.
  const usersPromise = supabase.rpc('search_mentionable_members', {
    p_query: trimmed === '' ? null : trimmed,
    p_tier_id: opts.tierId ?? null,
    p_limit: MENTION_USERS_LIMIT,
  });

  const [chatsRes, orgRes, usersRes] = await Promise.all([
    chatsQuery,
    orgPromise,
    usersPromise,
  ]);

  // Channels-section payload: id is now conversations.id, name is the chat title.
  const channels: MentionChannelSuggestion[] = (chatsRes.data || []).map(
    (c: any) => ({ id: c.id, name: c.title ?? '', icon: null }),
  );

  if (usersRes.error) {
    // Surface instead of silently returning an empty roster: React Query
    // retries with backoff, and a thrown error is visible in dev tooling.
    throw usersRes.error;
  }
  const memberProfiles: MentionUserSuggestion[] = ((usersRes.data || []) as any[])
    .map((r) => ({ id: r.user_id, full_name: r.full_name, avatar_url: r.avatar_url ?? null }))
    .filter((p): p is MentionUserSuggestion => Boolean(p.id && p.full_name));

  let internal: MentionUserSuggestion[] = [];
  const orgId = (orgRes.data as any)?.current_organization_id ?? null;
  if (orgId) {
    // Two-step shape: organization_members.user_id has no FK to user_profiles.
    const internalMembersRes = await supabase
      .from('organization_members')
      .select('user_id')
      .eq('organization_id', orgId)
      .eq('status', 'active')
      .neq('user_id', userId)
      .limit(limit * 4);
    const internalIds = Array.from(
      new Set(((internalMembersRes.data || []) as any[]).map((r) => r.user_id).filter(Boolean)),
    );
    if (internalIds.length > 0) {
      const internalProfilesQuery = supabase
        .from('user_profiles')
        .select('id, full_name, avatar_url')
        .in('id', internalIds)
        .limit(limit * 4);
      if (ilike) internalProfilesQuery.ilike('full_name', ilike);
      const internalProfilesRes = await internalProfilesQuery;
      internal = ((internalProfilesRes.data || []) as any[])
        .filter((p): p is MentionUserSuggestion => Boolean(p?.id && p?.full_name))
        .slice(0, limit);
    }
  }

  const users: MentionUserSuggestion[] = memberProfiles;

  // Dedup users that already appear in internal
  const internalIds = new Set(internal.map((u) => u.id));
  const filteredUsers = users.filter((u) => !internalIds.has(u.id));

  return { channels, internal, users: filteredUsers };
}

// ===========================================
// Share post into a chat conversation
// ===========================================

/**
 * Inserts a message with `referenced_post_id` set, optionally with body
 * commentary and mentions. The `trg_message_post_shared_outbox` trigger
 * notifies the post author (unless they are already in the conversation,
 * which the edge function dedups against).
 *
 * `metadata` populates the `shared_post_*` keys that <SharedPostPreview>
 * reads to render the post card inside the chat bubble. When omitted, the
 * bubble has no preview to draw and renders empty (only timestamp visible).
 */
export async function sharePostToConversation(opts: {
  postId: string;
  conversationId: string;
  body?: string;
  metadata?: Record<string, unknown>;
}): Promise<void> {
  const userId = await getAuthUserId();
  // Realtime delivery (message.created on the conversation topic +
  // conversation.updated on each participant's private user topic) is emitted
  // server-side by trg_broadcast_message_events on this INSERT — hermes
  // migration 468. The returning-select is now only a "the row really landed"
  // guard, so it no longer reads the columns the old broadcast payload needed.
  const { error } = await supabase
    .from('messages')
    .insert({
      conversation_id: opts.conversationId,
      sender_id: userId,
      message_type: 'text',
      content: opts.body ?? '',
      referenced_post_id: opts.postId,
      ...(opts.metadata ? { metadata: opts.metadata } : {}),
    })
    .select('id')
    .single();
  if (error) throw error;
}

export async function sharePostToConversations(opts: {
  postId: string;
  conversationIds: string[];
  body?: string;
}): Promise<void> {
  await Promise.all(
    opts.conversationIds.map((conversationId) =>
      sharePostToConversation({
        postId: opts.postId,
        conversationId,
        body: opts.body,
      })
    ),
  );
}

/**
 * For each mention in a post or comment, drop the post into the relevant
 * conversation as a referenced message:
 *
 *   - user / internal → find-or-create DM with that user
 *   - channel         → the chat conversation directly (id IS conversations.id)
 *   - everyone        → tier conversation that scopes the post itself
 *
 * Resolved conversation IDs are de-duped so that mixing two chat mentions of
 * the same conversation, or @everyone + the user's tier chat, results in one
 * share, not two.
 *
 * Each shared message has the body:
 *   "You have been mentioned in: <post title or 'Post by <author>'>"
 * with the standard <SharedPostPreview> card metadata so the post link/preview
 * renders identically to the explicit Share button (no link recreation).
 *
 * The message arrives in every recipient's chat sidebar instantly via
 * Realtime — a much faster signal than the post_mention notification path,
 * which depends on the outbox cron.
 *
 * Self-mentions (author mentioning themselves) are suppressed. Failures are
 * swallowed: the post itself is already persisted; a share failure should
 * never roll back the post.
 */
export interface ResolveMentionConversationsOpts {
  authorId: string;
  postTierId: string | null;
  mentions: Mention[] | null | undefined;
}

/**
 * Resolve a post's mentions to the SET of chat conversation ids that should
 * receive an auto-share message. Single source of truth shared by
 * `sharePostFromMentions` (create) and `reconcilePostMentionShares` (edit) so
 * both agree exactly on the target set.
 *
 *   - @everyone        → the post tier's `community_channel` conversation
 *   - kind:'channel'   → `m.id` IS the conversation id
 *   - kind:'user'|'internal' → find-or-create DM (author excluded)
 *
 * NOTE: this performs find-or-create + join side effects
 * (`getOrCreateTierConversation` / `findOrCreateDm`). That is intentional —
 * the join is what guarantees the author is an active participant so the
 * downstream `messages` INSERT/UPDATE (archive/restore) passes RLS. Per-mention
 * resolution failures are swallowed (warn only) so one bad mention can't sink
 * the whole share.
 */
export async function resolveMentionConversations(
  opts: ResolveMentionConversationsOpts,
): Promise<Set<string>> {
  const mentions = opts.mentions ?? [];
  const conversationIds = new Set<string>();
  if (mentions.length === 0) return conversationIds;

  // @everyone → the post's tier chat (find-or-create so a seedless tier still
  // receives the share).
  if (mentions.some((m) => m.kind === 'everyone') && opts.postTierId) {
    try {
      const { id } = await getOrCreateTierConversation(opts.postTierId);
      conversationIds.add(id);
    } catch (err) {
      console.warn('[mentions] tier conversation lookup failed', opts.postTierId, err);
    }
  }

  // channel-kind mentions: id is already the chat conversation id (the
  // mention picker now sources these from `conversations` directly, not
  // from the legacy `community_channels` feed table).
  for (const m of mentions) {
    if (m.kind === 'channel' && m.id) conversationIds.add(m.id);
  }

  // user / internal mentions → find-or-create DM with each user.
  const userIds = Array.from(
    new Set(
      mentions
        .filter(
          (m): m is Extract<Mention, { kind: 'user' | 'internal' }> =>
            (m.kind === 'user' || m.kind === 'internal') && Boolean(m.id),
        )
        .map((m) => m.id),
    ),
  ).filter((id) => id !== opts.authorId);

  await Promise.all(
    userIds.map(async (uid) => {
      try {
        const dmId = await findOrCreateDm(uid);
        conversationIds.add(dmId);
      } catch (err) {
        console.warn('[mentions] find-or-create DM failed for user', uid, err);
      }
    }),
  );

  return conversationIds;
}

export async function sharePostFromMentions(opts: {
  postId: string;
  authorId: string;
  postTierId: string | null;
  mentions: Mention[] | null | undefined;
}): Promise<void> {
  const mentions = opts.mentions ?? [];
  if (mentions.length === 0) return;

  // Resolved set of conversation IDs to share into (dedup + side-effecting
  // find-or-create/join). Shared with the edit reconcile path.
  const conversationIds = await resolveMentionConversations({
    authorId: opts.authorId,
    postTierId: opts.postTierId,
    mentions,
  });

  if (conversationIds.size === 0) return;

  const { body, metadata } = await buildAutoShareMessage(opts.postId);

  await Promise.all(
    Array.from(conversationIds).map(async (conversationId) => {
      try {
        await sharePostToConversation({
          postId: opts.postId,
          conversationId,
          body,
          metadata,
        });
      } catch (err) {
        console.warn('[mentions] share-to-conversation failed', conversationId, err);
      }
    }),
  );
}

/**
 * Build the auto-share message body + metadata for a post. Single source for
 * both create (`sharePostFromMentions`) and reconcile inserts so the bubble
 * renders identically. The returned `metadata` always carries
 * `auto_shared_from_mention: true`, which is the identity marker reconcile
 * matches on.
 */
async function buildAutoShareMessage(
  postId: string,
): Promise<{ body: string; metadata: Record<string, unknown> }> {
  // Matches shareableItemMetadata used by the explicit Share button — same
  // card render, no link recreation.
  const { data: postRow } = await supabase
    .from('community_posts')
    .select(`
      id, title, content, images,
      user_profiles!community_posts_author_id_user_profiles_id_fk ( full_name )
    `)
    .eq('id', postId)
    .single();
  const post = (postRow ?? {}) as {
    id?: string;
    title?: string | null;
    content?: string | null;
    images?: Array<{ url?: string }> | null;
    user_profiles?: { full_name?: string | null } | null;
  };
  const authorName = post.user_profiles?.full_name?.trim() || 'a member';
  const subject = post.title?.trim() || `Post by ${authorName}`;
  const body = `You have been mentioned in: ${subject}`;

  const excerptSource = post.title?.trim() || post.content?.trim() || '';
  const metadata: Record<string, unknown> = {
    shared_post_id: postId,
    shared_post_author: post.user_profiles?.full_name ?? null,
    // Strip inline mention tokens BEFORE slicing so a token straddling char 200
    // can't survive as a partial `[mention:channel:<uuid>` fragment.
    shared_post_excerpt: stripMentionTokens(excerptSource).slice(0, 200),
    shared_post_image: post.images?.[0]?.url ?? null,
    shared_post_url: `/community/post/${postId}`,
    auto_shared_from_mention: true,
  };

  return { body, metadata };
}

/**
 * Content-preserving archive of an auto-share message: sets `deleted_at` only,
 * leaving `content`/`attachments`/`metadata` intact (unlike `deleteMessage`,
 * which clears them). The row renders as a "deleted" tombstone off `deleted_at`
 * while staying restorable to its exact prior body.
 *
 * RLS: `messages_update` USING is `sender_id = auth.uid()` with NO `WITH CHECK`
 * and no `deleted_at` filter, so the post author (== the auto-share sender) can
 * set `deleted_at` directly. Failures are thrown to the caller to swallow.
 */
async function archiveAutoShareMessage(messageId: string): Promise<void> {
  // Fetch conversation_id before the soft-delete so we can broadcast the drop
  // (mirrors deleteMessage).
  const { data: msg } = await supabase
    .from('messages')
    .select('conversation_id')
    .eq('id', messageId)
    .single();

  const deletedAt = new Date().toISOString();
  const { error } = await supabase
    .from('messages')
    .update({ deleted_at: deletedAt })
    .eq('id', messageId);
  if (error) throw error;

  // ─── Realtime: drop the bubble live in any open channel/DM view. Mirrors
  // deleteMessage's MESSAGE_DELETED broadcast. Fire-and-forget. ───
  if (msg) {
    broadcastToConversation(msg.conversation_id, {
      type: 'broadcast',
      event: REALTIME_EVENTS.MESSAGE_DELETED,
      payload: { message_id: messageId, deleted_at: deletedAt },
    }).catch(() => {
      // Non-critical: recipient sees the archive on next poll/reconnect.
    });
  }
}

/**
 * Restore a previously-archived auto-share message: clears `deleted_at`. The
 * `messages_select`/`messages_update` policies carry no `deleted_at` filter, so
 * the soft-deleted row stays SELECT-visible and updatable to its sender — the
 * clear lands client-side and the original content reappears.
 */
async function restoreAutoShareMessage(messageId: string): Promise<void> {
  // Realtime is server-emitted: trg_broadcast_message_restored (hermes
  // migration 468) fires on this deleted_at NULL transition and re-sends
  // message.created so open views re-add the bubble. No client broadcast —
  // message.created is a server-owned event (the migration-473 send policy
  // denies it to clients by name once the private leg is live).
  const { error } = await supabase
    .from('messages')
    .update({ deleted_at: null })
    .eq('id', messageId);
  if (error) throw error;
}

interface AutoShareRow {
  id: string;
  conversation_id: string;
  deleted_at: string | null;
}

/**
 * Reconcile a post's auto-share chat messages against its CURRENT mentions.
 *
 * Identity of an auto-share message = one row per
 * `(referenced_post_id = postId, conversation_id, metadata.auto_shared_from_mention = true)`.
 * We resolve the current mentions to a set of conversation ids, load the
 * existing auto-share rows (INCLUDING archived ones), and reconcile each:
 *
 *   | current mention | existing row        | action                     |
 *   |-----------------|---------------------|----------------------------|
 *   | present         | none                | insert (active)            |
 *   | present         | archived            | restore (clear deleted_at) |
 *   | removed         | active              | archive (set deleted_at)   |
 *   | removed         | already archived    | leave                      |
 *
 * Never hard-deletes — re-adding a removed mention restores the same row.
 * Fire-and-forget from `updatePost`: per-conversation failures are swallowed so
 * a reconcile error never rolls back the post edit.
 */
export async function reconcilePostMentionShares(opts: {
  postId: string;
  authorId: string;
  postTierId: string | null;
  mentions: Mention[] | null | undefined;
}): Promise<void> {
  // Current target set (side-effecting: creates/joins conversations so the
  // author is a participant and the message INSERT/UPDATE passes RLS).
  const desired = await resolveMentionConversations({
    authorId: opts.authorId,
    postTierId: opts.postTierId,
    mentions: opts.mentions,
  });

  // Existing auto-share rows for this post — INCLUDING archived ones (no
  // `deleted_at` filter), keyed by conversation. If two rows somehow exist for
  // one conversation, prefer an active row so we don't archive a live message
  // while leaving an archived one.
  const { data: existingData, error: existingErr } = await supabase
    .from('messages')
    .select('id, conversation_id, deleted_at')
    .eq('referenced_post_id', opts.postId)
    .eq('metadata->>auto_shared_from_mention', 'true');
  if (existingErr) {
    console.warn('[mentions] reconcile: failed to load existing shares', opts.postId, existingErr);
    return;
  }

  const existingByConversation = new Map<string, AutoShareRow>();
  for (const raw of (existingData ?? []) as AutoShareRow[]) {
    const prior = existingByConversation.get(raw.conversation_id);
    // Keep the active row if one exists; otherwise the (archived) row.
    if (!prior || (prior.deleted_at !== null && raw.deleted_at === null)) {
      existingByConversation.set(raw.conversation_id, raw);
    }
  }

  // Lazily built only when there is at least one insert to perform.
  type ShareMessage = { body: string; metadata: Record<string, unknown> };
  let shareMessage: ShareMessage | null = null;
  const getShareMessage = async (): Promise<ShareMessage> => {
    if (!shareMessage) shareMessage = await buildAutoShareMessage(opts.postId);
    return shareMessage;
  };

  const tasks: Array<Promise<void>> = [];

  // Present mentions → insert (none) or restore (archived).
  for (const conversationId of desired) {
    const existing = existingByConversation.get(conversationId);
    if (!existing) {
      tasks.push(
        (async () => {
          try {
            const { body, metadata } = await getShareMessage();
            await sharePostToConversation({
              postId: opts.postId,
              conversationId,
              body,
              metadata,
            });
          } catch (err) {
            console.warn('[mentions] reconcile insert failed', conversationId, err);
          }
        })(),
      );
    } else if (existing.deleted_at !== null) {
      tasks.push(
        (async () => {
          try {
            await restoreAutoShareMessage(existing.id);
          } catch (err) {
            console.warn('[mentions] reconcile restore failed', existing.id, err);
          }
        })(),
      );
    }
    // present + active → nothing to do.
  }

  // Removed mentions → archive any still-active auto-share row.
  for (const [conversationId, existing] of existingByConversation) {
    if (desired.has(conversationId)) continue;
    if (existing.deleted_at !== null) continue; // already archived → leave
    tasks.push(
      (async () => {
        try {
          await archiveAutoShareMessage(existing.id);
        } catch (err) {
          console.warn('[mentions] reconcile archive failed', existing.id, err);
        }
      })(),
    );
  }

  await Promise.all(tasks);
}


// ===========================================
// Custom Communities — admin management (migrations 378-383)
// ===========================================
// All writes flow through SECURITY DEFINER RPCs (super-admin gated behind
// fn_can_manage_community server-side); reads use RLS-scoped table selects.

export const customCommunityRowSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  description: z.string().nullable(),
  icon: z.string().nullable(),
  color: z.string().nullable(),
  archived_at: z.string().nullable(),
  created_at: z.string().nullable(),
});
export type CustomCommunityRow = z.infer<typeof customCommunityRowSchema>;

/** Admin list — super admins see all (incl. archived) via RLS. */
export async function fetchCustomCommunities(
  opts: { archived?: boolean } = {},
): Promise<CustomCommunityRow[]> {
  let query = supabase
    .from('communities')
    .select('id, name, description, icon, color, archived_at, created_at')
    .order('name');
  query = opts.archived
    ? query.not('archived_at', 'is', null)
    : query.is('archived_at', null);
  const { data, error } = await query;
  if (error) throw error;
  return z.array(customCommunityRowSchema).parse(data ?? []);
}

export const customCommunityCreateInputSchema = z.object({
  name: z.string().min(1).max(100),
  description: z.string().max(500).optional().nullable(),
  icon: z.string().max(64).optional().nullable(),
  color: z.string().max(32).optional().nullable(),
  /** Tiers granted access at creation (bulk-add + auto-provision). */
  tierIds: z.array(z.string().uuid()).optional(),
  /** Initial hand-picked members. */
  memberIds: z.array(z.string().uuid()).optional(),
});
export type CustomCommunityCreateInput = z.infer<typeof customCommunityCreateInputSchema>;

/** Atomic create: community + chat conversation + default channel + owner. */
export async function createCustomCommunity(
  input: CustomCommunityCreateInput,
): Promise<string> {
  const parsed = customCommunityCreateInputSchema.parse(input);
  const { data, error } = await supabase.rpc('create_custom_community', {
    p_name: parsed.name,
    p_description: parsed.description ?? null,
    p_icon: parsed.icon ?? null,
    p_color: parsed.color ?? null,
    p_tier_ids: parsed.tierIds?.length ? parsed.tierIds : null,
    p_member_ids: parsed.memberIds?.length ? parsed.memberIds : null,
  });
  if (error) throw error;
  return z.string().uuid().parse(data);
}

export const customCommunityUpdateInputSchema = z.object({
  communityId: z.string().uuid(),
  name: z.string().min(1).max(100),
  description: z.string().max(500).optional().nullable(),
  icon: z.string().max(64).optional().nullable(),
  color: z.string().max(32).optional().nullable(),
});
export type CustomCommunityUpdateInput = z.infer<typeof customCommunityUpdateInputSchema>;

/** Renames the community AND its chat conversation title atomically. */
export async function updateCustomCommunity(
  input: CustomCommunityUpdateInput,
): Promise<void> {
  const parsed = customCommunityUpdateInputSchema.parse(input);
  const { error } = await supabase.rpc('update_custom_community', {
    p_community_id: parsed.communityId,
    p_name: parsed.name,
    p_description: parsed.description ?? null,
    p_icon: parsed.icon ?? null,
    p_color: parsed.color ?? null,
  });
  if (error) throw error;
}

export async function archiveCustomCommunity(communityId: string): Promise<void> {
  const { error } = await supabase.rpc('archive_custom_community', {
    p_community_id: communityId,
  });
  if (error) throw error;
}

export async function unarchiveCustomCommunity(communityId: string): Promise<void> {
  const { error } = await supabase.rpc('unarchive_custom_community', {
    p_community_id: communityId,
  });
  if (error) throw error;
}

/** Tier grant row with the tier joined for display. */
export const communityTierGrantSchema = z.object({
  id: z.string().uuid(),
  community_id: z.string().uuid(),
  community_tier_id: z.string().uuid(),
  auto_provision: z.boolean(),
  created_at: z.string().nullable(),
  community_tiers: z
    .object({ id: z.string().uuid(), name: z.string(), code: z.string() })
    .nullable(),
});
export type CommunityTierGrant = z.infer<typeof communityTierGrantSchema>;

export async function fetchCommunityTierGrants(
  communityId: string,
): Promise<CommunityTierGrant[]> {
  const { data, error } = await supabase
    .from('community_tier_access')
    .select(`
      id,
      community_id,
      community_tier_id,
      auto_provision,
      created_at,
      community_tiers!community_tier_id (id, name, code)
    `)
    .eq('community_id', communityId)
    .order('created_at', { ascending: true });
  if (error) throw error;
  return z.array(communityTierGrantSchema).parse(data ?? []);
}

/** Bulk-adds current tier members; returns the count added. */
export async function grantCommunityTierAccess(
  communityId: string,
  tierId: string,
  autoProvision = true,
): Promise<number> {
  const { data, error } = await supabase.rpc('grant_community_tier_access', {
    p_community_id: communityId,
    p_tier_id: tierId,
    p_auto_provision: autoProvision,
  });
  if (error) throw error;
  return z.number().parse(data);
}

/** Removes members whose only claim was this grant; returns the count removed. */
export async function revokeCommunityTierAccess(
  communityId: string,
  tierId: string,
): Promise<number> {
  const { data, error } = await supabase.rpc('revoke_community_tier_access', {
    p_community_id: communityId,
    p_tier_id: tierId,
  });
  if (error) throw error;
  return z.number().parse(data);
}

const communityMemberAdminRawSchema = z.object({
  user_id: z.string().uuid(),
  full_name: z.string().nullable(),
  email: z.string().nullable(),
  avatar_url: z.string().nullable(),
  role: z.enum(['owner', 'admin', 'member']),
  provisioned_via: z.string().uuid().nullable(),
  joined_at: z.string().nullable(),
});

export const communityMemberRowSchema = z.object({
  user_id: z.string().uuid(),
  /** Profile name, falling back to email, falling back to 'Unknown'. */
  full_name: z.string(),
  email: z.string().nullable(),
  avatar_url: z.string().nullable(),
  role: z.enum(['owner', 'admin', 'member']),
  /** NULL = hand-picked/manual (sticky); set = provisioned via a tier grant. */
  provisioned_via: z.string().uuid().nullable(),
  joined_at: z.string().nullable(),
});
export type CommunityMemberRow = z.infer<typeof communityMemberRowSchema>;

/**
 * Active members of a community via the super-admin RPC (386).
 * community_members.user_id has no FK to user_profiles (profiles can lag auth
 * users — bulk-imported tier members often have none), so the RPC joins
 * auth.users and the email becomes the identity fallback. Hand-picked members
 * sort before tier-provisioned ones, alphabetical within each group.
 */
export async function fetchCommunityMembers(
  communityId: string,
): Promise<CommunityMemberRow[]> {
  const { data, error } = await supabase.rpc('get_community_members_admin', {
    p_community_id: communityId,
  });
  if (error) throw error;
  const rows = z.array(communityMemberAdminRawSchema).parse(data ?? []);
  return rows
    .map((r) => ({
      user_id: r.user_id,
      role: r.role,
      provisioned_via: r.provisioned_via,
      joined_at: r.joined_at,
      email: r.email,
      full_name: r.full_name ?? r.email ?? 'Unknown',
      avatar_url: r.avatar_url,
    }))
    .sort((a, b) => {
      const provA = a.provisioned_via === null ? 0 : 1;
      const provB = b.provisioned_via === null ? 0 : 1;
      if (provA !== provB) return provA - provB;
      return a.full_name.localeCompare(b.full_name);
    });
}

export async function addCommunityMembers(
  communityId: string,
  userIds: string[],
): Promise<number> {
  const { data, error } = await supabase.rpc('add_community_members', {
    p_community_id: communityId,
    p_user_ids: userIds,
  });
  if (error) throw error;
  return z.number().parse(data);
}

export async function removeCommunityMember(
  communityId: string,
  userId: string,
): Promise<void> {
  const { error } = await supabase.rpc('remove_community_member', {
    p_community_id: communityId,
    p_user_id: userId,
  });
  if (error) throw error;
}

export async function updateCommunityMemberRole(
  communityId: string,
  userId: string,
  role: 'owner' | 'admin' | 'member',
): Promise<void> {
  const { error } = await supabase.rpc('update_community_member_role', {
    p_community_id: communityId,
    p_user_id: userId,
    p_role: role,
  });
  if (error) throw error;
}
